import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { getAlunni, getAnniDisponibili, getFiltriClassi, getAlunniCountsByCiclo, getComunicatorOutboxByAlunno } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import RichEditor from "@/components/RichEditor";
import AlunnoEmailsModal from "@/components/AlunnoEmailsModal";
// 🆕 Feb 2026: badge iscrizione + modal movimenti collegati (stessi usati in Curriculum/AlunniPage).
import IscrizioneBadge from "@/components/IscrizioneBadge";
import MovimentiCollegatiModal from "@/components/MovimentiCollegatiModal";

function MultiCheckDropdown({ testId, placeholder, options, selected, onToggle, onClear, minWidth = 150 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const selCount = selected.length;
  const summary = selCount === 0
    ? placeholder
    : (selCount === 1 ? (options.find(o => o.value === selected[0])?.label || selected[0]) : `${selCount} selez.`);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" data-testid={testId} onClick={() => setOpen(o => !o)} className="sw-filter-select"
        style={{ minWidth, textAlign: "left", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
      </button>
      {open && (
        <div data-testid={`${testId}-panel`} style={{
          position: "absolute", zIndex: 50, top: "calc(100% + 4px)", left: 0, background: "white",
          border: "1px solid #cfd8dc", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
          padding: 6, minWidth: Math.max(minWidth, 180), maxHeight: 320, overflowY: "auto",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 6px 6px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#607d8b" }}>{placeholder}</span>
            {selCount > 0 && (
              <button type="button" onClick={onClear} style={{ fontSize: 11, color: "#c62828", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>Pulisci</button>
            )}
          </div>
          {options.map(o => {
            const checked = selected.includes(o.value);
            return (
              <label key={o.value} data-testid={`${testId}-opt-${o.value}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", cursor: "pointer", fontSize: 13, borderRadius: 4, background: checked ? "#e3f2fd" : "transparent" }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o.value)} />
                <span>{o.label}</span>
              </label>
            );
          })}
          {options.length === 0 && <div style={{ padding: 8, fontSize: 12, color: "#90a4ae" }}>Nessuna opzione</div>}
        </div>
      )}
    </div>
  );
}

const SOURCE_BADGE = {
  segremat: { c: "#1565c0", bg: "#e3f2fd", l: "Segremat" },
  import:   { c: "#558b2f", bg: "#f1f8e9", l: "Importata" },
  manual:   { c: "#6a1b9a", bg: "#f3e5f5", l: "Manuale" },
  comunicator: { c: "#bf360c", bg: "#fff3e0", l: "Comunicator" },
};

// 🆕 Feb 2026: gestione placeholder custom per-alunno (localStorage).
// Definiti dall'utente nel popup "📜 Storico invii" e iniettati come `custom_placeholders`
// nel payload di invia-bulk-v2 al momento dell'invio.
const CUSTOM_PH_LS_PREFIX = "com_alu_custom_ph_v1_";
export function readCustomPlaceholder(cod) {
  try {
    const raw = localStorage.getItem(CUSTOM_PH_LS_PREFIX + cod);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.name && typeof parsed.value === "string" ? parsed : null;
  } catch { return null; }
}
export function writeCustomPlaceholder(cod, name, value) {
  try {
    localStorage.setItem(CUSTOM_PH_LS_PREFIX + cod, JSON.stringify({ name, value, saved_at: new Date().toISOString() }));
  } catch { /* ignore quota */ }
}
export function clearCustomPlaceholder(cod) {
  try { localStorage.removeItem(CUSTOM_PH_LS_PREFIX + cod); } catch { /* ignore */ }
}
export function hasCustomPlaceholder(cod) {
  return !!readCustomPlaceholder(cod);
}

export default function ComunicatorPage() {
  const [globalOpen, setGlobalOpen] = useState(false);
  return (
    <div>
      <div className="sw-page-title">
        <h1>Comunicazioni</h1>
        <div className="sw-breadcrumb">Home &gt; Comunicazioni</div>
      </div>
      <DestinatariView />
      {globalOpen && <GlobalSettingsModal onClose={() => setGlobalOpen(false)} />}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ padding: "8px 18px", border: "none", background: active ? "#00838f" : "white", color: active ? "white" : "#00838f",
        fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: "4px 4px 0 0", border: "1px solid #ccc" }}>
      {children}
    </button>
  );
}

// ============== TAB 1: DESTINATARI + INVIO ==============
function DestinatariView() {
  const { annoCorrente, setAnnoCorrente } = useAuth();
  const { anno: annoGlobal, setAnno: setAnnoGlobal } = useAnnoScolastico();
  const { gestione } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link da altre liste: ?codes=12345,67890&label=Curriculum%20P1
  const initialCodesFromUrl = useMemo(() => {
    const s = searchParams.get("codes");
    if (!s) return null;
    return s.split(",").map(x => parseInt(x.trim(), 10)).filter(x => Number.isFinite(x));
  }, [searchParams]);
  const labelFromUrl = searchParams.get("label") || "";
  const annoFromUrl = parseInt(searchParams.get("anno") || "", 10);
  // 🆕 Selezione condivisa da "Situazioni Contabili" (credito visual) via sessionStorage:
  // così la spunta resta anche arrivando in Comunicazioni dal menù (non solo dal pulsante solleciti).
  const storedCodes = useMemo(() => {
    try {
      const arr = JSON.parse(sessionStorage.getItem("sc_selected_cods_v1") || "[]");
      return Array.isArray(arr) ? arr.map((x) => parseInt(x, 10)).filter(Number.isFinite) : [];
    } catch { return []; }
  }, []);
  const [deepLinkLabel, setDeepLinkLabel] = useState(labelFromUrl);
  const [anno, setAnno] = useState(Number.isFinite(annoFromUrl) ? annoFromUrl : annoCorrente);
  const [anni, setAnni] = useState([]);

  // 🆕 Sincronizza l'anno locale della pagina con il selettore A.S. globale
  // (in alto a destra). Senza questo, cambiando l'anno dal selettore globale la
  // pagina restava sull'anno precedente finché non si faceva F5 (header e dati
  // disallineati). Salta il primo render per non sovrascrivere l'anno del deep-link.
  const annoSyncSkip = useRef(true);
  useEffect(() => {
    if (annoSyncSkip.current) { annoSyncSkip.current = false; return; }
    // Non toccare l'anno se stiamo elaborando un deep-link (?codes=...&anno=...)
    if (initialCodesFromUrl && initialCodesFromUrl.length > 0) return;
    const g = Number.isFinite(annoGlobal) ? annoGlobal : annoCorrente;
    if (Number.isFinite(g) && g !== anno) setAnno(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal, annoCorrente]);
  const [settoriList, setSettoriList] = useState([]);
  const [classiList, setClassiList] = useState([]);
  const [cicloCounts, setCicloCounts] = useState({});
  const [totalAllCicli, setTotalAllCicli] = useState(0);
  const [filtroCiclo, setFiltroCiclo] = useState("");
  const [filtroSettore, setFiltroSettore] = useState([]);
  const [filtroClasse, setFiltroClasse] = useState([]);
  const [soloSelezionati, setSoloSelezionati] = useState(false);
  const [bulkBase, setBulkBase] = useState(false);
  const [bulkStampa, setBulkStampa] = useState(false);
  const [filtroNonPagato, setFiltroNonPagato] = useState(false);       // mostra solo alunni con non pagato > 0
  const [filtroScaduto, setFiltroScaduto] = useState(false);           // mostra solo alunni con non pagato scaduto > 0
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Map()); // Map<cod, alunnoSummary> persiste tra cambi tab
  // Bulk sequenziale: quando l'utente clicca "Invia email a N selezionati", apriamo il wizard
  // UNA VOLTA per ogni alunno selezionato, in sequenza. `bulkQueue` contiene la coda dei
  // destinatari restanti; `bulkTotal` è il totale iniziale (per mostrare "Alunno X di Y").
  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkTotal, setBulkTotal] = useState(0);
  const [manualLogOpen, setManualLogOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [sortBy, setSortBy] = useState("tot_non_pagato_scaduto");
  const [sortDir, setSortDir] = useState("desc");
  const [, setEmailsCounts] = useState({}); // valore non più mostrato in tabella; il setter resta per i fetch esistenti
  const [emailsModalCod, setEmailsModalCod] = useState(null);
  // 🆕 Feb 2026: Storico invii per alunno (popup) + placeholder custom
  const [inviiModalCod, setInviiModalCod] = useState(null);
  const [customPhVersion, setCustomPhVersion] = useState(0); // per forzare refresh dei badge ★
  // 🆕 Feb 2026: badge iscrizione (movimenti Cau=1) — apre modal come in Curriculum/Alunni.
  const [iscrCounts, setIscrCounts] = useState({});
  const [iscrModal, setIscrModal] = useState(null);
  const [ridAttivi, setRidAttivi] = useState(() => new Set()); // 🆕 CF con mandato RID attivo (tabella fissa RID)
  const [doteScuola, setDoteScuola] = useState(() => new Map()); // 🆕 CF -> importo Dote Scuola (tabella fissa)
  const requestRef = useRef(0);

  useEffect(() => { getAnniDisponibili().then(r => setAnni(r.data?.anni || [])).catch(() => { }); }, []);
  // 🆕 CF con mandato RID attivo (tabella fissa RID) → badge "RID" nella lista destinatari
  useEffect(() => {
    api.get("/rid").then(r => {
      const s = new Set();
      for (const it of (r.data?.items || [])) {
        if (it.attivo && it.codice_fiscale) s.add(String(it.codice_fiscale).trim().toUpperCase());
      }
      setRidAttivi(s);
    }).catch(() => { });
  }, []);
  // 🆕 CF -> importo Dote Scuola (tabella fissa) → colonna + badge "DOTE SCUOLA"
  useEffect(() => {
    api.get("/dote").then(r => {
      const m = new Map();
      for (const it of (r.data?.items || [])) {
        const cf = String(it.codice_fiscale || "").trim().toUpperCase();
        if (!cf) continue;
        m.set(cf, (m.get(cf) || 0) + Number(it.importo || 0));
      }
      setDoteScuola(m);
    }).catch(() => { });
  }, []);
  useEffect(() => {
    if (!anno) return;
    getFiltriClassi(anno).then(r => {
      setSettoriList(r.data?.settori || []);
      setClassiList(r.data?.classi || []);
    }).catch(() => { });
    setFiltroSettore(""); setFiltroClasse("");
  }, [anno]);

  // Counts per ciclo tab (curriculum-based)
  useEffect(() => {
    if (!anno) { setCicloCounts({}); setTotalAllCicli(0); return; }
    getAlunniCountsByCiclo({ anno, ...(search ? { search } : {}) })
      .then(r => { setCicloCounts(r.data?.counts || {}); setTotalAllCicli(r.data?.total || 0); })
      .catch(() => { });
  }, [anno, search]);

  const carica = useCallback(async () => {
    const reqId = ++requestRef.current;
    setLoading(true); setErr(null);
    try {
      const params = {
        anno,
        sort_by: sortBy, sort_dir: sortDir,
        limit: 5000, page: 1,
      };
      if (filtroCiclo) params.ciclo = filtroCiclo;
      if (filtroSettore.length) params.settore = filtroSettore.join(",");
      if (filtroClasse.length) params.classe_nome = filtroClasse.join(",");
      if (search) params.search = search;
      const r = await getAlunni(params);
      if (reqId !== requestRef.current) return;
      const rows = r.data?.data || r.data?.items || [];
      setItems(rows);
      // NOTA: NON resettiamo `selected` qui — vogliamo che la selezione persista
      // attraverso i cambi di tab/filtri. Si resetta solo dopo l'invio o tramite
      // il pulsante "Reset spunte" (vedi sotto).
      // Carica counts email per gli alunni mostrati
      if (rows.length > 0) {
        try {
          const cr = await api.post("/alunni-emails/_counts", { cods: rows.map(a => a.Cod) });
          if (reqId === requestRef.current) setEmailsCounts(cr.data?.counts || {});
        } catch { /* ignore */ }
        // 🆕 Batch fetch counts iscrizione (Cau=1) — usati dal badge 🎟
        try {
          const ir = await api.post("/alunni/iscrizione-movimenti/counts", { cods: rows.map(a => a.Cod), anno: Number(anno) });
          if (reqId === requestRef.current) setIscrCounts(ir.data?.counts || {});
        } catch { /* ignore */ }
      } else {
        setEmailsCounts({});
        setIscrCounts({});
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore caricamento alunni");
    } finally { if (reqId === requestRef.current) setLoading(false); }
  }, [anno, filtroCiclo, filtroSettore, filtroClasse, search, sortBy, sortDir]);

  // 🆕 Apre modal con lista movimenti Cau=1 di un singolo alunno (stesso flusso di Curriculum/Alunni)
  const openIscrizioneModal = useCallback(async (cod) => {
    try {
      const r = await api.get(`/alunni/${cod}/iscrizione-movimenti`, { params: { anno: Number(anno) } });
      const resp = r.data || {};
      setIscrModal({
        iscr: { alunno_cod: resp.alunno_cod, cognome: resp.cognome, nome: resp.nome, anno: resp.anno },
        data: resp,
      });
    } catch (e) {
      alert("Errore caricamento movimenti iscrizione: " + (e.response?.data?.detail || e.message));
    }
  }, [anno]);

  useEffect(() => { if (anno && filtroCiclo !== "_SELECTED_") carica(); }, [carica, anno, filtroCiclo]);

  // Deep-link da altre liste: ?codes=12345,67890&label=...&anno=...
  // Al primo mount (e solo se non già processato), carica gli alunni da
  // backend con il filtro `cods` e popola `selected` + passa al filtro
  // "_SELECTED_" così l'utente può procedere subito con l'invio mail.
  const deepLinkProcessed = useRef(false);
  useEffect(() => {
    if (deepLinkProcessed.current) return;
    if (!anno) return;
    const codes = (initialCodesFromUrl && initialCodesFromUrl.length) ? initialCodesFromUrl : storedCodes;
    if (!codes || codes.length === 0) return;
    deepLinkProcessed.current = true;
    const fromStored = !(initialCodesFromUrl && initialCodesFromUrl.length);
    (async () => {
      try {
        const r = await getAlunni({
          anno,
          cods: codes.join(","),
          limit: 5000, page: 1,
          sort_by: sortBy, sort_dir: sortDir,
        });
        const rows = r.data?.data || r.data?.items || [];
        const m = new Map();
        for (const a of rows) m.set(a.Cod, a);
        setSelected(m);
        setItems(rows);
        setFiltroCiclo("_SELECTED_");
        if (fromStored && !labelFromUrl) setDeepLinkLabel("Situazioni Contabili");
        if (rows.length > 0) {
          try {
            const cr = await api.post("/alunni-emails/_counts", { cods: rows.map(a => a.Cod) });
            setEmailsCounts(cr.data?.counts || {});
          } catch { /* ignore */ }
        }
      } catch (e) {
        setErr("Errore caricamento alunni dal deep-link: " + (e.response?.data?.detail || e.message));
      }
    })();
  }, [anno, initialCodesFromUrl, storedCodes, sortBy, sortDir]);

  // Pulizia query string deep-link (dopo l'elaborazione, così un refresh
  // della pagina non re-applica nuovamente l'auto-selezione)
  const clearDeepLink = () => {
    setDeepLinkLabel("");
    const sp = new URLSearchParams(searchParams);
    sp.delete("codes"); sp.delete("label"); sp.delete("anno");
    setSearchParams(sp, { replace: true });
  };

  // Items mostrati: se il filtro è "_SELECTED_" mostriamo direttamente la mappa `selected`
  const baseItems = (soloSelezionati || filtroCiclo === "_SELECTED_") ? Array.from(selected.values()) : items;
  const displayedItems = baseItems.filter(a =>
    (!filtroNonPagato || (Number(a.tot_non_pagato) || 0) > 0) &&
    (!filtroScaduto || (Number(a.tot_non_pagato_scaduto) || 0) > 0)
  );

  const toggleSel = (a) => {
    const m = new Map(selected);
    if (m.has(a.Cod)) m.delete(a.Cod);
    else m.set(a.Cod, a);
    setSelected(m);
  };
  const stampaElencoPdf = () => {
    const arr = Array.from(selected.values());
    if (!arr.length) return;
    arr.sort((a, b) => (`${a.Cog || ""} ${a.Nom || ""}`).localeCompare(`${b.Cog || ""} ${b.Nom || ""}`, "it"));
    const asLbl = `${anno}/${String(anno + 1).slice(-2)}`;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = arr.map((a, i) => `<tr>
        <td class="n">${i + 1}</td>
        <td>${esc(`${a.Cog || ""} ${a.Nom || ""}`.trim())}</td>
        <td class="c">${esc(a.classe || "")}</td>
        <td class="c">${esc(a.settore || "")}</td>
        <td class="cf">${esc(a.CodFis || "")}</td>
      </tr>`).join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Elenco selezionati ${asLbl}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        * { font-family: Arial, Helvetica, sans-serif; }
        h1 { font-size: 16px; margin: 0 0 2px; color: #0f172a; }
        .sub { font-size: 11px; color: #555; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; }
        th { background: #e8eef7; font-size: 10px; text-transform: uppercase; letter-spacing: .3px; }
        td.n, td.c { text-align: center; }
        td.cf { font-family: Consolas, monospace; }
        tr:nth-child(even) td { background: #f7f9fc; }
        .foot { margin-top: 10px; font-size: 10px; color: #777; }
      </style></head><body>
      <h1>Istituto Canossa Diaz — Elenco alunni selezionati</h1>
      <div class="sub">Anno scolastico ${asLbl} · ${arr.length} alunni · stampato il ${new Date().toLocaleDateString("it-IT")}</div>
      <table><thead><tr><th class="n">#</th><th>Cognome e Nome</th><th>Classe</th><th>Settore</th><th>Codice Fiscale</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <div class="foot">Totale: ${arr.length} alunni</div>
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 200); };</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Consenti i popup per stampare l'elenco."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const allCurrentSelected = displayedItems.length > 0 && displayedItems.every(a => selected.has(a.Cod));
  const toggleAll = () => {
    const m = new Map(selected);
    if (allCurrentSelected) {
      for (const a of displayedItems) m.delete(a.Cod);
    } else {
      for (const a of displayedItems) m.set(a.Cod, a);
    }
    setSelected(m);
  };
  const resetSpunte = () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Resettare tutte le ${selected.size} spunte?`)) return;
    setSelected(new Map());
  };
  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === "desc" ? "asc" : "desc"); }
    else { setSortBy(col); setSortDir("desc"); }
  };
  const sortArrow = (col) => sortBy !== col ? " ↕" : (sortDir === "desc" ? " ↓" : " ↑");

  // Opzioni del filtro a tendina "ciclo" (curriculum: Tutti · Selezionati · ? · I · P1..P5 · M1..M3 · U)
  const cicloTabs = [
    { k: "",             l: "Tutti", count: totalAllCicli },
    { k: "_SELECTED_",   l: "✓ Selezionati", count: selected.size, isSel: true },
    { k: "_NONE_",       l: "?",     count: cicloCounts["_NONE_"] || 0 },
    { k: "infanzia",     l: "I",     count: cicloCounts["infanzia"] || 0 },
    { k: "primaria_1",   l: "P1",    count: cicloCounts["primaria_1"] || 0 },
    { k: "primaria_2",   l: "P2",    count: cicloCounts["primaria_2"] || 0 },
    { k: "primaria_3",   l: "P3",    count: cicloCounts["primaria_3"] || 0 },
    { k: "primaria_4",   l: "P4",    count: cicloCounts["primaria_4"] || 0 },
    { k: "primaria_5",   l: "P5",    count: cicloCounts["primaria_5"] || 0 },
    { k: "secondaria_1", l: "M1",    count: cicloCounts["secondaria_1"] || 0 },
    { k: "secondaria_2", l: "M2",    count: cicloCounts["secondaria_2"] || 0 },
    { k: "secondaria_3", l: "M3",    count: cicloCounts["secondaria_3"] || 0 },
    { k: "uscito",       l: "U",     count: cicloCounts["uscito"] || 0 },
  ];

  return (
    <div data-testid="com-destinatari" style={{ padding: "8px 14px" }}>
      {/* Filters - same look as AlunniPage */}
      <div className="sw-filter-box">
        <div className="sw-filter-row">
          <span className="sw-filter-label">anno:</span>
          <select data-testid="com-anno" value={anno || ""} onChange={e => {
            const y = e.target.value ? parseInt(e.target.value) : null;
            setAnno(y);
            if (Number.isFinite(y)) { setAnnoGlobal(y); setAnnoCorrente(y); }
          }} className="sw-filter-select">
            {anni.map(a => <option key={a} value={a}>{a}-{String(a + 1).slice(2)}</option>)}
          </select>
          <span className="sw-filter-label">ciclo:</span>
          <select data-testid="com-ciclo" value={filtroCiclo} onChange={e => setFiltroCiclo(e.target.value)} className="sw-filter-select">
            {cicloTabs.map(t => (
              <option key={t.k || "all"} value={t.k}>{t.l}{typeof t.count === "number" ? ` (${t.count})` : ""}</option>
            ))}
          </select>
          <span className="sw-filter-label">settore:</span>
          <MultiCheckDropdown testId="com-set" placeholder="Qualsiasi settore"
            options={[...settoriList.map(s => ({ value: s, label: s })), { value: "_ALTRO_", label: "Altro (no A/E/F/M)" }]}
            selected={filtroSettore}
            onToggle={(v) => setFiltroSettore(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
            onClear={() => setFiltroSettore([])} minWidth={130} />
          <span className="sw-filter-label">classe:</span>
          <MultiCheckDropdown testId="com-cls" placeholder="Qualsiasi classe"
            options={classiList.map(c => ({ value: c, label: c }))}
            selected={filtroClasse}
            onToggle={(v) => setFiltroClasse(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
            onClear={() => setFiltroClasse([])} minWidth={130} />
          <span className="sw-filter-label">cerca:</span>
          <input data-testid="com-search" type="text" placeholder="cognome, nome, CF..." value={search} onChange={e => setSearch(e.target.value)} className="sw-filter-input" style={{ width: 200 }} />
          <label data-testid="com-filter-soloselez" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: soloSelezionati ? "#6a1b9a" : "#555", cursor: "pointer", marginLeft: 4 }}>
            <input type="checkbox" checked={soloSelezionati} onChange={e => setSoloSelezionati(e.target.checked)} />
            ✓ Solo selezionati ({selected.size})
          </label>
          <label data-testid="com-filter-nonpagato" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "#8a6d00", cursor: "pointer", marginLeft: 4 }}>
            <input type="checkbox" checked={filtroNonPagato} onChange={e => setFiltroNonPagato(e.target.checked)} />
            Non pagato &gt; 0
          </label>
          <label data-testid="com-filter-scaduto" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "#b71c1c", cursor: "pointer" }}>
            <input type="checkbox" checked={filtroScaduto} onChange={e => setFiltroScaduto(e.target.checked)} />
            Scaduto &gt; 0
          </label>
          {(filtroSettore.length || filtroClasse.length || filtroCiclo || search || filtroNonPagato || filtroScaduto || soloSelezionati) && <button className="sw-filter-btn" onClick={() => { setFiltroSettore([]); setFiltroClasse([]); setFiltroCiclo(""); setSearch(""); setFiltroNonPagato(false); setFiltroScaduto(false); setSoloSelezionati(false); }}>Pulisci</button>}
        </div>
      </div>

      {/* Banner deep-link: arrivati da altra lista con cods preselezionati */}
      {deepLinkLabel && selected.size > 0 && (
        <div data-testid="com-deeplink-banner"
             style={{ margin: "0 0 8px", padding: "10px 14px", background: "linear-gradient(90deg,#6a1b9a,#8e24aa)", color: "white", borderRadius: 3, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 18 }}>📧</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>
              Selezione importata da: <span style={{ background: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 3, marginLeft: 4 }}>{deepLinkLabel}</span>
            </div>
            <div style={{ fontSize: 11, opacity: 0.95, marginTop: 2 }}>
              <b>{selected.size}</b> alunni pre-selezionati per l&apos;invio email. Procedi al wizard cliccando &quot;Invia mail evoluto ({selected.size})&quot; oppure &quot;Invia mail base&quot; in alto a destra.
            </div>
          </div>
          <button onClick={clearDeepLink}
                  data-testid="com-deeplink-clear"
                  style={{ padding: "4px 10px", background: "white", color: "#6a1b9a", border: "none", fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
            ✕ Rimuovi
          </button>
        </div>
      )}

      {/* Ciclo: ora è un filtro a tendina nella barra filtri (le vecchie tab sono state rimosse) */}

      {/* Send button (stats removed per request) */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
        <button onClick={resetSpunte} disabled={selected.size === 0} data-testid="com-reset-btn"
          style={{ padding: "10px 14px", background: selected.size ? "white" : "#eee", color: selected.size ? "#c62828" : "#999", border: `1px solid ${selected.size ? "#c62828" : "#bbb"}`, fontSize: 12, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          ✕ Reset spunte ({selected.size})
        </button>
        <button onClick={() => setManualLogOpen(true)} disabled={selected.size === 0} data-testid="com-manual-log-btn"
          title="Registra una nota interna o una telefonata nel log Comunicator (nessuna email inviata)"
          style={{ padding: "10px 16px", background: selected.size ? "#6a1b9a" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          📝 Nota / 📞 Telefonata ({selected.size})
        </button>
        <button onClick={stampaElencoPdf} disabled={selected.size === 0} data-testid="com-print-list-btn"
          title="Genera un PDF stampabile con l'elenco degli alunni selezionati"
          style={{ padding: "10px 16px", background: selected.size ? "#455a64" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          🖨 Stampa elenco PDF ({selected.size})
        </button>
        {gestione !== "credito" && <button onClick={() => {
            const arr = Array.from(selected.values());
            setBulkBase(false);
            setBulkStampa(false);
            setBulkTotal(arr.length);
            setBulkQueue(arr);
          }} disabled={selected.size === 0} data-testid="com-send-btn"
          style={{ padding: "10px 18px", background: selected.size ? "#00838f" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          📧 Invia mail evoluto ({selected.size})
        </button>}
        {gestione !== "comunicator" && <button onClick={() => {
            const arr = Array.from(selected.values());
            setBulkBase(false);
            setBulkStampa(true);
            setBulkTotal(arr.length);
            setBulkQueue(arr);
          }} disabled={selected.size === 0} data-testid="com-stampa-btn"
          title="Genera i PDF (es. estratto conto) senza inviare email e registra la stampa nel log"
          style={{ padding: "10px 18px", background: selected.size ? "#5d4037" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          🖨 Stampa evoluto ({selected.size})
        </button>}
        {gestione !== "credito" && <button onClick={() => {
            const arr = Array.from(selected.values());
            setBulkBase(true);
            setBulkStampa(false);
            setBulkTotal(arr.length);
            setBulkQueue(arr);
          }} disabled={selected.size === 0} data-testid="com-send-base-btn"
          title="Invio semplice: solo Modello email + Email & Invio, senza allegati né firma"
          style={{ padding: "10px 18px", background: selected.size ? "#2e7d32" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          ✉ Invia mail base ({selected.size})
        </button>}
        {gestione !== "credito" && <button onClick={async () => {
            if (selected.size === 0) return;
            // Prompt per anno di competenza
            const defaultAnno = String(anno);
            const annoInput = window.prompt(
              `🚀 Routine Solleciti Insoluti\n\n` +
              `Invierà a ${selected.size} alunni selezionati:\n` +
              `• Template email: M001\n` +
              `• Allegato DOCX: D001\n` +
              `• Con i loro ADDEBITI NON PAGATI SCADUTI per l'anno indicato\n` +
              `  (ordinati per data scadenza)\n\n` +
              `Anno di competenza:`,
              defaultAnno
            );
            if (annoInput === null) return;
            const annoNum = parseInt(annoInput, 10);
            if (!Number.isFinite(annoNum) || annoNum < 2000 || annoNum > 2100) {
              alert("Anno non valido");
              return;
            }
            if (!window.confirm(
              `Confermi l'invio delle solleciti (M001 + D001)?\n\n` +
              `👥 Destinatari: ${selected.size}\n` +
              `🗓 Anno di competenza: ${annoNum}\n\n` +
              `Gli alunni SENZA addebiti scaduti verranno saltati automaticamente.\n\n` +
              `Procedere?`
            )) return;
            try {
              const r = await api.post("/comunicator/routine-solleciti-m001", {
                anno: annoNum,
                alunni_cod: Array.from(selected.keys()),
              });
              const d = r.data || {};
              const lines = [
                `✅ Routine completata`,
                ``,
                `📤 Inviate: ${d.sent ?? 0}`,
                `⚠ Errori: ${d.errors ?? 0}`,
              ];
              if (d.skipped_no_debts?.length) lines.push(`⏭ Saltati (senza addebiti scaduti): ${d.skipped_no_debts.length}`);
              if (d.skipped_not_found?.length) lines.push(`❓ Saltati (alunno non trovato): ${d.skipped_not_found.length}`);
              if (d.message) lines.push(`\nℹ ${d.message}`);
              alert(lines.join("\n"));
            } catch (e) {
              alert("Errore: " + (e.response?.data?.detail || e.message));
            }
          }} disabled={selected.size === 0} data-testid="com-solleciti-m001-btn"
          title="Routine automatica: invia M001+D001 con gli addebiti non pagati scaduti dell'anno indicato, ordinati per scadenza"
          style={{ padding: "10px 18px", background: selected.size ? "#c62828" : "#bbb", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: selected.size ? "pointer" : "not-allowed", borderRadius: 3 }}>
          🚀 Solleciti M001 → {selected.size}
        </button>}
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err}</div>}

      {/* Table - same columns as AlunniPage, optimized to fit screen */}
      <div className="sw-table-container" style={{ maxHeight: "62vh", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ tableLayout: "fixed", width: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
            <tr>
              <th style={{ width: 28, textAlign: "center" }}>
                <input type="checkbox" checked={allCurrentSelected} onChange={toggleAll} data-testid="com-toggleall" />
              </th>
              <th style={{ width: 60 }}>Cod</th>
              <th style={{ width: 200 }}>Cognome Nome / CF</th>
              <th style={{ width: 44, textAlign: "center" }} title="Ciclo curriculum dell'alunno per l'anno selezionato">Ciclo</th>
              <th style={{ width: 46 }}>Set.</th>
              <th style={{ width: 66 }}>Classe</th>
              <th style={{ width: 100 }}>Telefono</th>
              <th style={{ width: 170 }} title="Google mail @canossadiaz.it associata all'alunno">🎓 Google</th>
              <th style={{ width: 100, textAlign: "center" }} title="Data e ora dell'ultimo accesso al Portale Alunni">Ultimo Acc.</th>
              <th style={{ width: 130, textAlign: "center", background: "#fff3e0" }} title="Ultima email inviata a questo alunno (data + modello)">📧 Ultima Mail</th>
              <th style={{ width: 86, textAlign: "center" }}>Azioni</th>
              <th className="sortable num-col" onClick={() => toggleSort("tot_addebiti")} style={{ background: "#dde8f8", cursor: "pointer", width: 74 }}>Addeb.{sortArrow("tot_addebiti")}</th>
              <th className="sortable num-col" onClick={() => toggleSort("tot_non_pagato")} style={{ background: "#fef3cd", cursor: "pointer", width: 74 }}>N.Pag.{sortArrow("tot_non_pagato")}</th>
              <th className="sortable num-col" onClick={() => toggleSort("tot_non_pagato_scaduto")} style={{ background: "#f8d7da", cursor: "pointer", width: 74 }}>Scad.{sortArrow("tot_non_pagato_scaduto")}</th>
              <th className="sortable num-col" onClick={() => toggleSort("tot_dote")} style={{ background: "#cce5ff", cursor: "pointer", width: 66 }}>Dote{sortArrow("tot_dote")}</th>
              <th className="num-col" title="Dote Scuola — contributo regionale (tabella fissa)" style={{ background: "#c8e6c9", width: 78 }}>Dote Sc.</th>
            </tr>
          </thead>
          <tbody data-ph-version={customPhVersion}>
            {loading ? (
              <tr><td colSpan={16} className="sw-loading">Caricamento...</td></tr>
            ) : displayedItems.length === 0 ? (
              <tr><td colSpan={16} className="sw-empty"><p>{filtroCiclo === "_SELECTED_" ? "Nessun alunno selezionato" : "Nessun alunno trovato"}</p></td></tr>
            ) : displayedItems.map((a, i) => {
              const sel = selected.has(a.Cod);
              const sc = Number(a.tot_non_pagato_scaduto || 0);
              const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
              return (
                <tr key={`${a.Cod}-${a.anno}`} data-testid={`com-row-${a.Cod}`} onClick={() => toggleSel(a)}
                  style={{ cursor: "pointer", background: sel ? "#e8f5e9" : undefined }}>
                  <td className="center-col" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(a)} data-testid={`com-sel-${a.Cod}`} />
                  </td>
                  <td onClick={e => e.stopPropagation()} style={{ padding: "1px 4px", whiteSpace: "nowrap" }}>
                    {a.Cod}
                  </td>
                  <td style={{ padding: "1px 6px", overflow: "hidden" }} title={`${a.Cog || ""} ${a.Nom || ""}\n${a.CodFis || ""}`.trim()}>
                    <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ ...ellipsis, minWidth: 0 }}>{a.Cog} {a.Nom}</span>
                      {ridAttivi.has(String(a.CodFis || "").trim().toUpperCase()) && (
                        <span data-testid={`com-rid-badge-${a.Cod}`} title="Mandato RID attivo (addebito diretto)"
                          style={{ flex: "0 0 auto", padding: "0 5px", background: "#1b5e20", color: "white", fontSize: 9, fontWeight: 800, borderRadius: 3, letterSpacing: 0.5 }}>
                          RID
                        </span>
                      )}
                      {doteScuola.has(String(a.CodFis || "").trim().toUpperCase()) && (
                        <span data-testid={`com-dote-badge-${a.Cod}`} title="Dote Scuola (contributo regionale)"
                          style={{ flex: "0 0 auto", padding: "0 5px", background: "#1565c0", color: "white", fontSize: 9, fontWeight: 800, borderRadius: 3, letterSpacing: 0.3 }}>
                          DOTE SCUOLA
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: "#777", fontFamily: "Consolas,monospace", ...ellipsis }}>{a.CodFis || "-"}</div>
                  </td>
                  <td className="center-col" style={{ padding: "1px 2px" }}>
                    {a.ciclo_curriculum ? (
                      <span data-testid={`com-ciclo-${a.Cod}`}
                        title={`Ciclo curriculum: ${a.ciclo_curriculum} (override anno ${a.anno || anno})`}
                        style={{
                          display: "inline-block",
                          padding: "1px 6px",
                          background: (a.ciclo_curriculum === "I" ? "#fff3e0"
                                    : a.ciclo_curriculum?.startsWith("P") ? "#e8f5e9"
                                    : a.ciclo_curriculum?.startsWith("M") ? "#e3f2fd"
                                    : a.ciclo_curriculum === "U" ? "#eceff1"
                                    : "#f5f5f5"),
                          color: (a.ciclo_curriculum === "I" ? "#bf360c"
                               : a.ciclo_curriculum?.startsWith("P") ? "#1b5e20"
                               : a.ciclo_curriculum?.startsWith("M") ? "#0d47a1"
                               : a.ciclo_curriculum === "U" ? "#37474f"
                               : "#777"),
                          border: "1px solid currentColor",
                          fontSize: 10, fontWeight: 800, borderRadius: 2,
                        }}>
                        {a.ciclo_curriculum}
                      </span>
                    ) : (
                      <span style={{ color: "#ccc", fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td className="center-col">{a.settore || "-"}</td>
                  <td style={ellipsis} title={a.classe || "-"}>{a.classe || "-"}</td>
                  <td style={{ fontSize: 10, ...ellipsis }} title={a.Tel1 || a.Tel2 || "-"}>{a.Tel1 || a.Tel2 || "-"}</td>
                  <td style={{ fontSize: 10, padding: "1px 6px", fontFamily: "Consolas,monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    data-testid={`com-googleemail-${a.Cod}`}
                    title={a.google_email || "Nessuna mail @canossadiaz.it associata"}>
                    {a.google_email
                      ? <span style={{ color: "#1565c0", fontWeight: 600 }}>{a.google_email}</span>
                      : <span style={{ color: "#ccc" }}>—</span>}
                  </td>
                  <td className="center-col" style={{ padding: "1px 4px", fontSize: 10, fontFamily: "Consolas,monospace", whiteSpace: "nowrap" }}
                    data-testid={`com-ultimoaccesso-${a.Cod}`}
                    title={a.ultimo_accesso ? `Ultimo accesso al Portale Alunni: ${a.ultimo_accesso}` : "Nessun accesso registrato al Portale Alunni"}>
                    {(() => {
                      if (!a.ultimo_accesso) return <span style={{ color: "#ccc" }}>—</span>;
                      try {
                        const d = new Date(a.ultimo_accesso);
                        if (isNaN(d.getTime())) return <span style={{ color: "#ccc" }}>—</span>;
                        const dd = String(d.getDate()).padStart(2, "0");
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        const yy = String(d.getFullYear()).slice(-2);
                        const hh = String(d.getHours()).padStart(2, "0");
                        const mi = String(d.getMinutes()).padStart(2, "0");
                        return (
                          <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                            {dd}/{mm}/{yy} {hh}:{mi}
                          </span>
                        );
                      } catch {
                        return <span style={{ color: "#ccc" }}>—</span>;
                      }
                    })()}
                  </td>
                  {/* 🆕 Feb 2026: Ultima Mail inviata (data + template) */}
                  <td className="center-col" style={{ padding: "1px 4px", fontSize: 10, whiteSpace: "nowrap", background: a.ultima_mail_at ? "#fff8e1" : "#fafafa" }}
                    data-testid={`com-ultimamail-${a.Cod}`}
                    title={a.ultima_mail_at
                      ? `Ultima email: ${a.ultima_mail_template_label || a.ultima_mail_template_id || "?"}\nInviata il ${a.ultima_mail_at}`
                      : "Nessuna email inviata a questo alunno"}>
                    {(() => {
                      if (!a.ultima_mail_at) return <span style={{ color: "#ccc" }}>—</span>;
                      try {
                        const d = new Date(a.ultima_mail_at);
                        if (isNaN(d.getTime())) return <span style={{ color: "#ccc" }}>—</span>;
                        const dd = String(d.getDate()).padStart(2, "0");
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        const yy = String(d.getFullYear()).slice(-2);
                        const hh = String(d.getHours()).padStart(2, "0");
                        const mi = String(d.getMinutes()).padStart(2, "0");
                        return (
                          <div style={{ lineHeight: 1.2, fontFamily: "Consolas,monospace" }}>
                            <div style={{ color: "#e65100", fontWeight: 700 }}>
                              {dd}/{mm}/{yy} {hh}:{mi}
                            </div>
                            <div style={{ color: "#5d4037", fontWeight: 600, fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", maxWidth: 135 }}>
                              {String(a.ultima_mail_template_id || "").toUpperCase()}
                            </div>
                          </div>
                        );
                      } catch {
                        return <span style={{ color: "#ccc" }}>—</span>;
                      }
                    })()}
                    {/* 🆕 Feb 2026: bottone Storico invii — apre popup con checkbox e placeholder custom */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setInviiModalCod(a.Cod); }}
                      data-testid={`com-invii-${a.Cod}`}
                      title="Storico invii a questo alunno — spunta gli invii per creare un placeholder personalizzato da usare nel wizard"
                      style={{
                        marginTop: 3, padding: "1px 6px",
                        background: hasCustomPlaceholder(a.Cod) ? "#e1bee7" : "white",
                        color: "#6a1b9a", border: "1px solid #6a1b9a",
                        fontSize: 9, fontWeight: 800, cursor: "pointer", borderRadius: 2, lineHeight: 1.2,
                      }}
                    >
                      📜 Invii{hasCustomPlaceholder(a.Cod) ? " ★" : ""}
                    </button>
                  </td>
                  <td style={{ textAlign: "center", padding: "1px 2px" }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => navigate(`/g/segremat/movimenti?codice_alunno=${a.Cod}&anno=${anno}&back=${encodeURIComponent("/g/comunicator/comunicator")}&back_label=${encodeURIComponent("Torna alle Comunicazioni")}`)}
                      data-testid={`com-mov-${a.Cod}`}
                      title="Apri lista movimenti di questo alunno"
                      style={{ padding: "2px 8px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 10, fontWeight: 800, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap" }}>
                      📋 Mov.
                    </button>
                  </td>
                  <td className="num-col" style={{ background: "#f0f4ff" }}>{a.tot_addebiti ? formatCurrency(a.tot_addebiti) : "-"}</td>
                  <td className="num-col amount-warning" style={{ background: "#fffbeb" }}>{a.tot_non_pagato ? formatCurrency(a.tot_non_pagato) : "-"}</td>
                  <td className="num-col amount-negative" style={{ background: sc > 0 ? "#fff0f0" : undefined, fontWeight: sc > 0 ? 700 : 400 }}>{sc ? formatCurrency(sc) : "-"}</td>
                  <td className="num-col" style={{ background: "#e6f0ff", color: "#1976d2", fontWeight: 600 }}>{a.tot_dote ? formatCurrency(a.tot_dote) : "-"}</td>
                  <td className="num-col" data-testid={`com-dote-cell-${a.Cod}`} style={{ background: "#eef7ee", color: "#1565c0", fontWeight: 700 }}>{doteScuola.get(String(a.CodFis || "").trim().toUpperCase()) ? formatCurrency(doteScuola.get(String(a.CodFis || "").trim().toUpperCase())) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modalità sequenziale: il wizard viene aperto UNA volta per ogni alunno della coda.
          `key` forza il remount tra un alunno e l'altro così tutti gli step si resettano. */}
      {bulkQueue.length > 0 && (() => {
        const current = bulkQueue[0];
        const idx = bulkTotal - bulkQueue.length + 1;
        // 🖨 STAMPA: tutti gli alunni in un'unica passata (targets = intera coda), niente sequenziale.
        if (bulkStampa) {
          return (
            <SendBulkModal
              key="bulk-stampa"
              targets={bulkQueue}
              anno={anno}
              baseMode={false}
              stampaMode={true}
              bulkProgress={null}
              onClose={() => { setBulkQueue([]); setBulkTotal(0); setBulkStampa(false); }}
              onDone={() => { setBulkQueue([]); setBulkTotal(0); setBulkStampa(false); }}
              onSkip={null}
            />
          );
        }
        return (
          <SendBulkModal
            key={`bulk-${current.Cod}-${idx}`}
            targets={[current]}
            anno={anno}
            baseMode={bulkBase}
            stampaMode={false}
            bulkProgress={bulkTotal > 1 ? { current: idx, total: bulkTotal } : null}
            onClose={() => { setBulkQueue([]); setBulkTotal(0); setBulkStampa(false); }}
            onDone={() => {
              // toglie il primo della coda, dopo l'invio rimuove la spunta solo per quell'alunno
              setSelected(prev => { const m = new Map(prev); m.delete(current.Cod); return m; });
              setBulkQueue(prev => {
                const next = prev.slice(1);
                if (next.length === 0) setBulkTotal(0);
                return next;
              });
            }}
            onSkip={bulkTotal > 1 ? () => {
              // Salta silenziosamente questo alunno e passa al successivo (nessun log, nessuna conferma)
              setBulkQueue(prev => {
                const next = prev.slice(1);
                if (next.length === 0) setBulkTotal(0);
                return next;
              });
            } : null}
          />
        );
      })()}
      {manualLogOpen && (
        <ManualLogModal
          targets={Array.from(selected.values())}
          anno={anno}
          onClose={() => setManualLogOpen(false)}
          onDone={() => { setManualLogOpen(false); setSelected(new Map()); }}
        />
      )}
      {emailsModalCod !== null && (() => {
        const a = items.find(x => x.Cod === emailsModalCod);
        return <AlunnoEmailsModal cod={emailsModalCod} alunnoLabel={a ? `${a.Cog||""} ${a.Nom||""} (#${a.Cod})` : `Cod. ${emailsModalCod}`}
          onClose={() => setEmailsModalCod(null)}
          onUpdated={() => {
            // ricarica counts per quel cod
            api.post("/alunni-emails/_counts", { cods: items.map(x => x.Cod) })
              .then(r => setEmailsCounts(r.data?.counts || {})).catch(() => { });
          }} />;
      })()}
      {/* 🆕 Feb 2026: modal Storico invii + placeholder custom */}
      {inviiModalCod !== null && (() => {
        const a = items.find(x => x.Cod === inviiModalCod);
        return <AlunnoInviiModal cod={inviiModalCod}
          alunnoLabel={a ? `${a.Cog||""} ${a.Nom||""} (#${a.Cod})` : `Cod. ${inviiModalCod}`}
          onClose={() => setInviiModalCod(null)}
          onSaved={() => setCustomPhVersion(v => v + 1)} />;
      })()}
      {/* 🆕 Feb 2026: modal movimenti iscrizione (aperta dal badge 🎟) */}
      {iscrModal && <MovimentiCollegatiModal data={iscrModal} onClose={() => setIscrModal(null)} />}
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ background: "white", border: `2px solid ${color}`, padding: "6px 12px", minWidth: 110, textAlign: "center", borderRadius: 3 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: "#666", marginTop: 3, textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
    </div>
  );
}

// Ordine di visualizzazione/navigazione degli step del wizard di invio.
// Lo step "logico" resta lo stesso ID (1..5), ma viene mostrato/navigato in
// questo ordine richiesto dall'utente: Addebiti → Firma/Protocollo → Modello
// → Modulo PDF → Email & Invio.
const STEP_ORDER = [2, 4, 1, 3, 5];

// =================================================================
// MODALE NOTA / TELEFONATA: registra un evento manuale nel log
// Comunicator per i destinatari selezionati. Nessuna email viene
// inviata. Una riga di log per alunno.
// =================================================================
function ManualLogModal({ targets, anno, onClose, onDone }) {
  const isoNow = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [kind, setKind] = useState("telefonata");
  const [dataEvento, setDataEvento] = useState(isoNow());
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSave = async () => {
    if (!testo.trim()) { setErr("Inserisci il testo della nota / telefonata"); return; }
    setBusy(true); setErr("");
    try {
      const alunni = (targets || []).map((t) => ({
        cod: t.Cod, anno: t.anno || anno,
        cognome: t.Cog || t.cognome, nome: t.Nom || t.nome,
        classe: t.classe, settore: t.settore,
      }));
      await api.post("/comunicator/manual-log", {
        alunni, kind, testo, data_evento: dataEvento,
      });
      onDone && onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const kindCfg = kind === "telefonata"
    ? { icon: "📞", label: "Telefonata", color: "#6a1b9a", bg: "#f3e5f5" }
    : { icon: "📝", label: "Nota interna", color: "#1565c0", bg: "#e3f2fd" };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800,
               display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="manual-log-modal"
        style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 6,
                 overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
        <div style={{ background: kindCfg.color, color: "white", padding: "14px 18px",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: 15 }}>{kindCfg.icon} Registra {kindCfg.label} — {targets.length} alunno{targets.length === 1 ? "" : "i"}</strong>
          <button onClick={onClose} data-testid="manual-log-close"
            style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: 20, overflowY: "auto" }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#37474f", letterSpacing: 0.3, marginBottom: 6 }}>TIPO EVENTO</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { v: "telefonata", icon: "📞", lbl: "Telefonata", c: "#6a1b9a" },
                { v: "nota",       icon: "📝", lbl: "Nota interna", c: "#1565c0" },
              ].map((opt) => (
                <button key={opt.v} type="button" onClick={() => setKind(opt.v)}
                  data-testid={`manual-log-kind-${opt.v}`}
                  style={{
                    flex: 1, padding: "12px 14px", border: `2px solid ${kind === opt.v ? opt.c : "#cfd8dc"}`,
                    background: kind === opt.v ? opt.c : "white",
                    color: kind === opt.v ? "white" : "#37474f",
                    fontSize: 13, fontWeight: 800, cursor: "pointer", borderRadius: 4,
                    transition: "all 0.15s",
                  }}>
                  <span style={{ fontSize: 22, display: "block", marginBottom: 2 }}>{opt.icon}</span>
                  {opt.lbl}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#37474f", letterSpacing: 0.3, marginBottom: 6 }}>DATA E ORA</div>
            <input type="datetime-local" value={dataEvento}
              data-testid="manual-log-datetime"
              onChange={(e) => setDataEvento(e.target.value)}
              style={{ padding: "9px 12px", fontSize: 14, border: "1px solid #b0bec5",
                       borderRadius: 3, width: "100%", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }} />
            <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
              Default: adesso · puoi modificare per registrare un evento passato
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#37474f", letterSpacing: 0.3, marginBottom: 6 }}>
              TESTO {kind === "telefonata" ? "(contenuto telefonata)" : "(nota)"}
            </div>
            <textarea value={testo} onChange={(e) => setTesto(e.target.value)}
              data-testid="manual-log-text"
              placeholder={kind === "telefonata"
                ? "Es. Chiamata con la madre — confermata frequenza mensa lunedì.\nRichiesto cambio orario di consegna."
                : "Es. Famiglia ha consegnato modulo F23 in segreteria.\nAttesa conferma scadenza..."}
              style={{ width: "100%", minHeight: 140, padding: 10, fontSize: 13,
                       border: "1px solid #b0bec5", borderRadius: 3, fontFamily: "inherit",
                       resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ fontSize: 10, color: testo.length > 0 ? "#1b5e20" : "#888", marginTop: 3 }}>
              {testo.length} caratteri · prima riga usata come oggetto nel log
            </div>
          </div>

          <div style={{ padding: 10, background: kindCfg.bg, borderRadius: 3,
                        fontSize: 12, color: kindCfg.color, marginBottom: 12 }}>
            <b>{kindCfg.icon} Verrà registrata 1 voce nel log per ciascuno dei {targets.length} alunn{targets.length === 1 ? "o" : "i"} selezionat{targets.length === 1 ? "o" : "i"}:</b>
            <div style={{ marginTop: 6, maxHeight: 80, overflowY: "auto", fontSize: 11, color: "#37474f" }}>
              {targets.slice(0, 8).map((t) => (
                <div key={t.Cod}>· {t.Cog} {t.Nom} <span style={{ color: "#999" }}>(#{t.Cod})</span></div>
              ))}
              {targets.length > 8 && <div style={{ fontStyle: "italic", color: "#999" }}>… e altri {targets.length - 8}</div>}
            </div>
          </div>

          {err && (
            <div data-testid="manual-log-error"
              style={{ marginBottom: 10, padding: 10, background: "#ffebee",
                       color: "#b71c1c", border: "1px solid #ef9a9a",
                       fontSize: 12, borderRadius: 3 }}>
              ⚠ {err}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px", background: "#eceff1",
                      display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #cfd8dc" }}>
          <button onClick={onClose}
            style={{ padding: "9px 18px", background: "white", color: "#37474f",
                     border: "1px solid #b0bec5", fontSize: 12, fontWeight: 700,
                     cursor: "pointer", borderRadius: 3 }}>
            Annulla
          </button>
          <button onClick={onSave} disabled={busy || !testo.trim()}
            data-testid="manual-log-save"
            style={{ padding: "9px 22px", background: busy || !testo.trim() ? "#bbb" : kindCfg.color,
                     color: "white", border: "none", fontSize: 13, fontWeight: 800,
                     cursor: busy || !testo.trim() ? "not-allowed" : "pointer", borderRadius: 3,
                     letterSpacing: 0.3 }}>
            {busy ? "⏳ Salvataggio…" : `${kindCfg.icon} SALVA NEL LOG`}
          </button>
        </div>
      </div>
    </div>
  );
}


function SendBulkModal({ targets, anno, onClose, onDone, onSkip, bulkProgress, baseMode = false, stampaMode = false }) {
  // Modalità "base": wizard ridotto a Modello email (1) + Email & Invio (5), senza allegati né firma.
  const ORDER = baseMode ? [1, 5] : STEP_ORDER;
  // === STEP 1 - Modello mail ===
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [preview, setPreview] = useState(null);
  // === STEP 2 - Movimenti / addebiti ===
  // 🆕 Feb 2026: filtri persistiti su localStorage (chiave `com_send_movFiltri_v1`).
  //     I settaggi scelti dall'utente nel primo step del wizard vengono riproposti alla successiva apertura.
  //     Il pulsante "Resetta filtri" cancella anche il salvataggio.
  const MOV_FILTRI_LS_KEY = "com_send_movFiltri_v1";
  const FILTRI_DEFAULT = {
    anno_comp_da: "", anno_comp_a: "",
    anno_sca_da: "", anno_sca_a: "",
    anno_pag_da: "", anno_pag_a: "",
    sort_by: "DtaSca", sort_dir: "asc",
    solo_addebiti: true,
    stato_pagamento: "",
    causali: [],
    modalita: ["Bonifico", "Banca-Pos", "SDD Bancario"],
    tutti_anni: true,
  };
  const _loadMovFiltriFromLS = () => {
    try {
      const raw = window.localStorage.getItem(MOV_FILTRI_LS_KEY);
      if (!raw) return FILTRI_DEFAULT;
      const saved = JSON.parse(raw);
      // merge sui default per proteggersi da chiavi rimosse/aggiunte in futuro
      return { ...FILTRI_DEFAULT, ...saved };
    } catch { return FILTRI_DEFAULT; }
  };
  const [movFiltri, setMovFiltri] = useState(_loadMovFiltriFromLS);
  // Persisti su ogni cambio
  useEffect(() => {
    try { window.localStorage.setItem(MOV_FILTRI_LS_KEY, JSON.stringify(movFiltri)); } catch { /* quota/privacy */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movFiltri]);
  const [movPerAlunno, setMovPerAlunno] = useState({}); // {cod: [movimenti]}
  // Tabella completa delle modalità di pagamento (tutte quelle del sistema, non solo quelle presenti nei movimenti)
  const [modalitaTabella, setModalitaTabella] = useState([]);
  // Filtro alunno nello Step 2 (anteprima movimenti per singolo alunno)
  const [filtroAlunnoStep2, setFiltroAlunnoStep2] = useState("");
  const [exclusi, setExclusi] = useState({}); // {cod: Set(ids esclusi manualmente)}
  // Movimenti forzati a essere inclusi nonostante il filtro Causale/Modalità.
  // Usato quando l'utente spunta manualmente un movimento che il filtro escluderebbe.
  const [forced, setForced] = useState({}); // {cod: Set(ids forzati a essere inclusi)}
  const [loadingMov, setLoadingMov] = useState(false);
  const [previewAlunnoIdx, setPreviewAlunnoIdx] = useState(0);
  // === STEP 3 - Modulo PDF allegato ===
  const [moduli, setModuli] = useState([]);
  const [selectedModulo, setSelectedModulo] = useState("");
  // Allegato DOCX (nuovo sistema: file Word con placeholder che diventa PDF al momento dell'invio)
  const [allegatiDocx, setAllegatiDocx] = useState([]);
  const [selectedAllegatoDocx, setSelectedAllegatoDocx] = useState("");
  // 🆕 Giu 2026: proposta allegato all'atto della scelta del modello.
  //   1) Se il modello ha un allegato DOCX collegato esplicitamente (campo "allegato_docx_id"
  //      impostato nel tab ✉ Modelli), viene proposto quello.
  //   2) Altrimenti fallback convenzione Mxxx → allegato con nome "dNNN".
  //   In entrambi i casi l'utente può cambiarlo liberamente in sede di invio (step Modulo PDF).
  useEffect(() => {
    if (!selectedTpl) return;
    const tpl = (templates || []).find(t => t.id === selectedTpl);
    if (tpl?.allegato_docx_id && (allegatiDocx || []).some(a => a.id === tpl.allegato_docx_id)) {
      setSelectedAllegatoDocx(tpl.allegato_docx_id);
      return;
    }
    const m = String(selectedTpl).toLowerCase().match(/^m(\d+)$/);
    if (!m) return;
    const wantedNome = `d${m[1]}`;
    const found = (allegatiDocx || []).find(a => String(a.nome || "").trim().toLowerCase() === wantedNome);
    if (found) setSelectedAllegatoDocx(found.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTpl, templates, allegatiDocx]);
  // Tipo PDF predefinito (alternativo al modulo custom)
  const [tipoPredefinito, setTipoPredefinito] = useState(""); // ESTRATTO_CONTO | DICHIARAZIONE_WELFARE | DICHIARAZIONE_DDR | "" (=allegato DOCX custom)
  const [annoFiscale, setAnnoFiscale] = useState(new Date().getFullYear() - 1);
  // === STEP 4 - Firma ===
  const [firme, setFirme] = useState([]);
  const [selectedFirma, setSelectedFirma] = useState("");
  // === Protocollo & invio ===
  const [protoPeek, setProtoPeek] = useState(null);
  const [protocolloOverride, setProtocolloOverride] = useState("");
  const [dataInvioOverride, setDataInvioOverride] = useState(() => {
    // default = adesso (YYYY-MM-DDTHH:MM, local timezone)
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [dataAggiornamento, setDataAggiornamento] = useState("");
  const [dataAggiornamentoAutoLoaded, setDataAggiornamentoAutoLoaded] = useState(false);
  const [step, setStep] = useState(ORDER[0]);
  // 🆕 Feb 2026: ultimo invio all'alunno (visualizzato nello step 2 - prima schermata)
  // Attivo solo per invii singoli o sequenziali (1 alunno alla volta).
  const [lastSend, setLastSend] = useState(null);
  const [lastSendLoading, setLastSendLoading] = useState(false);
  // 🆕 Feb 2026: fetch ultimo invio per l'alunno corrente (invio singolo/sequenziale)
  useEffect(() => {
    if (!targets || targets.length !== 1 || !targets[0]?.Cod) {
      setLastSend(null); return;
    }
    let cancelled = false;
    setLastSendLoading(true);
    getComunicatorOutboxByAlunno(targets[0].Cod, { limit: 1 })
      .then(r => { if (!cancelled) setLastSend((r.data?.items || [])[0] || null); })
      .catch(() => { if (!cancelled) setLastSend(null); })
      .finally(() => { if (!cancelled) setLastSendLoading(false); });
    return () => { cancelled = true; };
  }, [targets]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [loadingPdfPreview, setLoadingPdfPreview] = useState(false);
  // Selezione genitore per il placeholder {genitore_selezionato_*}
  // valori: "" (nessuno) | "1" (primo genitore) | "2" (secondo genitore)
  const [genitoreSelezionato, setGenitoreSelezionato] = useState("");
  // Anteprima dati del genitore selezionato per l'alunno corrente (step 4 visivo)
  const [genitoriPreview, setGenitoriPreview] = useState(null);
  const [loadingGenitoriPreview, setLoadingGenitoriPreview] = useState(false);
  const [genitoriPreviewIdx, setGenitoriPreviewIdx] = useState(0);
  // === STEP 5 - Email destinatari ===
  // emailsPerAlunno[cod] = [{ id, email, source, note, selected: bool }]
  const [emailsPerAlunno, setEmailsPerAlunno] = useState({});
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [addEmailFor, setAddEmailFor] = useState(null); // cod alunno in cui aggiungere email inline
  const [newEmailVal, setNewEmailVal] = useState("");
  const [savingNewEmail, setSavingNewEmail] = useState(false);
  // Invio email di prova
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Opzione: includi anche `alunni.google_email` nell'invio (oltre alle email archivio)
  const [includeGoogleEmail, setIncludeGoogleEmail] = useState(false);
  // Crediti Resend (check preventivo)
  const [credit, setCredit] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);

  const loadCredit = async (refresh = false) => {
    setCreditLoading(true);
    try {
      const r = await api.get("/comunicator/resend/credit", { params: refresh ? { refresh: 1 } : {} });
      setCredit(r.data);
    } catch (e) {
      setCredit({ configured: false, error_it: e.response?.data?.detail || e.message });
    } finally { setCreditLoading(false); }
  };

  // Caricamenti iniziali in parallelo
  useEffect(() => {
    api.get("/comunicator/templates").then(r => {
      // 🆕 Feb 2026: ordina i modelli per CODICE (M001, M002, ...) e non per descrizione (label)
      const items = (r.data.items || []).slice().sort((a, b) => String(a.id || "").localeCompare(String(b.id || ""), "it", { numeric: true }));
      setTemplates(items);
      // NOTA: nessun template viene preselezionato — l'utente deve sceglierlo esplicitamente nello step "Modello email" del wizard.
    }).catch(e => setErr(e.response?.data?.detail || "Errore caricamento modelli"));
    api.get("/comunicator/moduli").then(r => setModuli(r.data.items || [])).catch(() => { });
    api.get("/comunicator/allegati-docx").then(r => setAllegatiDocx(r.data.items || [])).catch(() => { });
    api.get("/comunicator/firme").then(r => setFirme(r.data.items || [])).catch(() => { });
    // Carico la tabella completa delle modalità di pagamento per popolare il filtro
    // (così appaiono tutte le modalità del sistema, non solo quelle presenti nei movimenti caricati).
    api.get("/tabelle/modalita").then(r => setModalitaTabella(r.data?.data || [])).catch(() => { });
    api.get("/comunicator/protocollo/peek").then(r => {
      setProtoPeek(r.data);
      setProtocolloOverride(r.data?.prossimo || "");
    }).catch(() => { });
    // Carica l'ULTIMA data di registrazione bonifici da Stati di Cassa - Banca
    // → questo è il valore di default per "DATA AGGIORNAMENTO DATI" del wizard.
    api.get("/stati-cassa/banca/stats").then(r => {
      const dmy = r.data?.data_max; // formato dd/mm/yyyy
      if (dmy && typeof dmy === "string") {
        const m = dmy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) {
          setDataAggiornamento(`${m[3]}-${m[2]}-${m[1]}`);
          setDataAggiornamentoAutoLoaded(true);
        }
      }
    }).catch(() => { });
  }, []);

  // Anteprima testo email (alunno corrente in preview)
  useEffect(() => {
    if (!selectedTpl || targets.length === 0) return;
    const a = targets[previewAlunnoIdx] || targets[0];
    api.post("/comunicator/anteprima", {
      template_id: selectedTpl, alunno_cod: a.Cod, anno,
    }).then(r => setPreview(r.data)).catch(() => setPreview(null));
  }, [selectedTpl, targets, previewAlunnoIdx, anno]);

  // Caricamento movimenti per tutti gli alunni quando entriamo nello step 2 o cambia filtro
  const caricaMovimenti = useCallback(async () => {
    setLoadingMov(true);
    setErr(null);
    try {
      const params = { sort_by: movFiltri.sort_by, sort_dir: movFiltri.sort_dir, solo_addebiti: movFiltri.solo_addebiti };
      // Se non si vuole "tutti gli anni", restringe al solo anno scolastico corrente
      if (!movFiltri.tutti_anni) params.anno = anno;
      if (movFiltri.stato_pagamento) params.stato_pagamento = movFiltri.stato_pagamento;
      if (movFiltri.anno_comp_da) params.anno_competenza_da = movFiltri.anno_comp_da;
      if (movFiltri.anno_comp_a) params.anno_competenza_a = movFiltri.anno_comp_a;
      if (movFiltri.anno_sca_da) params.anno_scadenza_da = movFiltri.anno_sca_da;
      if (movFiltri.anno_sca_a) params.anno_scadenza_a = movFiltri.anno_sca_a;
      if (movFiltri.anno_pag_da) params.anno_pagamento_da = movFiltri.anno_pag_da;
      if (movFiltri.anno_pag_a) params.anno_pagamento_a = movFiltri.anno_pag_a;
      const out = {};
      // batch in serie per evitare di sommergere il backend
      for (const a of targets) {
        try {
          const r = await api.get(`/comunicator/movimenti-alunno/${a.Cod}`, { params });
          out[a.Cod] = r.data.items || [];
        } catch { out[a.Cod] = []; }
      }
      setMovPerAlunno(out);
      // mantengo le esclusioni e i "forzati" dell'utente; filtro a quelle ancora valide
      setExclusi(prev => {
        const next = {};
        for (const [cod, ids] of Object.entries(prev)) {
          const valid = new Set((out[cod] || []).map(m => m.id));
          const filtered = new Set();
          for (const id of ids) if (valid.has(id)) filtered.add(id);
          if (filtered.size > 0) next[cod] = filtered;
        }
        return next;
      });
      setForced(prev => {
        const next = {};
        for (const [cod, ids] of Object.entries(prev)) {
          const valid = new Set((out[cod] || []).map(m => m.id));
          const filtered = new Set();
          for (const id of ids) if (valid.has(id)) filtered.add(id);
          if (filtered.size > 0) next[cod] = filtered;
        }
        return next;
      });
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento movimenti");
    } finally { setLoadingMov(false); }
  }, [anno, movFiltri, targets]);

  useEffect(() => {
    if (step !== 2) return;
    const t = setTimeout(() => { caricaMovimenti(); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [step, movFiltri]);

  // === STEP 4 (Firma & Protocollo): Caricamento anteprima genitori per l'alunno corrente ===
  useEffect(() => {
    if (step !== 4) return;
    const a = targets[genitoriPreviewIdx] || targets[0];
    if (!a) { setGenitoriPreview(null); return; }
    let alive = true;
    setLoadingGenitoriPreview(true);
    api.get(`/comunicator/genitori-preview`, {
      params: { alunno_cod: a.Cod, anno, genitore_selezionato: genitoreSelezionato || undefined },
    }).then(r => {
      if (alive) setGenitoriPreview(r.data || null);
    }).catch(() => {
      if (alive) setGenitoriPreview(null);
    }).finally(() => {
      if (alive) setLoadingGenitoriPreview(false);
    });
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [step, genitoreSelezionato, genitoriPreviewIdx, targets.length, anno]);

  // === STEP 5: Caricamento email per ciascun alunno ===
  const caricaEmailsDestinatari = useCallback(async () => {
    setLoadingEmails(true);
    try {
      const out = {};
      for (const a of targets) {
        try {
          const r = await api.get(`/alunni-emails/${a.Cod}`);
          const items = (r.data?.items || []).map(it => ({ ...it, selected: true }));
          // dedup per email
          const seen = new Set();
          out[a.Cod] = items.filter(it => {
            const k = String(it.email || "").trim().toLowerCase();
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        } catch { out[a.Cod] = []; }
      }
      setEmailsPerAlunno(prev => {
        // Conserva eventuali scelte 'selected' dell'utente se le email coincidono
        const merged = {};
        for (const cod of Object.keys(out)) {
          const prevList = prev[cod] || [];
          const prevMap = new Map(prevList.map(x => [String(x.email || "").toLowerCase(), x.selected]));
          merged[cod] = out[cod].map(x => ({
            ...x,
            selected: prevMap.has(String(x.email || "").toLowerCase()) ? prevMap.get(String(x.email || "").toLowerCase()) : true,
          }));
        }
        return merged;
      });
    } finally { setLoadingEmails(false); }
  }, [targets]);

  useEffect(() => {
    if (step !== 5) return;
    caricaEmailsDestinatari();
    /* eslint-disable-next-line */
  }, [step]);

  const toggleEmailSelezione = (cod, email) => {
    setEmailsPerAlunno(prev => {
      const list = (prev[cod] || []).map(x => x.email === email ? { ...x, selected: !x.selected } : x);
      return { ...prev, [cod]: list };
    });
  };

  // Imposta (o azzera) l'email preferita per un alunno.
  // Solo UNA email per alunno può essere preferita.
  const togglePreferita = async (cod, emailItem) => {
    if (!emailItem?.id) return;
    const newValue = !emailItem.is_preferred;
    // Optimistic UI update
    setEmailsPerAlunno(prev => {
      const list = (prev[cod] || []).map(x => ({
        ...x,
        is_preferred: x.id === emailItem.id ? newValue : (newValue ? false : x.is_preferred),
      }));
      return { ...prev, [cod]: list };
    });
    try {
      await api.put(`/alunni-emails/${cod}/${emailItem.id}/preferred`, { value: newValue });
    } catch (e) {
      // Rollback
      setEmailsPerAlunno(prev => ({ ...prev, [cod]: (prev[cod] || []).map(x => ({ ...x, is_preferred: x.id === emailItem.id ? !newValue : x.is_preferred })) }));
      setErr(e.response?.data?.detail || "Errore aggiornamento email preferita");
    }
  };

  // Modalità di selezione per l'invio (multi-selezionabili, eccezione: tutte ↔ preferita si escludono)
  // - preferita: invia alla mail preferita di ciascun alunno (fallback prima se nessuna)
  // - tutte: invia a tutte le mail collegate (include implicitamente la preferita)
  // - google: invia all'email @canossadiaz.it
  // Possono essere combinati (es. tutte + google) tranne preferita+tutte (tutte già la include).
  const [modiInvio, setModiInvio] = useState({ preferita: false, tutte: true, google: false });
  const isGoogleEmail = (e) => /@canossadiaz\.it\s*$/i.test(String(e || "").trim());

  // Calcola la selezione di una mail in base ai modi attivi
  const calcSelectionForList = (list, modi) => {
    const hasPref = list.some(y => y.is_preferred);
    return list.map(x => {
      let sel = false;
      if (modi.tutte) sel = sel || true;
      if (modi.preferita) {
        const fallback = !hasPref && list[0] && list[0].id === x.id;
        sel = sel || !!x.is_preferred || fallback;
      }
      if (modi.google) sel = sel || isGoogleEmail(x.email);
      return { ...x, selected: sel };
    });
  };

  const applicaModi = (nuoviModi) => {
    setModiInvio(nuoviModi);
    setEmailsPerAlunno(prev => {
      const out = {};
      for (const cod of Object.keys(prev)) {
        out[cod] = calcSelectionForList(prev[cod] || [], nuoviModi);
      }
      return out;
    });
  };

  const toggleModoInvio = (key) => {
    setModiInvio(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // Mutua esclusione: preferita ↔ tutte
      if (key === "tutte" && next.tutte) next.preferita = false;
      if (key === "preferita" && next.preferita) next.tutte = false;
      // Almeno un modo attivo
      if (!next.preferita && !next.tutte && !next.google) {
        // ripristina ultimo modo: forza "tutte"
        next.tutte = true;
      }
      // Applica subito la selezione
      setEmailsPerAlunno(em => {
        const out = {};
        for (const cod of Object.keys(em)) {
          out[cod] = calcSelectionForList(em[cod] || [], next);
        }
        return out;
      });
      return next;
    });
  };

  const aggiungiEmailAlunno = async (cod) => {
    const em = (newEmailVal || "").trim().toLowerCase();
    if (!em || !em.includes("@")) { setErr("Email non valida"); return; }
    setSavingNewEmail(true); setErr(null);
    try {
      await api.post(`/alunni-emails/${cod}`, { email: em, note: "Aggiunta da Invio mail", source: "comunicator" });
      setNewEmailVal("");
      setAddEmailFor(null);
      // Ricarica le email dell'alunno
      const r = await api.get(`/alunni-emails/${cod}`);
      const items = (r.data?.items || []).map(it => ({ ...it, selected: true }));
      const seen = new Set();
      const cleaned = items.filter(it => {
        const k = String(it.email || "").trim().toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setEmailsPerAlunno(prev => ({ ...prev, [cod]: cleaned }));
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore aggiunta email");
    } finally { setSavingNewEmail(false); }
  };

  const rimuoviEmailAlunno = async (cod, emailItem) => {
    if (!emailItem?.id) { setErr("Email non cancellabile (id mancante)"); return; }
    if (!window.confirm(`Cancellare definitivamente l'email ${emailItem.email} per questo alunno?\n\nVerrà rimossa dall'anagrafica e dovrai re-importarla via Segremat per ripristinarla.`)) return;
    setErr(null);
    try {
      await api.delete(`/alunni-emails/${cod}/${emailItem.id}`);
      // Aggiorna lo stato locale: rimuove l'item dalla lista
      setEmailsPerAlunno(prev => ({
        ...prev,
        [cod]: (prev[cod] || []).filter(x => x.id !== emailItem.id),
      }));
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore cancellazione email");
    }
  };

  // Toggle inclusione: se attualmente INCLUSO → escludi; se ESCLUSO (per filtro o manualmente) → forza inclusione.
  // In questo modo l'utente può sempre spuntare/despuntare qualsiasi addebito, anche quelli filtrati dal Step 1.
  const toggleEscluso = (cod, id) => {
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const mov = (movPerAlunno[cod] || []).find(m => m.id === id);
    const cauSet = new Set((movFiltri.causali || []).map(s => (s || "").toLowerCase()));
    const modSet = new Set((movFiltri.modalita || []).map(norm));
    const passaFiltroLocal = mov ? (
      (cauSet.size === 0 || cauSet.has(String(mov.causale || "").toLowerCase())) &&
      (modSet.size === 0 || modSet.has(norm(mov.modalita)))
    ) : true;
    const esc = exclusi[cod] || new Set();
    const fz = forced[cod] || new Set();
    const inclusoOra = fz.has(id) || (passaFiltroLocal && !esc.has(id));
    if (inclusoOra) {
      // Vuole ESCLUDERE: rimuove dal forzato e aggiunge agli esclusi manuali
      setForced(prev => {
        const s = new Set(prev[cod] || []); s.delete(id);
        return { ...prev, [cod]: s };
      });
      setExclusi(prev => {
        const s = new Set(prev[cod] || []); s.add(id);
        return { ...prev, [cod]: s };
      });
    } else {
      // Vuole INCLUDERE: rimuove dagli esclusi manuali; se il filtro lo escluderebbe, aggiunge ai forzati
      setExclusi(prev => {
        const s = new Set(prev[cod] || []); s.delete(id);
        return { ...prev, [cod]: s };
      });
      if (!passaFiltroLocal) {
        setForced(prev => {
          const s = new Set(prev[cod] || []); s.add(id);
          return { ...prev, [cod]: s };
        });
      }
    }
  };

  // Spunta TUTTI gli addebiti (rimuove tutte le esclusioni manuali e forza l'inclusione anche dei filtrati).
  const selezionaTuttiAddebitiAlunno = (cod) => {
    const all = movPerAlunno[cod] || [];
    setExclusi(prev => ({ ...prev, [cod]: new Set() }));
    setForced(prev => ({ ...prev, [cod]: new Set(all.map(m => m.id)) }));
  };

  // Desplecca TUTTI gli addebiti (esclude manualmente ogni movimento dell'alunno corrente).
  const desplecaTuttiAddebitiAlunno = (cod) => {
    const all = movPerAlunno[cod] || [];
    setForced(prev => ({ ...prev, [cod]: new Set() }));
    setExclusi(prev => ({ ...prev, [cod]: new Set(all.map(m => m.id)) }));
  };

  // Spunta TUTTI gli addebiti per TUTTI gli alunni selezionati.
  const selezionaTuttiAddebitiGlobale = () => {
    const out = {};
    for (const a of targets) {
      const all = movPerAlunno[a.Cod] || [];
      out[a.Cod] = new Set(all.map(m => m.id));
    }
    setExclusi({});
    setForced(out);
  };

  // Desplecca TUTTI gli addebiti per TUTTI gli alunni selezionati.
  const desplecaTuttiAddebitiGlobale = () => {
    const out = {};
    for (const a of targets) {
      const all = movPerAlunno[a.Cod] || [];
      out[a.Cod] = new Set(all.map(m => m.id));
    }
    setForced({});
    setExclusi(out);
  };

  const movimentiInclusiPerAlunno = useMemo(() => {
    const out = {};
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const cauSet = new Set((movFiltri.causali || []).map(s => (s || "").toLowerCase()));
    const modSet = new Set((movFiltri.modalita || []).map(norm));
    for (const a of targets) {
      const tutti = movPerAlunno[a.Cod] || [];
      const esc = exclusi[a.Cod] || new Set();
      const fz = forced[a.Cod] || new Set();
      out[a.Cod] = tutti.filter(m => {
        if (esc.has(m.id)) return false;
        // Se l'utente l'ha forzato → incluso indipendentemente dal filtro
        if (fz.has(m.id)) return true;
        if (cauSet.size > 0 && !cauSet.has(String(m.causale || "").toLowerCase())) return false;
        // 🐛 Feb 2026: i movimenti NON pagati hanno ModDes/modalita vuota perché
        // la modalità viene assegnata solo al momento del pagamento. Non escluderli
        // in base al filtro modalità (altrimenti nessun insoluto passerebbe).
        if (modSet.size > 0 && m.pagato && !modSet.has(norm(m.modalita))) return false;
        return true;
      });
    }
    return out;
  }, [movPerAlunno, exclusi, forced, targets, movFiltri.causali, movFiltri.modalita]);

  // Opzioni dinamiche per i filtri multi-select (estratte dai movimenti caricati)
  const opzioniCausali = useMemo(() => {
    const s = new Set();
    for (const arr of Object.values(movPerAlunno)) for (const m of arr) if (m.causale) s.add(m.causale);
    return Array.from(s).sort((a, b) => a.localeCompare(b, "it"));
  }, [movPerAlunno]);
  const opzioniModalita = useMemo(() => {
    // Unione: tutte le modalità della tabella + quelle presenti nei movimenti caricati
    // (a salvaguardia di codici legacy non più in tabella).
    const s = new Set();
    for (const t of (modalitaTabella || [])) {
      const nome = String(t.descrizione || t.codice || "").trim();
      if (nome) s.add(nome);
    }
    for (const arr of Object.values(movPerAlunno)) {
      for (const m of arr) if (m.modalita) s.add(String(m.modalita).trim());
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, "it"));
  }, [movPerAlunno, modalitaTabella]);

  const totMovSelezionati = useMemo(
    () => Object.values(movimentiInclusiPerAlunno).reduce((acc, arr) => acc + arr.length, 0),
    [movimentiInclusiPerAlunno]
  );
  const totImportoSelezionato = useMemo(() => {
    let s = 0;
    for (const arr of Object.values(movimentiInclusiPerAlunno)) for (const m of arr) s += Number(m.importo || 0);
    return s;
  }, [movimentiInclusiPerAlunno]);

  const fmtEur = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Filtro alunni nello Step 2 — basato sul testo digitato (cognome, nome o codice)
  const targetsFiltrati = useMemo(() => {
    const q = filtroAlunnoStep2.trim().toLowerCase();
    return targets
      .map((a, i) => ({ a, i }))
      .filter(({ a }) => {
        if (!q) return true;
        return (
          (a.Cog || "").toLowerCase().includes(q) ||
          (a.Nom || "").toLowerCase().includes(q) ||
          String(a.Cod || "").toLowerCase().includes(q) ||
          (a.CodFis || "").toLowerCase().includes(q)
        );
      });
  }, [targets, filtroAlunnoStep2]);

  // Quando il filtro cambia, se l'alunno corrente non è più nella lista filtrata,
  // sposta l'anteprima al primo alunno filtrato.
  useEffect(() => {
    if (targetsFiltrati.length === 0) return;
    const stillVisible = targetsFiltrati.some(({ i }) => i === previewAlunnoIdx);
    if (!stillVisible) setPreviewAlunnoIdx(targetsFiltrati[0].i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroAlunnoStep2, targetsFiltrati.length]);

  // Anteprima PDF (un alunno)
  const anteprimaPdf = async () => {
    const a = targets[previewAlunnoIdx] || targets[0];
    if (!a) return;
    setLoadingPdfPreview(true); setErr(null);
    if (pdfPreviewUrl) { URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); }
    try {
      const movs = movimentiInclusiPerAlunno[a.Cod] || [];
      let r;
      if (tipoPredefinito) {
        r = await api.post("/comunicator/anteprima-pdf-predefinito", {
          tipo: tipoPredefinito,
          firma_id: selectedFirma || null,
          cod: a.Cod, anno,
          anno_fiscale: annoFiscale,
          movimenti: movs,
          protocollo: protocolloOverride,
          data_invio: dataInvioOverride ? new Date(dataInvioOverride).toLocaleString("it-IT") : "",
        }, { responseType: "blob" });
      } else if (selectedAllegatoDocx) {
        r = await api.post(`/comunicator/allegati-docx/${selectedAllegatoDocx}/anteprima-pdf`, {
          cod: a.Cod, anno,
          movimenti: movs,
          numero_protocollo: protocolloOverride,
          data_invio: dataInvioOverride ? new Date(dataInvioOverride).toLocaleString("it-IT") : "",
          data_aggiornamento_dati: formatDateIt(dataAggiornamento),
          anno_fiscale: annoFiscale,
          anno_pagamento_da: movFiltri.anno_pag_da ? Number(movFiltri.anno_pag_da) : null,
          anno_pagamento_a: movFiltri.anno_pag_a ? Number(movFiltri.anno_pag_a) : null,
          genitore_selezionato: genitoreSelezionato || null,
        }, { responseType: "blob" });
      } else if (selectedModulo) {
        r = await api.post("/comunicator/anteprima-pdf", {
          modulo_id: selectedModulo,
          firma_id: selectedFirma || null,
          cod: a.Cod, anno,
          movimenti: movs,
          protocollo: protocolloOverride,
          data_invio: dataInvioOverride ? new Date(dataInvioOverride).toLocaleString("it-IT") : "",
          data_aggiornamento_dati: formatDateIt(dataAggiornamento),
          tot_non_pagato: a.tot_non_pagato,
          tot_non_pagato_scaduto: a.tot_non_pagato_scaduto,
          anno_fiscale: annoFiscale,
          anno_pagamento_da: movFiltri.anno_pag_da ? Number(movFiltri.anno_pag_da) : null,
          anno_pagamento_a: movFiltri.anno_pag_a ? Number(movFiltri.anno_pag_a) : null,
          genitore_selezionato: genitoreSelezionato || null,
        }, { responseType: "blob" });
      } else {
        setErr("Seleziona un PDF predefinito o un Allegato DOCX per generare l'anteprima");
        return;
      }
      const url = URL.createObjectURL(r.data);
      setPdfPreviewUrl(url);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore anteprima PDF");
    } finally { setLoadingPdfPreview(false); }
  };

  // Calcolo range protocollo previsto
  const protocolloRange = useMemo(() => {
    const m = /^PROT-(\d{4})-(\d{1,7})$/i.exec((protocolloOverride || "").trim().toUpperCase());
    if (!m) return null;
    const yr = m[1];
    const start = parseInt(m[2], 10);
    const end = start + targets.length - 1;
    return {
      start: `PROT-${yr}-${String(start).padStart(6, "0")}`,
      end: `PROT-${yr}-${String(end).padStart(6, "0")}`,
      count: targets.length,
    };
  }, [protocolloOverride, targets.length]);

  const formatDateIt = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  };

  const inviaTest = async () => {
    const em = (testEmail || "").trim().toLowerCase();
    if (!em || !em.includes("@")) { setErr("Email di test non valida"); return; }
    if (!selectedTpl) { setErr("Seleziona un modello email"); return; }
    const a = targets[previewAlunnoIdx] || targets[0];
    if (!a) { setErr("Nessun alunno disponibile per il test"); return; }
    setTestSending(true); setTestResult(null); setErr(null);
    try {
      const r = await api.post("/comunicator/invia-test", {
        template_id: selectedTpl,
        modulo_id: selectedModulo || null,
        allegato_docx_id: selectedAllegatoDocx || null,
        tipo_predefinito: tipoPredefinito || null,
        anno_fiscale: annoFiscale,
        firma_id: selectedFirma || null,
        protocollo: protocolloOverride,
        data_invio: dataInvioOverride,
        data_aggiornamento_dati: formatDateIt(dataAggiornamento),
        email_test: em,
        alunno: {
          cod: a.Cod, anno,
          classe: a.classe, settore: a.settore,
          tot_addebiti: a.tot_addebiti, tot_non_pagato: a.tot_non_pagato,
          tot_non_pagato_scaduto: a.tot_non_pagato_scaduto, tot_dote: a.tot_dote,
          movimenti: movimentiInclusiPerAlunno[a.Cod] || [],
        },
      });
      setTestResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore invio test");
    } finally { setTestSending(false); }
  };

  const openOutboxPdf = async (oid) => {
    try {
      const r = await api.get(`/comunicator/outbox/${oid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank");
      if (!w) { const link = document.createElement("a"); link.href = url; link.download = "documento.pdf"; document.body.appendChild(link); link.click(); link.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert("Impossibile aprire il PDF."); }
  };

  const invia = async () => {
    if (!selectedTpl) { setErr("Seleziona un modello email"); return; }
    if (!protocolloRange) { setErr("Numero protocollo non valido. Formato atteso: PROT-AAAA-NNNNNN"); return; }

    // Check preventivo crediti Resend (sia MENSILE che GIORNALIERO) — saltato in modalità stampa
    const totSelMails = Object.values(emailsPerAlunno).reduce((s, l) => s + (l || []).filter(x => x.selected).length, 0);
    if (!stampaMode && credit && credit.configured) {
      const remDaily = (credit.remaining_today != null) ? credit.remaining_today : credit.remaining;
      const remMonth = credit.remaining;
      const remEff = (credit.remaining_effective != null)
        ? credit.remaining_effective
        : Math.min(remDaily, remMonth);
      if (totSelMails > remEff) {
        const isLimitedByDaily = remDaily < remMonth;
        const proceed = window.confirm(
          `🛑 ATTENZIONE: Crediti Resend insufficienti!\n\n` +
          `Mail da inviare: ${totSelMails}\n` +
          `Crediti residui: ${remEff} ` +
          `${isLimitedByDaily ? `(limite GIORNALIERO ${credit.daily_limit}/giorno)` : `(limite MENSILE)`}\n` +
          `Mancanti: ${totSelMails - remEff}\n\n` +
          `L'invio andrà sicuramente in ERRORE a metà.\n` +
          `Ti consiglio di ridurre i destinatari a max ${remEff} ` +
          `${isLimitedByDaily ? `oppure attendere domani / aggiornare il piano Resend.` : `oppure aggiornare il piano Resend.`}\n\n` +
          `Vuoi continuare comunque? (Sconsigliato)`
        );
        if (!proceed) return;
      }
    }

    // Conferma
    const msg = stampaMode
      ? `Confermi la STAMPA?\n\n` +
        `🖨 Alunni: ${targets.length}\n` +
        `📎 Documento: ${tipoPredefinito ? tipoPredefinito.replace(/_/g, " ") : (selectedAllegatoDocx ? "allegato DOCX" : (selectedModulo ? "modulo custom" : "—"))}\n` +
        `✍ Firma: ${selectedFirma ? "Sì" : "No"}\n\n` +
        `Nessuna email verrà inviata: verrà generato un PDF per ogni alunno e registrato nel log (esito "Stampato").\n\nProcedere?`
      : `Confermi l'invio?\n\n` +
      `📧 Destinatari: ${targets.length}\n` +
      `🔢 Protocolli: ${protocolloRange.start} … ${protocolloRange.end}\n` +
      `📅 Data invio: ${new Date(dataInvioOverride).toLocaleString("it-IT")}\n` +
      (dataAggiornamento ? `🗓 Data aggiornamento dati: ${formatDateIt(dataAggiornamento)}\n` : "") +
      (credit && credit.configured ? `💳 Crediti Resend: ${credit.remaining_effective != null ? credit.remaining_effective : credit.remaining} disponibili → ${(credit.remaining_effective != null ? credit.remaining_effective : credit.remaining) - totSelMails} dopo invio\n` : "") +
      `📎 PDF allegato: ${tipoPredefinito ? tipoPredefinito.replace(/_/g, " ") : (selectedAllegatoDocx ? "allegato DOCX" : (selectedModulo ? "modulo custom" : "No"))}\n` +
      `✍ Firma: ${selectedFirma ? "Sì" : "No"}\n\n` +
      `Procedere?`;
    if (!window.confirm(msg)) return;
    setSending(true); setErr(null);
    try {
      const r = await api.post("/comunicator/invia-bulk-v2", {
        template_id: selectedTpl,
        solo_stampa: stampaMode,
        modulo_id: baseMode ? null : (selectedModulo || null),
        allegato_docx_id: baseMode ? null : (selectedAllegatoDocx || null),
        tipo_predefinito: baseMode ? null : (tipoPredefinito || null),
        anno_fiscale: annoFiscale,
        anno_pagamento_da: movFiltri.anno_pag_da ? Number(movFiltri.anno_pag_da) : null,
        anno_pagamento_a: movFiltri.anno_pag_a ? Number(movFiltri.anno_pag_a) : null,
        genitore_selezionato: genitoreSelezionato || null,
        firma_id: baseMode ? null : (selectedFirma || null),
        protocollo_iniziale: protocolloOverride,
        data_invio: dataInvioOverride,
        data_aggiornamento_dati: formatDateIt(dataAggiornamento),
        include_google_email: includeGoogleEmail,
        alunni: targets.map(a => {
          // Email selezionate dallo Step 5 (se caricate); altrimenti il backend userà il default
          const emailsState = emailsPerAlunno[a.Cod];
          const emailsSel = emailsState
            ? emailsState.filter(x => x.selected).map(x => String(x.email || "").trim().toLowerCase()).filter(Boolean)
            : null;
          // 🆕 Feb 2026: placeholder custom per-alunno (definito dal popup 📜 Storico invii)
          const customPh = readCustomPlaceholder(a.Cod);
          const customPhObj = customPh ? { [customPh.name]: customPh.value } : null;
          return {
            cod: a.Cod, anno,
            classe: a.classe, settore: a.settore,
            tot_addebiti: a.tot_addebiti, tot_non_pagato: a.tot_non_pagato,
            tot_non_pagato_scaduto: a.tot_non_pagato_scaduto, tot_dote: a.tot_dote,
            movimenti: baseMode ? [] : (movimentiInclusiPerAlunno[a.Cod] || []),
            ...(emailsSel !== null ? { emails: emailsSel } : {}),
            ...(customPhObj ? { custom_placeholders: customPhObj } : {}),
          };
        }),
      });
      setResult(r.data);
      if (stampaMode && r.data && Array.isArray(r.data.results)) {
        const printedItems = r.data.results.filter(x => x.outbox_id);
        if (printedItems.length === 1) openOutboxPdf(printedItems[0].outbox_id);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore invio");
    } finally { setSending(false); }
  };

  const senzaEmail = targets.filter(a => !a.Mai).length;

  // Stepper header
  const StepDot = ({ n, label, active, done }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%",
        background: done ? "#1b5e20" : (active ? "#00838f" : "#ccc"),
        color: "white", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 800,
      }}>{done ? "✓" : n}</div>
      <div style={{ fontSize: 11, fontWeight: active ? 700 : 500, color: active ? "#00838f" : "#666" }}>{label}</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div data-testid="com-send-modal"
        style={{ background: "white", width: "100%", maxWidth: 1100, borderRadius: 6, overflow: "hidden", maxHeight: "94vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#00838f", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {bulkProgress && (
              <span data-testid="com-bulk-progress"
                style={{ display: "inline-block", padding: "3px 10px", background: "rgba(255,255,255,0.18)", borderRadius: 3, fontSize: 12, fontWeight: 800, marginRight: 12, letterSpacing: 0.3 }}
                title="Invio sequenziale: il wizard si riapre automaticamente per il prossimo alunno dopo ogni invio">
                👤 Alunno {bulkProgress.current} di {bulkProgress.total}
              </span>
            )}
            <strong>📧 Invio email — {targets.length === 1 ? `${targets[0].Cog || ""} ${targets[0].Nom || ""}`.trim() : `${targets.length} destinatari`}</strong>
            {protoPeek && <span style={{ marginLeft: 14, fontSize: 11, opacity: 0.85 }}>Prossimo protocollo: <code style={{ background: "rgba(0,0,0,0.25)", padding: "1px 6px", borderRadius: 2 }}>{protoPeek.prossimo}</code></span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* 🆕 Feb 2026: pulsante Salta spostato nella toolbar footer accanto ad Avanti */}
            {false && onSkip && step === 2 && false && (
              <button onClick={onSkip} data-testid="com-send-skip-header"
                title="Salta questo alunno e passa al successivo della coda (nessun invio, nessun log)"
                style={{
                  background: "rgba(255,255,255,0.18)", color: "white", border: "1px solid rgba(255,255,255,0.5)",
                  padding: "4px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3,
                }}>
                ⏭ Salta
              </button>
            )}
            <button onClick={onClose} data-testid="com-send-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
          </div>
        </div>

        {/* Stepper — completo (evoluto) oppure ridotto (base: Modello + Email) */}
        {!result && (
          <div style={{ padding: "10px 18px", display: "flex", gap: 20, alignItems: "center", background: "#f5fafa", borderBottom: "1px solid #ddd", flexWrap: "wrap" }}>
            {baseMode ? (
              <>
                <StepDot n={1} label="Modello email" active={step === 1} done={ORDER.indexOf(step) > 0} />
                <span style={{ color: "#aaa" }}>›</span>
                <StepDot n={2} label="Email & Invio" active={step === 5} done={false} />
                <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: "#2e7d32", background: "#e8f5e9", padding: "3px 10px", borderRadius: 999 }}>Invio base · senza allegati</span>
              </>
            ) : (
              <>
                <StepDot n={1} label="Addebiti" active={step === 2} done={STEP_ORDER.indexOf(step) > 0} />
                <span style={{ color: "#aaa" }}>›</span>
                <StepDot n={2} label="Firma & Protocollo" active={step === 4} done={STEP_ORDER.indexOf(step) > 1} />
                <span style={{ color: "#aaa" }}>›</span>
                <StepDot n={3} label="Modello email" active={step === 1} done={STEP_ORDER.indexOf(step) > 2} />
                <span style={{ color: "#aaa" }}>›</span>
                <StepDot n={4} label="Modulo PDF" active={step === 3} done={STEP_ORDER.indexOf(step) > 3} />
                <span style={{ color: "#aaa" }}>›</span>
                <StepDot n={5} label="Email & Invio" active={step === 5} done={false} />
              </>
            )}
          </div>
        )}

        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          {senzaEmail > 0 && step === 1 && (
            <div style={{ marginBottom: 12, padding: 10, background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 3, fontSize: 11, color: "#bf360c" }}>
              ⚠ {senzaEmail} destinatar{senzaEmail === 1 ? "io" : "i"} senza email registrata — verrà{senzaEmail === 1 ? "" : "nno"} saltat{senzaEmail === 1 ? "o" : "i"}.
            </div>
          )}

          {/* STEP 1: MODELLO */}
          {!result && step === 1 && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>MODELLO EMAIL *</label>
              {/* 🆕 Feb 2026: lista spuntabile (radio) invece di dropdown a discesa.
                   Quando si seleziona un template Mxxx viene proposto in automatico l'allegato Dxxx corrispondente
                   (m001↔d001, m002↔d002, ...) — la sincronizzazione è gestita dallo useEffect qui sotto. */}
              <div data-testid="com-send-tpl-list"
                style={{ maxHeight: 220, overflowY: "auto", border: "2px solid #00838f", borderRadius: 3, background: "white", marginBottom: 12 }}>
                {templates.length === 0 && (
                  <div style={{ padding: 10, fontSize: 11, color: "#bf360c", background: "#fff3e0" }}>
                    Nessun modello disponibile. Vai nella tab &quot;✉ Modelli&quot; e creane uno.
                  </div>
                )}
                {templates.map((t, i) => {
                  const checked = selectedTpl === t.id;
                  return (
                    <label key={t.id}
                      data-testid={`com-send-tpl-item-${t.id}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "6px 10px",
                        background: checked ? "#e0f7fa" : (i % 2 ? "#fafafa" : "white"),
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                        fontSize: 12,
                      }}>
                      <input type="radio" name="com-send-tpl-radio"
                        checked={checked}
                        onChange={() => setSelectedTpl(t.id)} />
                      <span style={{ fontWeight: 700, color: "#00838f", minWidth: 46, fontFamily: "Consolas, monospace" }}>
                        {t.id.toUpperCase()}
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        {(t.label && t.label !== t.id)
                          ? t.label
                          : <span style={{ color: "#bbb", fontStyle: "italic", fontWeight: 400 }}>(senza nome)</span>}
                      </span>
                      {t.subject && (
                        <span style={{ color: "#888", flex: 1, textAlign: "right", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.subject}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {selectedTpl && (
                <input type="hidden" data-testid="com-send-tpl" value={selectedTpl} readOnly />
              )}
              {targets.length > 1 && (
                <div style={{ marginBottom: 8, fontSize: 11, color: "#666" }}>
                  Anteprima per: <select value={previewAlunnoIdx} onChange={e => setPreviewAlunnoIdx(Number(e.target.value))}
                    style={{ padding: 4, border: "1px solid #ccc", fontSize: 12 }}>
                    {targets.map((a, i) => <option key={a.Cod} value={i}>{a.Cog} {a.Nom} ({a.Cod})</option>)}
                  </select>
                </div>
              )}
              {preview && (
                <div style={{ marginTop: 8, padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 3 }}>
                  <div style={{ fontSize: 10, color: "#888", marginBottom: 4 }}>ANTEPRIMA TESTO EMAIL</div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Oggetto: {preview.subject}</div>
                  {(() => {
                    const b = preview.body || "";
                    const isHtml = typeof b === "string" && b.trim().startsWith("<") && b.includes(">");
                    return isHtml
                      ? <div className="com-html-preview" style={{ background: "white", padding: 10, border: "1px solid #ddd", maxHeight: 320, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b) }} />
                      : <pre style={{ margin: 0, fontSize: 11, fontFamily: "Consolas, monospace", whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 320, overflow: "auto" }}>{b}</pre>;
                  })()}
                </div>
              )}
            </>
          )}

          {/* STEP 2: MOVIMENTI / ADDEBITI */}
          {!result && step === 2 && (
            <div data-testid="com-send-step2">
              {/* 🆕 Feb 2026: banner ultimo invio a questo alunno */}
              {targets.length === 1 && (
                <div data-testid="com-last-send-banner" style={{
                  marginBottom: 12, padding: 10,
                  background: lastSend ? "#e8f5e9" : "#fff3e0",
                  border: `1px solid ${lastSend ? "#66bb6a" : "#ffb74d"}`,
                  borderLeft: `4px solid ${lastSend ? "#2e7d32" : "#e65100"}`,
                  borderRadius: 3, fontSize: 11, color: "#333",
                }}>
                  {lastSendLoading ? (
                    <span style={{ color: "#888" }}>⏳ Caricamento storico invii…</span>
                  ) : lastSend ? (
                    <>
                      <b style={{ color: "#1b5e20" }}>📧 Ultimo invio:</b>{' '}
                      <b>{lastSend.created_at ? new Date(lastSend.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</b>
                      {' '}·{' '}
                      <span title="Oggetto email inviata">
                        <i>"{lastSend.subject || lastSend.oggetto || "(senza oggetto)"}"</i>
                      </span>
                      {(lastSend.to_email || lastSend.to || (lastSend.recipients && lastSend.recipients[0])) && (
                        <>{' '}→{' '}
                          <span style={{ fontFamily: "Consolas,monospace", color: "#00838f" }}>
                            {lastSend.to_email || lastSend.to || (lastSend.recipients && lastSend.recipients[0])}
                          </span>
                        </>
                      )}
                      {(lastSend.status || lastSend.stato) && (
                        <span style={{ marginLeft: 8, padding: "1px 6px", background: (lastSend.status || lastSend.stato) === "SENT" || (lastSend.status || lastSend.stato) === "sent" ? "#2e7d32" : "#e65100", color: "white", fontSize: 10, borderRadius: 2, fontWeight: 700 }}>
                          {(lastSend.status || lastSend.stato).toUpperCase()}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: "#bf360c" }}>ℹ Nessun invio precedente registrato per questo alunno.</span>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 12, padding: 10, background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 11, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <b>Filtri addebiti — applicati automaticamente a TUTTI gli alunni selezionati.</b> I filtri vengono ricaricati quando li modifichi. Nell'anteprima sotto puoi escludere singoli addebiti per alunno.
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: movFiltri.tutti_anni ? "#fff3e0" : "white", border: `1px solid ${movFiltri.tutti_anni ? "#f57c00" : "#bbb"}`, borderRadius: 3, cursor: "pointer", fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={movFiltri.tutti_anni}
                    onChange={e => setMovFiltri({ ...movFiltri, tutti_anni: e.target.checked })}
                    data-testid="com-mov-tutti-anni" />
                  📅 Tutti gli anni {movFiltri.tutti_anni ? "(consigliato)" : `(solo ${anno})`}
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
                <FiltroAnno label="Competenza" da={movFiltri.anno_comp_da} a={movFiltri.anno_comp_a}
                  onChange={(da, a) => setMovFiltri({ ...movFiltri, anno_comp_da: da, anno_comp_a: a })} />
                <FiltroAnno label="Scadenza" da={movFiltri.anno_sca_da} a={movFiltri.anno_sca_a}
                  onChange={(da, a) => setMovFiltri({ ...movFiltri, anno_sca_da: da, anno_sca_a: a })} />
                <FiltroAnno label="Pagamento" da={movFiltri.anno_pag_da} a={movFiltri.anno_pag_a}
                  onChange={(da, a) => setMovFiltri({ ...movFiltri, anno_pag_da: da, anno_pag_a: a })} />
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <label style={{ fontSize: 11, color: "#666", fontWeight: 700 }}>Stato pagam.:</label>
                <select value={movFiltri.stato_pagamento} onChange={e => setMovFiltri({ ...movFiltri, stato_pagamento: e.target.value })}
                  data-testid="com-mov-stato"
                  style={{ padding: 5, border: "1px solid #ccc", fontSize: 11 }}>
                  <option value="">Tutti</option>
                  <option value="pagati">Solo pagati</option>
                  <option value="non_pagati">Solo non pagati</option>
                </select>
                <MultiSelectChips
                  label="Causale"
                  options={opzioniCausali}
                  selected={movFiltri.causali}
                  onChange={(vals) => setMovFiltri({ ...movFiltri, causali: vals })}
                  testid="com-mov-causale"
                  width={170}
                />
                <MultiSelectChips
                  label="Modalità"
                  options={opzioniModalita}
                  selected={movFiltri.modalita}
                  onChange={(vals) => setMovFiltri({ ...movFiltri, modalita: vals })}
                  testid="com-mov-modalita"
                  width={150}
                />
                <label style={{ fontSize: 11, color: "#666", fontWeight: 700 }}>Ordina per:</label>
                <select value={movFiltri.sort_by} onChange={e => setMovFiltri({ ...movFiltri, sort_by: e.target.value })} style={{ padding: 5, border: "1px solid #ccc", fontSize: 11 }}>
                  <option value="DtaSca">📅 Scadenza</option>
                  <option value="DtaPag">💰 Pagamento</option>
                  <option value="AnnCom_DtaSca">🎓 A.scol. → Scadenza</option>
                </select>
                <select value={movFiltri.sort_dir} onChange={e => setMovFiltri({ ...movFiltri, sort_dir: e.target.value })} style={{ padding: 5, border: "1px solid #ccc", fontSize: 11 }}>
                  <option value="asc">↑ crescente</option>
                  <option value="desc">↓ decrescente</option>
                </select>
                <button onClick={caricaMovimenti} disabled={loadingMov} data-testid="com-mov-applica"
                  style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  {loadingMov ? "Caricamento..." : "↻ Ricarica"}
                </button>
                <button onClick={() => {
                    setMovFiltri(FILTRI_DEFAULT);
                    setExclusi({});
                    try { window.localStorage.removeItem(MOV_FILTRI_LS_KEY); } catch { /* noop */ }
                  }} data-testid="com-mov-reset"
                  title="Riporta tutti i filtri ai valori di default, annulla le esclusioni e cancella i filtri salvati"
                  style={{ padding: "6px 12px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  ✕ Resetta filtri
                </button>
                <div style={{ marginLeft: "auto", fontSize: 12 }}>
                  <b>{totMovSelezionati}</b> addebiti inclusi · <b>€ {fmtEur(totImportoSelezionato)}</b>
                </div>
              </div>

              {/* Anteprima per singolo alunno + checkbox esclusione */}
              {targets.length > 1 && (
                <div style={{ marginBottom: 8, fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <input type="text" value={filtroAlunnoStep2} onChange={e => setFiltroAlunnoStep2(e.target.value)}
                    data-testid="com-mov-search-alunno"
                    placeholder="🔍 Filtra alunno (cognome, nome o codice)..."
                    style={{ padding: "4px 8px", border: "1px solid #1976d2", borderRadius: 3, fontSize: 11, minWidth: 240, fontWeight: 600 }} />
                  <span>Anteprima per:</span>
                  <select value={previewAlunnoIdx} onChange={e => setPreviewAlunnoIdx(Number(e.target.value))}
                    style={{ padding: 4, border: "1px solid #ccc", fontSize: 12, maxWidth: 400 }}>
                    {targetsFiltrati.map(({ a, i }) => <option key={a.Cod} value={i}>{a.Cog} {a.Nom} ({a.Cod}) — {(movPerAlunno[a.Cod] || []).length} mov.</option>)}
                  </select>
                  {filtroAlunnoStep2 && (
                    <span style={{ fontSize: 10, color: "#1565c0", fontWeight: 700 }}>
                      {targetsFiltrati.length} di {targets.length} alunni
                    </span>
                  )}
                </div>
              )}
              {(() => {
                const aSel = targets[previewAlunnoIdx] || targets[0];
                if (!aSel) return null;
                const movs = movPerAlunno[aSel.Cod] || [];
                const esc = exclusi[aSel.Cod] || new Set();
                // Filtri causale/modalità: NON nascondono righe, deselezionano solo l'INC
                const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                const cauSet = new Set((movFiltri.causali || []).map(s => (s || "").toLowerCase()));
                const modSet = new Set((movFiltri.modalita || []).map(norm));
                const passaFiltro = (m) => {
                  if (cauSet.size > 0 && !cauSet.has(String(m.causale || "").toLowerCase())) return false;
                  if (modSet.size > 0 && !modSet.has(norm(m.modalita))) return false;
                  return true;
                };
                return (
                  <>
                  {/* Toolbar Spunta tutti / Desplecca tutti */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#f5f5f5", border: "1px solid #ddd", borderBottom: "none", fontSize: 11, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, color: "#555" }}>Per <b>{aSel.Cog} {aSel.Nom}</b>:</span>
                    <button onClick={() => selezionaTuttiAddebitiAlunno(aSel.Cod)}
                      data-testid="com-mov-check-all-alunno"
                      style={{ padding: "3px 10px", background: "#2e7d32", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      ✓ Spunta tutti
                    </button>
                    <button onClick={() => desplecaTuttiAddebitiAlunno(aSel.Cod)}
                      data-testid="com-mov-uncheck-all-alunno"
                      style={{ padding: "3px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      ✕ Desplecca tutti
                    </button>
                    {targets.length > 1 && (
                      <>
                        <span style={{ color: "#aaa" }}>|</span>
                        <span style={{ fontWeight: 700, color: "#555" }}>Per <b>tutti i {targets.length} alunni</b>:</span>
                        <button onClick={selezionaTuttiAddebitiGlobale}
                          data-testid="com-mov-check-all-global"
                          style={{ padding: "3px 10px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          ✓✓ Spunta tutti (globale)
                        </button>
                        <button onClick={desplecaTuttiAddebitiGlobale}
                          data-testid="com-mov-uncheck-all-global"
                          style={{ padding: "3px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          ✕✕ Desplecca tutti (globale)
                        </button>
                      </>
                    )}
                    <span style={{ marginLeft: "auto", color: "#666", fontSize: 10 }}>
                      {movs.filter(m => passaFiltro(m) && !esc.has(m.id)).length} / {movs.length} addebiti inclusi per questo alunno
                    </span>
                  </div>
                  <div style={{ border: "1px solid #ddd", background: "white", maxHeight: 360, overflow: "auto" }}>
                    <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 11 }}>
                      <thead style={{ background: "#1f3864", color: "white", position: "sticky", top: 0 }}>
                        <tr>
                          <th style={{ width: 30 }}>INC</th>
                          <th>Comp.</th>
                          <th>Documento</th>
                          <th>Scadenza</th>
                          <th>Pagamento</th>
                          <th>Causale</th>
                          <th>Descrizione</th>
                          <th>Mod.</th>
                          <th className="num-col">Importo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingMov && <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
                        {!loadingMov && movs.length === 0 && <tr><td colSpan={9} style={{ padding: 16, textAlign: "center", color: "#888" }}>Nessun addebito trovato per questo alunno con i filtri attuali.</td></tr>}
                        {!loadingMov && movs.map(m => {
                          const escluso_filtro = !passaFiltro(m);
                          const escluso_manuale = esc.has(m.id);
                          const fz = forced[aSel.Cod] || new Set();
                          const forzato = fz.has(m.id);
                          // Incluso se: utente l'ha forzato OR (passa filtro AND non escluso manualmente)
                          const incluso = forzato || (!escluso_filtro && !escluso_manuale);
                          return (
                            <tr key={m.id} style={{ background: incluso ? undefined : (escluso_filtro ? "#f5f5f5" : "#ffebee"), color: incluso ? undefined : "#999", textDecoration: incluso ? undefined : "line-through" }}>
                              <td className="center-col" onClick={e => e.stopPropagation()}>
                                <input type="checkbox" checked={incluso}
                                  onChange={() => toggleEscluso(aSel.Cod, m.id)}
                                  data-testid={`com-mov-${aSel.Cod}-${m.id}`}
                                  title={escluso_filtro && !forzato ? "Escluso dal filtro Causale/Modalità — click per forzare l'inclusione" : (incluso ? "Click per escludere" : "Click per includere")} />
                              </td>
                              <td>{m.competenza || "-"}</td>
                              <td style={{ fontSize: 10 }}>{m.data_doc || "-"}</td>
                              <td style={{ fontSize: 10, fontWeight: 700, color: !m.pagato ? "#c62828" : undefined }}>{m.data_scadenza || "-"}</td>
                              <td style={{ fontSize: 10, color: m.pagato ? "#2e7d32" : "#999" }}>{m.data_pagamento || "(non pag.)"}</td>
                              <td style={{ fontWeight: 600 }}>{m.causale}</td>
                              <td style={{ fontSize: 11 }} title={m.descrizione}>{(m.descrizione || "").slice(0, 40)}</td>
                              <td style={{ fontSize: 10 }}>{m.modalita}</td>
                              <td className="num-col" style={{ fontWeight: 700 }}>{fmtEur(m.importo)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* STEP 3: MODULO PDF */}
          {!result && step === 3 && (
            <div data-testid="com-send-step3">
              <div style={{ marginBottom: 12, padding: 10, background: "#e0f2f1", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 12, color: "#006064" }}>
                <b>Scegli il documento PDF da allegare.</b> Carica i tuoi modelli Word nel tab <b>📎 Allegati DOCX</b> e selezionali qui sotto.
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 6 }}>TIPO DOCUMENTO PDF</label>
              <div style={{ marginBottom: 14 }}>
                <div onClick={() => { setTipoPredefinito(""); if (pdfPreviewUrl) { URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); } }}
                  data-testid="com-send-pdf-NONE"
                  style={{
                    padding: 12, background: "white",
                    border: "2px solid #7b1fa2",
                    borderRadius: 4,
                    boxShadow: "0 2px 6px #7b1fa233",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 20 }}>📎</span>
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#7b1fa2" }}>Allegato DOCX personalizzato</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#666", lineHeight: 1.4 }}>
                    Usa un file <b>.docx</b> Word con placeholder (caricato nel tab <b>📎 Allegati DOCX</b>). Sostituisce automaticamente i dati e duplica le righe della tabella per ogni movimento. Layout completamente sotto il tuo controllo.
                  </div>
                </div>
              </div>

              {tipoPredefinito === "" && (
                <div style={{ marginBottom: 14, padding: 12, background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#6a1b9a", display: "block", marginBottom: 6 }}>
                    📎 ALLEGATO DOCX DA USARE (dal tab "Allegati DOCX")
                  </label>
                  {(allegatiDocx || []).length === 0 ? (
                    <div style={{ fontSize: 11, color: "#888", fontStyle: "italic", padding: "8px 0" }}>
                      Nessun allegato DOCX caricato. Vai nel tab <b>📎 Allegati DOCX</b> per caricare un file Word con placeholder.
                    </div>
                  ) : (
                    <>
                      {/* 🆕 Feb 2026: lista spuntabile (radio) invece di dropdown a discesa.
                           Include un item "Nessuno" per inviare solo l'email senza allegato. */}
                      <div data-testid="com-send-docx-list"
                        style={{ maxHeight: 220, overflowY: "auto", background: "white", border: "1px solid #ce93d8", borderRadius: 3 }}>
                        <label
                          data-testid="com-send-docx-item-none"
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "6px 10px",
                            background: selectedAllegatoDocx === "" ? "#f3e5f5" : "white",
                            borderBottom: "1px solid #eee",
                            cursor: "pointer", fontSize: 12,
                          }}>
                          <input type="radio" name="com-send-docx-radio"
                            checked={selectedAllegatoDocx === ""}
                            onChange={() => setSelectedAllegatoDocx("")} />
                          <span style={{ color: "#888", fontStyle: "italic" }}>— Nessuno (solo email senza allegato) —</span>
                        </label>
                        {allegatiDocx.map((a, i) => {
                          const checked = selectedAllegatoDocx === a.id;
                          return (
                            <label key={a.id}
                              data-testid={`com-send-docx-item-${a.id}`}
                              style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "6px 10px",
                                background: checked ? "#f3e5f5" : (i % 2 ? "#fafafa" : "white"),
                                borderBottom: "1px solid #eee",
                                cursor: "pointer", fontSize: 12,
                              }}>
                              <input type="radio" name="com-send-docx-radio"
                                checked={checked}
                                onChange={() => setSelectedAllegatoDocx(a.id)} />
                              <span style={{ fontWeight: 700, color: "#6a1b9a", minWidth: 46, fontFamily: "Consolas, monospace" }}>
                                {String(a.nome || a.id).toUpperCase()}
                              </span>
                              <span style={{ fontWeight: 600, color: "#666" }}>{a.filename}</span>
                              <span style={{ color: "#888", flex: 1, textAlign: "right", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {(a.size / 1024).toFixed(1)} KB
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {/* Hidden select mantenuto per compatibilità con eventuali test già scritti */}
                      <select value={selectedAllegatoDocx}
                        onChange={(e) => setSelectedAllegatoDocx(e.target.value)}
                        data-testid="com-send-docx-select"
                        style={{ display: "none" }}>
                        <option value="">Nessuno</option>
                        {allegatiDocx.map(a => (<option key={a.id} value={a.id}>{a.nome}</option>))}
                      </select>
                      <div style={{ fontSize: 10, color: "#6a1b9a", marginTop: 4 }}>
                        Il sistema sostituirà i placeholder per ogni alunno, duplicherà le righe della tabella per ogni movimento, e allegherà il PDF risultante.
                      </div>
                    </>
                  )}
                </div>
              )}

              {tipoPredefinito === "DICHIARAZIONE_DDR" && (
                <div style={{ marginBottom: 12, padding: 10, background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 3 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#bf360c", display: "block", marginBottom: 4 }}>ANNO FISCALE (anno solare del versamento)</label>
                  <input type="number" value={annoFiscale} onChange={e => setAnnoFiscale(Number(e.target.value) || new Date().getFullYear() - 1)}
                    data-testid="com-send-anno-fiscale"
                    style={{ width: 140, padding: 7, border: "1px solid #ff8f00", borderRadius: 3, fontSize: 13, fontWeight: 700, fontFamily: "Consolas, monospace" }} />
                  <span style={{ marginLeft: 8, fontSize: 10, color: "#6d4c41" }}>L'anno solare al quale si riferiscono i pagamenti detraibili</span>
                </div>
              )}

              {(tipoPredefinito || selectedAllegatoDocx) && (
                <>
                  {targets.length > 1 && (
                    <div style={{ marginBottom: 8, fontSize: 11, color: "#666" }}>
                      Anteprima per: <select value={previewAlunnoIdx} onChange={e => setPreviewAlunnoIdx(Number(e.target.value))}
                        style={{ padding: 4, border: "1px solid #ccc", fontSize: 12 }}>
                        {targets.map((a, i) => <option key={a.Cod} value={i}>{a.Cog} {a.Nom} ({a.Cod})</option>)}
                      </select>
                    </div>
                  )}
                  <button onClick={anteprimaPdf} disabled={loadingPdfPreview} data-testid="com-send-pdf-preview"
                    style={{ padding: "8px 16px", background: loadingPdfPreview ? "#bbb" : "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: loadingPdfPreview ? "wait" : "pointer", borderRadius: 3, marginBottom: 10, marginRight: 8 }}>
                    {loadingPdfPreview ? "Generazione anteprima..." : "🔍 Anteprima PDF"}
                  </button>
                  {pdfPreviewUrl && (
                    <button onClick={() => { URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); }}
                      data-testid="com-send-pdf-preview-close"
                      style={{ padding: "8px 14px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3, marginBottom: 10 }}>
                      ✕ Chiudi anteprima
                    </button>
                  )}
                  {pdfPreviewUrl && (
                    <iframe src={pdfPreviewUrl} title="Anteprima PDF"
                      style={{ width: "100%", height: 500, border: "1px solid #ccc", borderRadius: 3 }} />
                  )}
                </>
              )}
            </div>
          )}

          {/* STEP 4: PROTOCOLLO + DATA */}
          {!result && step === 4 && (
            <div data-testid="com-send-step4">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>
                    🔢 PROTOCOLLO INIZIALE *
                  </label>
                  <input value={protocolloOverride} onChange={e => setProtocolloOverride(e.target.value.toUpperCase())}
                    placeholder="PROT-2026-000001" data-testid="com-send-proto"
                    style={{ width: "100%", padding: 8, border: "2px solid #00838f", borderRadius: 3, fontSize: 13, fontWeight: 700, fontFamily: "Consolas, monospace" }} />
                  <div style={{ fontSize: 10, color: "#666", marginTop: 3 }}>
                    Formato: <code>PROT-AAAA-NNNNNN</code>. Per N alunni verranno usati N protocolli consecutivi.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>
                    📅 DATA E ORA INVIO *
                  </label>
                  <input type="datetime-local" value={dataInvioOverride} onChange={e => setDataInvioOverride(e.target.value)}
                    data-testid="com-send-data"
                    style={{ width: "100%", padding: 8, border: "2px solid #00838f", borderRadius: 3, fontSize: 13, fontWeight: 600 }} />
                  <div style={{ fontSize: 10, color: "#666", marginTop: 3 }}>
                    Verrà stampata sul PDF e registrata nel log.
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 14, padding: 12, background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 800, color: "#4a148c", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  👨‍👩‍👧 GENITORE SELEZIONATO <span style={{ fontWeight: 400, color: "#7e57c2" }}>(per il placeholder <code>{"{genitore_selezionato_*}"}</code>)</span>
                </label>
                <div style={{ display: "flex", gap: 14, fontSize: 12, flexWrap: "wrap" }}>
                  {[
                    { v: "", lbl: "Nessuno", desc: "Il placeholder resta vuoto" },
                    { v: "1", lbl: "Genitore 1", desc: "Primo genitore trovato per ciascun alunno" },
                    { v: "2", lbl: "Genitore 2", desc: "Secondo genitore (se presente)" },
                  ].map(opt => (
                    <label key={opt.v} data-testid={`com-send-genitore-${opt.v || "none"}`}
                      style={{ display: "flex", alignItems: "flex-start", gap: 6, cursor: "pointer", padding: "8px 12px", background: genitoreSelezionato === opt.v ? "#ce93d8" : "white", border: "2px solid", borderColor: genitoreSelezionato === opt.v ? "#7e57c2" : "#ce93d8", borderRadius: 4, minWidth: 200 }}>
                      <input type="radio" name="genitore_sel" value={opt.v}
                        checked={genitoreSelezionato === opt.v}
                        onChange={() => setGenitoreSelezionato(opt.v)}
                        style={{ marginTop: 2 }} />
                      <span>
                        <b style={{ color: "#4a148c" }}>{opt.lbl}</b>
                        <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{opt.desc}</div>
                      </span>
                    </label>
                  ))}
                </div>
                {genitoreSelezionato && (
                  <div style={{ marginTop: 8, padding: 8, background: "white", border: "1px dashed #ab47bc", fontSize: 11, color: "#4a148c", borderRadius: 3 }}>
                    📝 Nel template puoi usare: <code>{"{genitore_selezionato_anagrafica_completa}"}</code> (blocco multi-riga: Cognome Nome / Indirizzo / CAP Città Prov / Nato a / Nato il / Codice Fiscale)
                    <br/>oppure i singoli: <code>{"{genitore_selezionato_cognome}"}</code>, <code>{"{genitore_selezionato_nome}"}</code>, <code>{"{genitore_selezionato_indirizzo}"}</code>, <code>{"{genitore_selezionato_cap_citta}"}</code>, <code>{"{genitore_selezionato_nato_a}"}</code>, <code>{"{genitore_selezionato_nato_il}"}</code>, <code>{"{genitore_selezionato_cf}"}</code>
                  </div>
                )}

                {/* Anteprima dati genitori per l'alunno corrente */}
                {targets.length > 0 && (
                  <div style={{ marginTop: 12, padding: 12, background: "white", border: "2px solid #ab47bc", borderRadius: 4 }}
                    data-testid="com-send-genitori-preview">
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#4a148c", letterSpacing: 0.5 }}>
                        👁 ANTEPRIMA DATI GENITORE
                      </span>
                      {targets.length > 1 ? (
                        <>
                          <span style={{ fontSize: 11, color: "#666" }}>Alunno:</span>
                          <select value={genitoriPreviewIdx}
                            onChange={e => setGenitoriPreviewIdx(Number(e.target.value))}
                            data-testid="com-send-genitori-alunno"
                            style={{ padding: "4px 8px", border: "1px solid #ab47bc", fontSize: 12, fontWeight: 600, color: "#4a148c", maxWidth: 360 }}>
                            {targets.map((a, i) => <option key={a.Cod} value={i}>{a.Cog} {a.Nom} ({a.Cod})</option>)}
                          </select>
                          <span style={{ fontSize: 10, color: "#888" }}>{targets.length} alunni selezionati totali</span>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#4a148c" }}>
                          {targets[0].Cog} {targets[0].Nom} ({targets[0].Cod})
                        </span>
                      )}
                      {loadingGenitoriPreview && <span style={{ fontSize: 11, color: "#888", marginLeft: "auto" }}>⏳ Caricamento...</span>}
                    </div>

                    {!loadingGenitoriPreview && genitoriPreview && (
                      <>
                        <div data-testid="com-genitori-source" style={{
                          marginBottom: 8, padding: "4px 10px",
                          background: genitoriPreview._source === "alunni_genitori" ? "#e8f5e9" : "#fff3e0",
                          border: `1px solid ${genitoriPreview._source === "alunni_genitori" ? "#2e7d32" : "#ef6c00"}`,
                          borderRadius: 3, fontSize: 11, color: genitoriPreview._source === "alunni_genitori" ? "#1b5e20" : "#bf360c",
                          display: "flex", alignItems: "center", gap: 8,
                        }}>
                          {genitoriPreview._source === "alunni_genitori" ? (
                            <>
                              <strong>🆕 Sorgente: Archivio Alunni-Genitori (Segremat AG1E2)</strong>
                              <span style={{ opacity: 0.85 }}>· Dati di contatto aggiornati dall'ultimo import XLSX</span>
                            </>
                          ) : genitoriPreview._source === "legacy" ? (
                            <>
                              <strong>📦 Sorgente: Sync Segremat legacy</strong>
                              <span style={{ opacity: 0.85 }}>· Alunno non presente nell'archivio AG1E2 — considera di importare il file più recente in <i>Alunni-Genitori</i></span>
                            </>
                          ) : (
                            <>
                              <strong>❓ Nessun dato genitore in archivio né nel sync legacy</strong>
                              <span style={{ opacity: 0.85 }}>· Verifica se l'alunno ha genitori registrati o importa il file XLSX da Segremat</span>
                            </>
                          )}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <GenitoreCard idx={1} data={genitoriPreview} highlight={genitoreSelezionato === "1"} />
                          <GenitoreCard idx={2} data={genitoriPreview} highlight={genitoreSelezionato === "2"} />
                        </div>
                      </>
                    )}
                    {!loadingGenitoriPreview && !genitoriPreview && (
                      <div style={{ padding: 14, textAlign: "center", color: "#888", fontSize: 12, fontStyle: "italic" }}>
                        Nessun dato genitore disponibile per questo alunno.
                      </div>
                    )}
                    {genitoreSelezionato && genitoriPreview && (
                      <div style={{ marginTop: 10, padding: 10, background: "#f3e5f5", border: "1px dashed #7e57c2", borderRadius: 3 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#4a148c", marginBottom: 4 }}>
                          ✓ Verrà usato come <code>{"{genitore_selezionato_*}"}</code>:
                        </div>
                        <pre style={{ margin: 0, fontSize: 11, color: "#37474f", fontFamily: "Consolas, monospace", whiteSpace: "pre-wrap" }}>
{genitoriPreview.genitore_selezionato_anagrafica_completa || "(vuoto)"}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>
                  🗓 DATA AGGIORNAMENTO DATI <span style={{ fontWeight: 400, color: "#888" }}>(opzionale)</span>
                </label>
                <input type="date" value={dataAggiornamento} onChange={e => setDataAggiornamento(e.target.value)}
                  data-testid="com-send-data-agg"
                  style={{ width: 220, padding: 8, border: "2px solid #00838f", borderRadius: 3, fontSize: 13, fontWeight: 600 }} />
                <div style={{ fontSize: 10, color: "#666", marginTop: 3 }}>
                  {dataAggiornamentoAutoLoaded && (
                    <div style={{ color: "#2e7d32", fontWeight: 700, marginBottom: 2 }}>
                      ✓ Pre-caricata automaticamente dall'ultima registrazione bonifici in <i>Stati di Cassa → Banca</i>.
                    </div>
                  )}
                  Data di riferimento dei dati riportati (es. estratto conto). Disponibile come placeholder <code>{"{data_aggiornamento_dati}"}</code> nei modelli e negli allegati.
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: EMAIL DESTINATARI + RIEPILOGO + INVIO */}
          {!result && step === 5 && (
            <div data-testid="com-send-step5">
              <div style={{ marginBottom: 8, padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 3, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
                   data-testid="com-modo-invio-bar">
                <span style={{ fontSize: 11, fontWeight: 800, color: "#4a148c", marginRight: 4 }}>MODALITÀ INVIO (combinabili):</span>
                <label data-testid="com-modo-preferita" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3,
                         background: modiInvio.preferita ? "#7b1fa2" : "white",
                         color: modiInvio.preferita ? "white" : "#7b1fa2",
                         border: "1px solid #7b1fa2", userSelect: "none" }}>
                  <input type="checkbox" checked={modiInvio.preferita} onChange={() => toggleModoInvio("preferita")} style={{ margin: 0 }} />
                  ★ Preferita
                </label>
                <label data-testid="com-modo-tutte" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3,
                         background: modiInvio.tutte ? "#7b1fa2" : "white",
                         color: modiInvio.tutte ? "white" : "#7b1fa2",
                         border: "1px solid #7b1fa2", userSelect: "none" }}>
                  <input type="checkbox" checked={modiInvio.tutte} onChange={() => toggleModoInvio("tutte")} style={{ margin: 0 }} />
                  📨 Tutte le mail
                </label>
                <label data-testid="com-modo-google" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3,
                         background: modiInvio.google ? "#7b1fa2" : "white",
                         color: modiInvio.google ? "white" : "#7b1fa2",
                         border: "1px solid #7b1fa2", userSelect: "none" }}>
                  <input type="checkbox" checked={modiInvio.google} onChange={() => toggleModoInvio("google")} style={{ margin: 0 }} />
                  🎓 @canossadiaz.it
                </label>
                <span style={{ fontSize: 10, color: "#6a1b9a", marginLeft: 8, lineHeight: 1.3 }}>
                  ★ + 📨 si escludono (📨 contiene già ★). 🎓 si combina con entrambi.<br/>
                  Imposta la preferita di ciascun alunno cliccando la stella accanto all&apos;email.
                </span>
              </div>
              <div style={{ marginBottom: 12, padding: 10, background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 11 }}>
                <b>Email destinatari per ciascun alunno.</b> Le email sono pre-selezionate (✓). Deseleziona per escludere temporaneamente, oppure clicca <b style={{ color: "#c62828" }}>✕</b> per cancellarle definitivamente dall&apos;anagrafica. Aggiungine di nuove con il bottone &quot;+ Aggiungi email&quot; (origine &quot;Comunicator&quot;).
              </div>
              {loadingEmails && <div style={{ padding: 16, textAlign: "center", color: "#888", fontSize: 12 }}>Caricamento email...</div>}
              {!loadingEmails && (
                <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd" }}>
                  <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 11 }}>
                    <thead style={{ background: "#1f3864", color: "white", position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ width: 280 }}>Alunno</th>
                        <th>Email registrate</th>
                        <th style={{ width: 60, textAlign: "center" }}>Sel.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map(a => {
                        const list = emailsPerAlunno[a.Cod] || [];
                        const selCount = list.filter(x => x.selected).length;
                        return (
                          <tr key={a.Cod} style={{ borderTop: "1px solid #eee", verticalAlign: "top" }}>
                            <td style={{ padding: "6px 8px", fontWeight: 700 }}>
                              <div>{a.Cog} {a.Nom}</div>
                              <div style={{ fontSize: 10, color: "#888", fontFamily: "Consolas, monospace" }}>Cod {a.Cod} · {a.classe || ""}</div>
                            </td>
                            <td style={{ padding: "6px 8px" }}>
                              {list.length === 0 && (
                                <div style={{ fontSize: 10, color: "#c62828", fontStyle: "italic", marginBottom: 4 }}>⚠ Nessuna email registrata</div>
                              )}
                              {list.map(it => {
                                const badge = SOURCE_BADGE[it.source] || SOURCE_BADGE.manual;
                                return (
                                  <div key={it.id || it.email} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1, minWidth: 0 }}>
                                      <input type="checkbox" checked={!!it.selected}
                                        onChange={() => toggleEmailSelezione(a.Cod, it.email)}
                                        data-testid={`com-em-${a.Cod}-${(it.email || "").replace(/[^a-z0-9]/gi, "_")}`} />
                                      <span style={{ fontFamily: "Consolas, monospace", color: "#0d47a1", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis" }}>{it.email}</span>
                                      <span style={{ padding: "0px 6px", background: badge.bg, color: badge.c, fontSize: 9, fontWeight: 700, borderRadius: 2, flexShrink: 0 }}>{badge.l}</span>
                                    </label>
                                    <button
                                      onClick={() => togglePreferita(a.Cod, it)}
                                      title={it.is_preferred ? "Email preferita (clicca per togliere)" : "Imposta come preferita"}
                                      data-testid={`com-em-pref-${a.Cod}-${(it.email || "").replace(/[^a-z0-9]/gi, "_")}`}
                                      style={{ background: "transparent",
                                               color: it.is_preferred ? "#ff9800" : "#bdbdbd",
                                               border: "none", cursor: "pointer", padding: "0 4px", fontSize: 14, lineHeight: 1, flexShrink: 0 }}>
                                      {it.is_preferred ? "★" : "☆"}
                                    </button>
                                    <button
                                      onClick={() => rimuoviEmailAlunno(a.Cod, it)}
                                      title={`Cancella ${it.email} dall'anagrafica`}
                                      data-testid={`com-em-del-${a.Cod}-${(it.email || "").replace(/[^a-z0-9]/gi, "_")}`}
                                      style={{ background: "transparent", color: "#c62828", border: "none", cursor: "pointer", padding: "0 4px", fontSize: 14, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>
                                      ✕
                                    </button>
                                  </div>
                                );
                              })}
                              {addEmailFor === a.Cod ? (
                                <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center" }}>
                                  <input type="email" value={newEmailVal} onChange={e => setNewEmailVal(e.target.value)}
                                    placeholder="nuova@email.it" autoFocus
                                    onKeyDown={e => { if (e.key === "Enter") aggiungiEmailAlunno(a.Cod); if (e.key === "Escape") { setAddEmailFor(null); setNewEmailVal(""); } }}
                                    data-testid={`com-em-new-${a.Cod}`}
                                    style={{ flex: 1, minWidth: 180, padding: "4px 6px", border: "1px solid #00838f", fontSize: 11, fontFamily: "Consolas, monospace" }} />
                                  <button onClick={() => aggiungiEmailAlunno(a.Cod)} disabled={savingNewEmail}
                                    data-testid={`com-em-new-save-${a.Cod}`}
                                    style={{ background: "#2e7d32", color: "white", border: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: savingNewEmail ? "wait" : "pointer" }}>
                                    {savingNewEmail ? "..." : "✓ Aggiungi"}
                                  </button>
                                  <button onClick={() => { setAddEmailFor(null); setNewEmailVal(""); }}
                                    style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>✕</button>
                                </div>
                              ) : (
                                <button onClick={() => { setAddEmailFor(a.Cod); setNewEmailVal(""); }}
                                  data-testid={`com-em-add-${a.Cod}`}
                                  style={{ marginTop: 6, background: "white", color: "#00838f", border: "1px dashed #00838f", padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                                  + Aggiungi email
                                </button>
                              )}
                            </td>
                            <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 800, color: selCount > 0 ? "#1b5e20" : "#c62828" }}>
                              {selCount}/{list.length}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Invio email di prova */}
              <div style={{ marginTop: 14, padding: 12, background: "#fff8e1", border: "1px dashed #ff8f00", borderRadius: 3 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#bf360c", marginBottom: 6 }}>🧪 Invio email di prova</div>
                <div style={{ fontSize: 11, color: "#6d4c41", marginBottom: 8 }}>
                  Invia 1 sola mail a un indirizzo a tua scelta usando i dati dell'alunno in anteprima (<b>{(targets[previewAlunnoIdx] || targets[0])?.Cog} {(targets[previewAlunnoIdx] || targets[0])?.Nom}</b>). Il protocollo NON viene incrementato e il subject sarà prefissato con <code>[TEST]</code>.
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                    placeholder="prova@email.it" data-testid="com-send-test-email"
                    onKeyDown={e => { if (e.key === "Enter") inviaTest(); }}
                    style={{ flex: 1, minWidth: 240, padding: 7, border: "1px solid #ff8f00", fontSize: 12, fontFamily: "Consolas, monospace" }} />
                  <button onClick={inviaTest} disabled={testSending || !testEmail || !selectedTpl}
                    data-testid="com-send-test-btn"
                    style={{ background: (testSending || !testEmail || !selectedTpl) ? "#ccc" : "#ef6c00", color: "white", border: "none", padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: testSending ? "wait" : "pointer", borderRadius: 3 }}>
                    {testSending ? "Invio test..." : "🧪 Invia email di prova"}
                  </button>
                </div>
                {testResult && (
                  <div data-testid="com-send-test-result"
                    style={{ marginTop: 8, padding: 8, background: testResult.mocked ? "#fff3e0" : "#e8f5e9", border: `1px solid ${testResult.mocked ? "#ffb74d" : "#81c784"}`, fontSize: 11, color: testResult.mocked ? "#bf360c" : "#1b5e20", borderRadius: 2 }}>
                    {testResult.mocked
                      ? <>⚠ <b>Modalità MOCK</b> (Resend non configurato): l'invio non è stato effettuato realmente. Subject: <code>{testResult.subject}</code></>
                      : <>✓ Email di prova inviata a <b>{testResult.to}</b>. Subject: <code>{testResult.subject}</code>{testResult.pdf_attached && <> · PDF allegato</>}</>}
                  </div>
                )}
              </div>

              {/* Riepilogo invio */}
              <div style={{ marginTop: 14, padding: 14, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 3 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1b5e20", marginBottom: 8 }}>📋 Riepilogo invio</div>
                <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                  <div>📧 Destinatari: <b>{targets.length}</b> alunni · <b>{Object.values(emailsPerAlunno).reduce((s, l) => s + l.filter(x => x.selected).length, 0)}</b> indirizzi email selezionati</div>
                  <div>✉ Modello email: <b>{templates.find(t => t.id === selectedTpl)?.label || "(nessuno)"}</b></div>
                  <div>📎 Modulo PDF: <b>{moduli.find(m => m.id === selectedModulo)?.nome || "(nessuno)"}</b></div>
                  <div>✍ Firma: <b>{firme.find(f => f.id === selectedFirma)?.nome || "(nessuna)"}</b></div>
                  <div>💶 Addebiti selezionati: <b>{totMovSelezionati}</b> (€ {fmtEur(totImportoSelezionato)})</div>
                  {protocolloRange
                    ? <div>🔢 Protocolli: <b>{protocolloRange.start}</b> … <b>{protocolloRange.end}</b> ({protocolloRange.count})</div>
                    : <div style={{ color: "#c62828" }}>⚠ Protocollo iniziale non valido</div>}
                  <div>📅 Data invio: <b>{dataInvioOverride ? new Date(dataInvioOverride).toLocaleString("it-IT") : "—"}</b></div>
                  {dataAggiornamento && <div>🗓 Data aggiornamento dati: <b>{formatDateIt(dataAggiornamento)}</b></div>}
                </div>
              </div>

              {/* PANNELLO CREDITI RESEND (preventivo) */}
              {(() => {
                const totSelMails = Object.values(emailsPerAlunno).reduce((s, l) => s + (l || []).filter(x => x.selected).length, 0);
                return (
                  <div data-testid="com-send-credit-panel"
                       style={{ marginTop: 12, padding: 12, background: "#f5f7fa", border: "1px solid #cfd8dc", borderRadius: 3 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#00838f" }}>💳 Crediti Resend</div>
                      {!credit && (
                        <button onClick={() => loadCredit(true)} disabled={creditLoading}
                                data-testid="com-send-credit-load"
                                style={{ padding: "6px 14px", background: "#00838f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: creditLoading ? "wait" : "pointer", borderRadius: 3 }}>
                          {creditLoading ? "Sto leggendo Resend…" : "🔄 Verifica credito (tempo reale)"}
                        </button>
                      )}
                      {credit && credit.configured && (
                        <>
                          <div style={{ flex: 1, minWidth: 220 }}>
                            {/* BARRA MENSILE */}
                            <div style={{ fontSize: 9, fontWeight: 700, color: "#666", marginBottom: 2 }}>MENSILE</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 14, background: "#eee", borderRadius: 7, overflow: "hidden", position: "relative" }}>
                                <div style={{
                                  width: `${Math.min(100, credit.percentage_used)}%`,
                                  height: "100%",
                                  background: credit.percentage_used >= 90 ? "#c62828"
                                            : credit.percentage_used >= 70 ? "#ef6c00"
                                            : "#2e7d32",
                                }} />
                                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#222" }}>
                                  {credit.used_this_month} / {credit.monthly_limit}
                                </div>
                              </div>
                            </div>
                            {/* BARRA GIORNALIERA */}
                            {credit.daily_limit != null && (
                              <>
                                <div style={{ fontSize: 9, fontWeight: 700, color: "#666", marginTop: 6, marginBottom: 2 }}>OGGI</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ flex: 1, height: 14, background: "#eee", borderRadius: 7, overflow: "hidden", position: "relative" }}>
                                    <div style={{
                                      width: `${Math.min(100, credit.percentage_used_today || 0)}%`,
                                      height: "100%",
                                      background: (credit.percentage_used_today || 0) >= 90 ? "#c62828"
                                                : (credit.percentage_used_today || 0) >= 70 ? "#ef6c00"
                                                : "#1565c0",
                                    }} />
                                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#222" }}>
                                      {credit.used_today || 0} / {credit.daily_limit}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          <button onClick={() => loadCredit(true)} disabled={creditLoading}
                                  data-testid="com-send-credit-refresh"
                                  style={{ padding: "4px 10px", background: "white", color: "#00838f", border: "1px solid #00838f", fontSize: 10, fontWeight: 700, cursor: creditLoading ? "wait" : "pointer", borderRadius: 3 }}>
                            ↻
                          </button>
                        </>
                      )}
                    </div>
                    {credit && credit.configured && (() => {
                      // Residuo effettivo = il minore tra giornaliero e mensile
                      const remDaily = (credit.remaining_today != null) ? credit.remaining_today : credit.remaining;
                      const remMonth = credit.remaining;
                      const remEff = (credit.remaining_effective != null)
                        ? credit.remaining_effective
                        : Math.min(remDaily, remMonth);
                      const isLimitedByDaily = remDaily < remMonth;
                      const overLimit = totSelMails > remEff;
                      return (
                        <div data-testid="com-send-credit-status"
                             style={{ marginTop: 10, padding: 10, borderRadius: 3,
                                      background: overLimit ? "#ffebee" : "#e8f5e9",
                                      border: `1px solid ${overLimit ? "#ef5350" : "#81c784"}` }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: overLimit ? "#b71c1c" : "#1b5e20" }}>
                            {overLimit
                              ? <>🛑 ATTENZIONE: stai per inviare <b>{totSelMails}</b> mail ma sono disponibili solo <b>{remEff}</b> crediti
                                  {isLimitedByDaily ? <> (limite <b>giornaliero</b> di {credit.daily_limit}/giorno)</> : <> (limite <b>mensile</b>)</>}.
                                  Riduci i destinatari a max <b>{remEff}</b>{isLimitedByDaily ? <> oppure attendi domani / aggiorna il piano su resend.com</> : <> oppure aggiorna il piano su resend.com</>}.</>
                              : <>✅ OK: stai per inviare <b>{totSelMails}</b> mail. Rimarranno <b>{remEff - totSelMails}</b> crediti {isLimitedByDaily ? "oggi" : "questo mese"} dopo l'invio.</>}
                          </div>
                        </div>
                      );
                    })()}
                    {credit && !credit.configured && (
                      <div style={{ marginTop: 8, color: "#c62828", fontSize: 11, fontWeight: 700 }}>
                        ⚠ {credit.error_it || "Resend non configurato"}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* RISULTATO */}
          {result && (
            <div style={{ padding: 14, background: result.aborted ? "#fff3e0" : "#e8f5e9", border: `1px solid ${result.aborted ? "#ef6c00" : "#81c784"}`, borderRadius: 3 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: result.aborted ? "#bf360c" : "#1b5e20", marginBottom: 10 }}>
                {result.aborted ? (stampaMode ? "⚠ Stampa INTERROTTA" : "⚠ Invio INTERROTTO") : (stampaMode ? "✓ Stampa completata" : "✓ Invio completato")}
              </div>
              {result.aborted && result.aborted_message_it && (
                <div data-testid="com-send-aborted-msg" style={{ marginBottom: 10, padding: 10, background: "#ffebee", border: "1px solid #ef5350", borderRadius: 3, fontSize: 12, color: "#b71c1c", fontWeight: 600 }}>
                  🛑 {result.aborted_message_it}
                  {result.aborted_reason && (
                    <div style={{ marginTop: 4, fontSize: 10, color: "#666", fontWeight: 400 }}>
                      Codice: <code>{result.aborted_code || "?"}</code> · Tipo: <code>{result.aborted_reason}</code>
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {stampaMode
                  ? <div>🖨 Stampati: <b data-testid="com-send-result-printed">{result.printed || 0}</b></div>
                  : <div>📧 Inviate: <b data-testid="com-send-result-sent">{result.sent}</b></div>}
                <div>⚠ Errori: <b data-testid="com-send-result-errors" style={{ color: result.errors > 0 ? "#c62828" : "#666" }}>{result.errors}</b></div>
                {Number(result.pending || 0) > 0 && (
                  <div>⏸ Non inviate (batch interrotto): <b data-testid="com-send-result-pending" style={{ color: "#bf360c" }}>{result.pending}</b></div>
                )}
                {!stampaMode && <div>⊘ Saltate (senza email): <b style={{ color: "#888" }}>{result.skipped}</b></div>}
                {result.mocked && !stampaMode && <div style={{ marginTop: 8, color: "#bf360c" }}>⚠ Modalità MOCK (Resend non configurato).</div>}
                <div style={{ marginTop: 8, fontSize: 10, color: "#888" }}>Batch ID: <code>{result.batch_id}</code></div>
                {stampaMode && result.results && result.results.filter(x => x.outbox_id).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>📄 PDF generati (clicca per aprire/stampare):</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflow: "auto" }}>
                      {result.results.filter(x => x.outbox_id).map((r, i) => (
                        <button key={i} type="button" data-testid={`com-stampa-open-${r.cod}`} onClick={() => openOutboxPdf(r.outbox_id)}
                          style={{ textAlign: "left", background: "white", border: "1px solid #5d4037", color: "#5d4037", padding: "6px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                          📄 {(r.cognome || "")} {(r.nome || "")} — apri PDF
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {result.results && result.results.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer", fontWeight: 700 }}>Vedi dettaglio per alunno ({result.results.length})</summary>
                    <ul style={{ fontSize: 11, fontFamily: "Consolas, monospace", margin: "6px 0", maxHeight: 240, overflow: "auto" }}>
                      {result.results.map((r, i) => (
                        <li key={i} style={{ color: r.status === "pending" ? "#bf360c" : (r.status === "error" ? "#c62828" : "#222") }}>
                          {r.protocollo || "—"} · cod {r.cod} → {r.to || "(no email)"} ({r.status})
                          {r.status === "pending" && " — non inviata: batch interrotto"}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          {err && <div style={{ marginTop: 12, padding: 10, background: "#fff0f0", color: "#c00", border: "1px solid #fcc", borderRadius: 3, fontSize: 12 }}>{err}</div>}
        </div>

        {/* FOOTER NAVIGATION */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: "#fafafa" }}>
          {!result ? (
            <>
              <button onClick={onClose} data-testid="com-send-cancel"
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                Annulla
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                {(() => {
                  const idx = ORDER.indexOf(step);
                  const prev = idx > 0 ? ORDER[idx - 1] : null;
                  const next = idx < ORDER.length - 1 ? ORDER[idx + 1] : null;
                  const isLast = next === null;
                  const isFirst = prev === null;
                  return (
                    <>
                      {!isFirst && (
                        <button onClick={() => setStep(prev)} data-testid="com-send-prev"
                          style={{ background: "white", color: "#00838f", border: "1px solid #00838f", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                          ‹ Indietro
                        </button>
                      )}
                      {!isLast && (
                        <>
                          {onSkip && step === 2 && (
                            <button onClick={onSkip} data-testid="com-send-skip-next"
                              title="Salta questo alunno e passa al successivo della coda (nessun invio, nessun log)"
                              style={{ background: "#c62828", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
                              ⏭ Salta
                            </button>
                          )}
                          <button onClick={() => setStep(next)} disabled={step === 1 && !selectedTpl} data-testid="com-send-next"
                            style={{ background: (step === 1 && !selectedTpl) ? "#bbb" : "#00838f", color: "white", border: "none", padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                            Avanti ›
                          </button>
                        </>
                      )}
                      {isLast && (
                        <label data-testid="com-include-googleemail-lbl"
                          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: includeGoogleEmail ? "#1b5e20" : "#666", padding: "6px 10px", border: `1px solid ${includeGoogleEmail ? "#2e7d32" : "#ccc"}`, background: includeGoogleEmail ? "#e8f5e9" : "white", borderRadius: 3, cursor: "pointer", marginRight: 8 }}>
                          <input type="checkbox" checked={includeGoogleEmail} onChange={e => setIncludeGoogleEmail(e.target.checked)}
                            data-testid="com-include-googleemail" />
                          ✉ Includi anche Google Email
                        </label>
                      )}
                      {isLast && (() => {
                        const totSelected = Object.values(emailsPerAlunno).reduce(
                          (s, l) => s + (l || []).filter(x => x.selected).length, 0
                        );
                        const hasReport = !!(tipoPredefinito || selectedModulo || selectedAllegatoDocx);
                        const canSend = stampaMode
                          ? (!sending && selectedTpl && hasReport)
                          : (!sending && selectedTpl && totSelected > 0);
                        const disabledReason = !selectedTpl
                          ? "Seleziona un modello email"
                          : (stampaMode
                              ? (!hasReport ? "Seleziona il documento da stampare (Estratto Conto / Welfare / DDR / modulo)" : "")
                              : (totSelected === 0
                                  ? "Seleziona almeno un'email destinatario nello step Email"
                                  : ""));
                        return (
                          <>
                            {onSkip && !sending && (
                              <button onClick={onSkip} data-testid="com-send-skip-step5"
                                title="Salta questo alunno e passa al successivo della coda (nessun invio)"
                                style={{
                                  background: "white", color: "#6a1b9a",
                                  border: "1px solid #6a1b9a",
                                  padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3, marginRight: 8,
                                }}>
                                ⏭ Salta alunno
                              </button>
                            )}
                            <button onClick={invia} disabled={!canSend} data-testid="com-send-confirm"
                              title={disabledReason}
                              style={{ background: canSend ? (stampaMode ? "#5d4037" : "#00838f") : "#bbb", color: "white", border: "none", padding: "8px 22px", fontSize: 12, fontWeight: 700, cursor: sending ? "wait" : (canSend ? "pointer" : "not-allowed"), borderRadius: 3 }}>
                              {sending
                                ? (stampaMode ? `Stampa in corso (${targets.length})...` : `Invio in corso (${targets.length})...`)
                                : (stampaMode ? `🖨 Stampa · ${targets.length} ${targets.length > 1 ? "alunni" : "alunno"}` : `📧 Conferma invio · ${totSelected} email`)}
                            </button>
                          </>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            </>
          ) : (
            <button onClick={onDone} data-testid="com-send-close-after"
              style={{ background: "#00838f", color: "white", border: "none", padding: "8px 24px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3, marginLeft: "auto" }}>
              {bulkProgress && bulkProgress.current < bulkProgress.total
                ? `✓ Prossimo alunno (${bulkProgress.current + 1}/${bulkProgress.total}) ›`
                : "✓ Chiudi"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FiltroAnno({ label, da, a, onChange }) {
  return (
    <div style={{ background: "#f8f8f8", padding: 8, border: "1px solid #ddd", borderRadius: 3 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginBottom: 4, textTransform: "uppercase" }}>Anno {label}</div>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#888" }}>dal</span>
        <input type="number" value={da || ""} onChange={e => onChange(e.target.value, a)} placeholder="—" min={2000} max={2100}
          style={{ width: 70, padding: 4, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
        <span style={{ fontSize: 10, color: "#888" }}>al</span>
        <input type="number" value={a || ""} onChange={e => onChange(da, e.target.value)} placeholder="—" min={2000} max={2100}
          style={{ width: 70, padding: 4, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
      </div>
    </div>
  );
}

// ============== TAB 2: TEMPLATES ==============
export function ComunicatorTemplatesView() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [allegatiDocx, setAllegatiDocx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFisso, setUploadingFisso] = useState(false);
  const fissoRef = useRef(null);
  const [lastFocused, setLastFocused] = useState("body"); // 'subject' | 'body'
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/comunicator/templates");
      setItems(r.data.items || []);
      setPlaceholders(r.data.placeholders || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    carica();
    api.get("/comunicator/allegati-docx").then(r => setAllegatiDocx(r.data.items || [])).catch(() => { });
  }, []);

  const salva = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/comunicator/templates/${editing.id}`, {
          subject: editing.subject, body: editing.body,
          name: editing.label || "",
          allegato_docx_id: editing.allegato_docx_id || null,
          allegati_fissi: editing.allegati_fissi || [],
          reply_to: editing.reply_to || "",
        });
        setEditing(null);
      } else if (creating) {
        await api.post("/comunicator/templates", {
          subject: creating.subject, body: creating.body,
          name: creating.label || creating.name || "",
          allegato_docx_id: creating.allegato_docx_id || null,
          allegati_fissi: creating.allegati_fissi || [],
          reply_to: creating.reply_to || "",
        });
        setCreating(null);
      }
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const elimina = async (tid) => {
    if (!window.confirm("Eliminare il modello?")) return;
    try {
      await api.delete(`/comunicator/templates/${tid}`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const editing_or_creating = editing || creating;
  const setField = (k, v) => {
    if (editing) setEditing({ ...editing, [k]: v });
    else if (creating) setCreating({ ...creating, [k]: v });
  };
  const insertPh = (ph) => {
    if (!editing_or_creating) return;
    if (lastFocused === "subject") {
      const input = subjectRef.current;
      const current = editing_or_creating.subject || "";
      const start = input?.selectionStart ?? current.length;
      const end = input?.selectionEnd ?? current.length;
      const newV = current.slice(0, start) + ph + current.slice(end);
      setField("subject", newV);
      requestAnimationFrame(() => {
        if (input) { input.focus(); input.setSelectionRange(start + ph.length, start + ph.length); }
      });
    } else {
      // body (Quill: usa l'API per inserire alla posizione del cursore)
      const r = bodyRef.current;
      if (r && r.insertText) {
        r.insertText(ph);
      } else {
        setField("body", (editing_or_creating.body || "") + ph);
      }
    }
  };

  const onFissoSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !editing_or_creating) return;
    setUploadingFisso(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/comunicator/template-allegati/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data);
      }
      setField("allegati_fissi", [...((editing_or_creating).allegati_fissi || []), ...added]);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Upload allegato non riuscito");
    } finally { setUploadingFisso(false); }
  };
  const removeFisso = (aid) => {
    setField("allegati_fissi", ((editing_or_creating).allegati_fissi || []).filter(a => a.id !== aid));
  };


  return (
    <div style={{ padding: "12px 20px" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: "#00838f", fontSize: 20 }}>✉ Modelli mail</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
            Crea modelli per estratto conto, solleciti, dichiarazioni, comunicazioni alle famiglie.
          </div>
        </div>
        <button onClick={() => setCreating({ subject: "", body: "" })} data-testid="com-tpl-new"
          style={{ background: "#00838f", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          + Nuovo modello
        </button>
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      <div style={{ background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 12 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ width: 90 }}>ETICH.</th>
              <th style={{ width: 220 }}>NOME MODELLO</th>
              <th>OGGETTO</th>
              <th style={{ width: 170 }}>↩ RISPONDI A</th>
              <th style={{ width: 160 }}>📎 ALLEGATO PROPOSTO</th>
              <th style={{ width: 110, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                Nessun modello. Crea il primo per iniziare a inviare email mirate.
              </td></tr>
            )}
            {!loading && items.map(t => (
              <tr key={t.id} style={{ borderTop: "1px solid #eee" }} data-testid={`com-tpl-row-${t.id}`}>
                <td style={{ fontFamily: "Consolas, monospace", color: "#00838f", fontWeight: 700 }}>{t.id}</td>
                <td style={{ color: "#1f3864", fontWeight: 700 }} data-testid={`com-tpl-name-${t.id}`}>
                  {(t.label && t.label !== t.id) ? t.label : <span style={{ color: "#bbb", fontWeight: 400, fontStyle: "italic" }}>(senza nome)</span>}
                </td>
                <td style={{ color: "#555" }}>{t.subject}</td>
                <td data-testid={`com-tpl-replyto-${t.id}`} style={{ fontSize: 11, color: t.reply_to ? "#ef6c00" : "#bbb", fontWeight: t.reply_to ? 700 : 400 }}>
                  {t.reply_to ? `↩ ${t.reply_to}` : <span style={{ fontStyle: "italic" }}>(default)</span>}
                </td>
                <td data-testid={`com-tpl-allegato-${t.id}`} style={{ fontSize: 11 }}>
                  {t.allegato_docx_id
                    ? <span style={{ color: "#7b1fa2", fontWeight: 700 }}>📎 {(allegatiDocx.find(a => a.id === t.allegato_docx_id)?.nome) || t.allegato_docx_id}</span>
                    : <span style={{ color: "#bbb", fontStyle: "italic" }}>—</span>}
                  {(t.allegati_fissi || []).length > 0 && (
                    <span data-testid={`com-tpl-fissi-count-${t.id}`} style={{ display: "inline-block", marginLeft: 6, color: "#2e7d32", fontWeight: 700 }}>
                      · 📎×{(t.allegati_fissi || []).length} fissi
                    </span>
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => setEditing({ ...t })} data-testid={`com-tpl-edit-${t.id}`}
                    style={{ background: "#1565c0", color: "white", border: "none", padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏</button>
                  <button onClick={() => elimina(t.id)} data-testid={`com-tpl-del-${t.id}`}
                    style={{ background: "#c62828", color: "white", border: "none", padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing_or_creating && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div data-testid="com-tpl-modal"
            style={{ background: "white", width: "100%", maxWidth: 780, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#00838f", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{editing ? `✏ Modifica modello: ${editing.id}${editing.label && editing.label !== editing.id ? " — " + editing.label : ""}` : "+ Nuovo modello mail"}</strong>
              <button onClick={() => { setEditing(null); setCreating(null); }} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
              {creating && (
                <div style={{ marginBottom: 12, padding: 10, background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 12, color: "#006064" }}>
                  L'<b>etichetta</b> verrà assegnata automaticamente al salvataggio (es. <code>M001</code>, <code>M002</code>, ...). Il <b>Nome modello</b> è invece libero e serve per riconoscere subito il template nel wizard invio.
                </div>
              )}
              {editing && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ETICHETTA</label>
                  <input value={editing.id} readOnly data-testid="com-tpl-label-ro"
                    style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 13, fontWeight: 700, fontFamily: "Consolas, monospace", background: "#f5f5f5", color: "#1f3864" }} />
                </div>
              )}

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOME MODELLO</label>
              <input value={(editing_or_creating).label || ""}
                onChange={e => setField("label", e.target.value)}
                placeholder="Es. Sollecito mensa, Estratto conto famiglia, Comunicazione fine anno..."
                data-testid="com-tpl-name"
                style={{ width: "100%", padding: 8, border: "2px solid #1f3864", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 12, background: "#f8faff" }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>📎 ALLEGATO PROPOSTO (DOCX)</label>
              <select value={(editing_or_creating).allegato_docx_id || ""}
                onChange={e => setField("allegato_docx_id", e.target.value)}
                data-testid="com-tpl-allegato-select"
                style={{ width: "100%", padding: 8, border: "2px solid #7b1fa2", borderRadius: 3, fontSize: 13, marginBottom: 4, background: "#faf5fc" }}>
                <option value="">— Nessun allegato proposto —</option>
                {allegatiDocx.map(a => (
                  <option key={a.id} value={a.id}>{a.nome}{a.filename ? ` (${a.filename})` : ""}</option>
                ))}
              </select>
              <div style={{ fontSize: 10, color: "#7b1fa2", marginBottom: 12 }}>
                Verrà proposto automaticamente nel wizard invio quando selezioni questo modello. Potrai comunque cambiarlo prima dell'invio.
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>📎 ALLEGATI FISSI (inviati con OGNI email del modello)</label>
              <div style={{ border: "2px solid #2e7d32", borderRadius: 3, padding: 10, marginBottom: 4, background: "#f4fbf4" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: (editing_or_creating.allegati_fissi || []).length ? 8 : 0 }}>
                  <button type="button" onClick={() => fissoRef.current?.click()} disabled={uploadingFisso}
                    data-testid="com-tpl-fisso-add"
                    style={{ background: "#2e7d32", color: "white", border: "none", padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: uploadingFisso ? "wait" : "pointer", borderRadius: 3 }}>
                    {uploadingFisso ? "Caricamento…" : "📎 Aggiungi allegato"}
                  </button>
                  <span style={{ fontSize: 10, color: "#2e7d32" }}>PDF, immagini, DOCX, ecc. (max 15 MB ciascuno)</span>
                  <input ref={fissoRef} type="file" multiple onChange={onFissoSelected} style={{ display: "none" }} data-testid="com-tpl-fisso-input" />
                </div>
                {(editing_or_creating.allegati_fissi || []).length > 0 && (
                  <div data-testid="com-tpl-fisso-list" style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {(editing_or_creating.allegati_fissi || []).map(a => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "white", border: "1px solid #c8e6c9", borderRadius: 4, padding: "5px 8px" }}>
                        <span style={{ fontSize: 13 }}>📎</span>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/comunicator/template-allegati/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: "#1b5e20", fontWeight: 600, flex: 1, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</a>
                        {a.size ? <span style={{ fontSize: 10, color: "#888" }}>{a.size < 1024 ? `${a.size} B` : a.size < 1024 * 1024 ? `${Math.round(a.size / 1024)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`}</span> : null}
                        <button type="button" onClick={() => removeFisso(a.id)} data-testid={`com-tpl-fisso-del-${a.id}`}
                          title="Rimuovi" style={{ background: "none", border: "none", color: "#c62828", cursor: "pointer", fontWeight: 700, fontSize: 14 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10, color: "#2e7d32", marginBottom: 12 }}>
                Questi file vengono allegati automaticamente a ogni email inviata con questo modello (sia invio evoluto che base).
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>OGGETTO *</label>
              <input ref={subjectRef} value={(editing_or_creating).subject}
                onChange={e => setField("subject", e.target.value)}
                onFocus={() => setLastFocused("subject")}
                data-testid="com-tpl-subject"
                style={{ width: "100%", padding: 8, border: "2px solid #00838f", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>↩ RISPONDI A (Reply-To)</label>
              <input type="email" value={(editing_or_creating).reply_to || ""}
                onChange={e => setField("reply_to", e.target.value)}
                data-testid="com-tpl-reply-to"
                placeholder="es. segreteria@canossadiaz.it — dove arrivano le risposte (vuoto = indirizzo predefinito)"
                style={{ width: "100%", padding: 8, border: "2px solid #ef6c00", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 4, background: "#fff8f0" }} />
              <div style={{ fontSize: 10, color: "#ef6c00", marginBottom: 12 }}>
                Quando il destinatario risponde a questa mail, la risposta arriva a questo indirizzo. Se vuoto si usa l'indirizzo di risposta predefinito del sistema.
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CORPO *</label>
              <div onFocus={() => setLastFocused("body")}>
                <RichEditor ref={bodyRef} value={(editing_or_creating).body}
                  onChange={(html) => setField("body", html)}
                  placeholder="Scrivi qui il corpo della mail. Usa la barra strumenti per formattare." height={260}
                  testid="com-tpl-body" />
              </div>
            </div>
            {/* PLACEHOLDER bar — sempre visibile, fuori dallo scroll */}
            <div style={{ padding: "10px 18px", background: "#e0f7fa", borderTop: "1px solid #4dd0e1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#006064", marginBottom: 6 }}>📋 PLACEHOLDER (clicca per inserire nel campo selezionato: <b>{lastFocused === "subject" ? "OGGETTO" : "CORPO"}</b>)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 90, overflowY: "auto" }}>
                {placeholders.map(ph => (
                  <button key={ph} onClick={() => insertPh(ph)}
                    style={{ padding: "3px 8px", background: "white", color: "#006064", border: "1px solid #00838f", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>{ph}</button>
                ))}
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setEditing(null); setCreating(null); }}
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>Annulla</button>
              <button onClick={salva} disabled={saving || !(editing_or_creating).subject || !(editing_or_creating).body}
                data-testid="com-tpl-save"
                style={{ background: "#00838f", color: "white", border: "none", padding: "8px 24px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                {saving ? "Salvataggio..." : "💾 Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== TAB 3: LOG ==============
export function ComunicatorLogView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ sent: 0, sent_mock: 0, error: 0, pending: 0 });
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroBatch, setFiltroBatch] = useState("");
  const [filtroAlunno, setFiltroAlunno] = useState("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [resendCfg, setResendCfg] = useState(false);
  // Crediti Resend
  const [credit, setCredit] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  // Verifica consegna
  const [verifyDays, setVerifyDays] = useState(7);
  const [verifying, setVerifying] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  // Settings panel
  const [showSettings, setShowSettings] = useState(false);
  const [settingsLimit, setSettingsLimit] = useState(3000);
  const [settingsDaily, setSettingsDaily] = useState(100);
  const [settingsLabel, setSettingsLabel] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const caricaCredit = async (refresh = false) => {
    setCreditLoading(true);
    try {
      const r = await api.get("/comunicator/resend/credit", { params: refresh ? { refresh: 1 } : {} });
      setCredit(r.data);
    } catch (e) {
      setCredit({ configured: false, error_it: e.response?.data?.detail || e.message });
    } finally { setCreditLoading(false); }
  };

  const caricaSettings = async () => {
    try {
      const r = await api.get("/comunicator/resend/settings");
      setSettingsLimit(r.data.monthly_limit || 3000);
      setSettingsDaily(r.data.daily_limit || 100);
      setSettingsLabel(r.data.plan_label || "");
    } catch (e) { /* ignore */ }
  };

  const salvaSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/comunicator/resend/settings", {
        monthly_limit: Number(settingsLimit),
        daily_limit: Number(settingsDaily),
        plan_label: settingsLabel || null,
      });
      await caricaCredit(true);
      setShowSettings(false);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSavingSettings(false); }
  };

  const verificaConsegna = async () => {
    if (!window.confirm(`Verificare lo stato di consegna delle email inviate negli ultimi ${verifyDays} giorni?\n\nQuesta operazione interroga Resend per ogni email e può richiedere alcuni secondi.`)) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await api.post("/comunicator/resend/verify-delivery", {
        days: Number(verifyDays),
        limit: 200,
      });
      setVerifyResult(r.data);
      await carica();  // refresh log per mostrare i nuovi stati
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setVerifying(false); }
  };

  const reinviaInvio = async (it) => {
    const dests = (it.to_list && it.to_list.length ? it.to_list : (it.to ? [it.to] : []));
    const who = `${it.cognome || ""} ${it.nome || ""}`.trim() || `cod ${it.alunno_cod || ""}`;
    if (!window.confirm(`Reinviare l'email "${it.subject || ""}"\na: ${dests.join(", ")}\n(${who})?\n\nVerrà spedita di nuovo con lo stesso oggetto, testo ed eventuali allegati, e registrata nel log.`)) return;
    setResendingId(it._id);
    try {
      const r = await api.post(`/comunicator/outbox/${it._id}/resend`);
      if (r.data.mocked) alert("Reinvio simulato (invio email non configurato in questo ambiente).");
      else if (r.data.status === "error") alert("Errore nel reinvio: " + (r.data.error || "sconosciuto"));
      await carica();
    } catch (e) {
      alert("Errore reinvio: " + (e.response?.data?.detail || e.message));
    } finally { setResendingId(null); }
  };

  const eliminaInvio = async (it) => {
    if (!isAdmin) return;
    const proto = it.protocollo || (it._id ? String(it._id).slice(-6) : "");
    const who = `${it.cognome || ""} ${it.nome || ""}`.trim() || `cod ${it.alunno_cod}`;
    if (!window.confirm(`Eliminare DEFINITIVAMENTE l'invio ${proto} a ${who}?\n\nQuesta operazione cancella solo la riga di log locale (Resend manterrà comunque la sua copia). Non si può annullare.`)) return;
    try {
      await api.delete(`/comunicator/outbox/${it._id}`);
      setItems(prev => prev.filter(x => x._id !== it._id));
    } catch (e) {
      alert("Errore eliminazione: " + (e.response?.data?.detail || e.message));
    }
  };

  const carica = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (filtroBatch) params.batch_id = filtroBatch;
      const r = await api.get("/comunicator/outbox", { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
      setResendCfg(!!r.data.resend_configured);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [filtroStatus, filtroBatch]);

  // Filtro client-side per nome/cognome/CF/codice alunno
  const itemsFiltered = useMemo(() => {
    const q = (filtroAlunno || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => {
      const s = `${it.cognome || ""} ${it.nome || ""} ${it.cod_fis || it.codice_fiscale || ""} ${it.alunno_cod || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [items, filtroAlunno]);

  const fmt = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return s; }
  };

  const badge = (s) => {
    const map = {
      sent: { bg: "#1b5e20", txt: "✓ INVIATA" },
      sent_partial: { bg: "#558b2f", txt: "◐ PARZIALE" },
      sent_mock: { bg: "#ef6c00", txt: "⊘ MOCK" },
      error: { bg: "#c62828", txt: "✗ ERRORE" },
      pending: { bg: "#bf360c", txt: "⏸ NON INVIATA" },
      nota: { bg: "#1565c0", txt: "📝 NOTA" },
      telefonata: { bg: "#6a1b9a", txt: "📞 TELEFONATA" },
    };
    const c = map[s] || { bg: "#888", txt: (s || "?").toUpperCase() };
    return <span style={{ padding: "2px 8px", background: c.bg, color: "white", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{c.txt}</span>;
  };

  const deliveryBadge = (s) => {
    if (!s) return <span style={{ fontSize: 10, color: "#bbb" }}>—</span>;
    const map = {
      delivered: { bg: "#1b5e20", txt: "✓ Consegnata" },
      sent: { bg: "#ef6c00", txt: "⏳ In viaggio" },
      bounced: { bg: "#c62828", txt: "✗ Rimbalzata" },
      complained: { bg: "#bf360c", txt: "⚠ Spam" },
      failed: { bg: "#c62828", txt: "✗ Fallita" },
      not_found: { bg: "#9e9e9e", txt: "❓ Non trovata" },
      api_error: { bg: "#9e9e9e", txt: "⚠ API err" },
    };
    const c = map[s] || { bg: "#9e9e9e", txt: s };
    return <span title={`Stato Resend: ${s}`} style={{ padding: "2px 6px", background: c.bg, color: "white", fontSize: 9, fontWeight: 700, borderRadius: 2 }}>{c.txt}</span>;
  };

  return (
    <div style={{ padding: "12px 20px" }}>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: "#00838f", fontSize: 20 }}>📬 Log Invii Comunicator</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Cronologia delle email inviate dal Comunicator (separata da Iscrizioni Extra).</div>
        </div>
        {resendCfg
          ? <span style={{ padding: "4px 10px", background: "#1b5e20", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 3 }}>✓ RESEND ATTIVO</span>
          : <span style={{ padding: "4px 10px", background: "#bf360c", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 3 }}>⚠ RESEND NON CONFIGURATO</span>}
      </div>

      {/* PANNELLO CREDITI RESEND */}
      <div data-testid="resend-credit-panel"
           style={{ marginBottom: 12, padding: 12, background: "#f5f7fa", border: "1px solid #cfd8dc", borderRadius: 4, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 18 }}>💳</div>
        {!credit && (
          <button onClick={() => caricaCredit(true)} disabled={creditLoading}
                  data-testid="resend-credit-load"
                  style={{ padding: "8px 18px", background: "#00838f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: creditLoading ? "wait" : "pointer", borderRadius: 3 }}>
            {creditLoading ? "Sto leggendo Resend…" : "🔄 Verifica credito Resend (tempo reale)"}
          </button>
        )}
        {credit && credit.configured && (
          <>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                Crediti usati (piano: <b>{credit.plan_label}</b>)
              </div>
              {/* BARRA MENSILE */}
              <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginBottom: 2 }}>MENSILE</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, height: 18, background: "#eee", borderRadius: 9, overflow: "hidden", position: "relative" }}>
                  <div style={{
                    width: `${Math.min(100, credit.percentage_used)}%`,
                    height: "100%",
                    background: credit.percentage_used >= 90 ? "#c62828"
                              : credit.percentage_used >= 70 ? "#ef6c00"
                              : "#2e7d32",
                    transition: "width 0.4s",
                  }} />
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#222" }}>
                    {credit.used_this_month} / {credit.monthly_limit} ({credit.percentage_used}%)
                  </div>
                </div>
              </div>
              {/* BARRA GIORNALIERA */}
              {credit.daily_limit != null && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginTop: 8, marginBottom: 2 }}>OGGI</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 18, background: "#eee", borderRadius: 9, overflow: "hidden", position: "relative" }}>
                      <div style={{
                        width: `${Math.min(100, credit.percentage_used_today || 0)}%`,
                        height: "100%",
                        background: (credit.percentage_used_today || 0) >= 90 ? "#c62828"
                                  : (credit.percentage_used_today || 0) >= 70 ? "#ef6c00"
                                  : "#1565c0",
                        transition: "width 0.4s",
                      }} />
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#222" }}>
                        {credit.used_today || 0} / {credit.daily_limit} ({credit.percentage_used_today || 0}%)
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div data-testid="resend-credit-remaining" style={{ fontSize: 12, marginTop: 8, fontWeight: 700, color: (credit.remaining_effective != null ? credit.remaining_effective : credit.remaining) <= 50 ? "#c62828" : (credit.remaining_effective != null ? credit.remaining_effective : credit.remaining) <= 200 ? "#ef6c00" : "#1b5e20" }}>
                {credit.remaining_today != null && credit.remaining_today < credit.remaining
                  ? <>✉ Rimangono <b>{credit.remaining_today}</b> invii <b>oggi</b> · <span style={{ color: "#666", fontWeight: 600 }}>{credit.remaining} questo mese</span></>
                  : <>✉ Rimangono <b>{credit.remaining}</b> invii questo mese{credit.remaining_today != null ? <> · <span style={{ color: "#666", fontWeight: 600 }}>{credit.remaining_today} oggi</span></> : null}</>}
                {credit.cached && <span style={{ marginLeft: 8, fontSize: 10, color: "#888", fontWeight: 400 }}>(cache 30s)</span>}
              </div>
            </div>
            <button onClick={() => caricaCredit(true)} disabled={creditLoading}
                    data-testid="resend-credit-refresh"
                    title="Aggiorna in tempo reale da Resend"
                    style={{ padding: "6px 12px", background: "white", color: "#00838f", border: "1px solid #00838f", fontSize: 11, fontWeight: 700, cursor: creditLoading ? "wait" : "pointer", borderRadius: 3 }}>
              {creditLoading ? "…" : "↻ Aggiorna"}
            </button>
            <button onClick={() => { caricaSettings(); setShowSettings(true); }}
                    data-testid="resend-settings-open"
                    title="Modifica il limite mensile del piano"
                    style={{ padding: "6px 12px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ⚙ Limite piano
            </button>
          </>
        )}
        {credit && !credit.configured && (
          <div style={{ color: "#c62828", fontSize: 12, fontWeight: 700 }}>
            ⚠ {credit.error_it || "Resend non configurato"}
          </div>
        )}
      </div>

      {/* MODALE IMPOSTAZIONI LIMITE PIANO */}
      {showSettings && (
        <div onClick={() => setShowSettings(false)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: "white", padding: 20, borderRadius: 4, minWidth: 400, maxWidth: 500 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#00838f", marginBottom: 12 }}>⚙ Limiti piano Resend</div>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 12 }}>
              Resend NON espone via API il credito residuo. Imposta qui i limiti del tuo piano
              (es. piano Free = <b>3.000/mese</b> e <b>100/giorno</b>). L&apos;app conterà via API quante mail hai inviato e calcolerà i residui.
            </div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 4 }}>Limite MENSILE (numero massimo di invii al mese)</label>
            <input type="number" value={settingsLimit} onChange={e => setSettingsLimit(e.target.value)}
                   data-testid="resend-settings-limit"
                   style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 14, marginBottom: 12 }} />
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 4 }}>Limite GIORNALIERO (numero massimo di invii al giorno)</label>
            <input type="number" value={settingsDaily} onChange={e => setSettingsDaily(e.target.value)}
                   data-testid="resend-settings-daily"
                   style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 14, marginBottom: 12 }} />
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 4 }}>Etichetta piano (opzionale, solo descrittiva)</label>
            <input type="text" value={settingsLabel} onChange={e => setSettingsLabel(e.target.value)}
                   placeholder="es. Free (3.000/mese · 100/giorno)"
                   data-testid="resend-settings-label"
                   style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 14, marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowSettings(false)}
                      style={{ padding: "8px 16px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                Annulla
              </button>
              <button onClick={salvaSettings} disabled={savingSettings}
                      data-testid="resend-settings-save"
                      style={{ padding: "8px 18px", background: "#00838f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: savingSettings ? "wait" : "pointer", borderRadius: 3 }}>
                {savingSettings ? "Salvataggio…" : "💾 Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <StatCard label="Inviate" value={counts.sent || 0} color="#1b5e20" testid="comlog-sent" />
        <StatCard label="Mock" value={counts.sent_mock || 0} color="#ef6c00" testid="comlog-mock" />
        <StatCard label="Errori" value={counts.error || 0} color="#c62828" testid="comlog-err" />
        {(counts.pending || 0) > 0 && (
          <StatCard label="Non inviate (batch interrotto)" value={counts.pending || 0} color="#bf360c" testid="comlog-pending" />
        )}
        {filtroAlunno && (
          <StatCard label={`Filtrati per "${filtroAlunno}"`} value={itemsFiltered.length} color="#00838f" testid="comlog-filtered" />
        )}
      </div>
      <div style={{ background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} data-testid="comlog-filter-status"
          style={{ padding: 6, border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti gli stati</option>
          <option value="sent">✓ Inviate</option>
          <option value="error">✗ Errori</option>
          <option value="pending">⏸ Non inviate (batch interrotto)</option>
          <option value="sent_mock">⊘ Mock</option>
        </select>
        <input value={filtroBatch} onChange={e => setFiltroBatch(e.target.value)} placeholder="Batch ID..."
          data-testid="comlog-filter-batch"
          style={{ padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", minWidth: 200 }} />
        <input value={filtroAlunno} onChange={e => setFiltroAlunno(e.target.value)} placeholder="🔍 Cerca alunno (cognome, nome, CF, codice)…"
          data-testid="comlog-filter-alunno"
          style={{ padding: 6, border: "1px solid #ccc", fontSize: 12, minWidth: 280, flex: "1 1 280px" }} />
        {filtroAlunno && (
          <button onClick={() => setFiltroAlunno("")} data-testid="comlog-filter-alunno-clear"
            title="Pulisci ricerca"
            style={{ padding: "6px 10px", background: "white", color: "#1f3864", border: "1px solid #1f3864", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>✕</button>
        )}
        <button onClick={carica} data-testid="comlog-refresh"
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>↻ Aggiorna</button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 11, color: "#666" }}>Ultimi
            <input type="number" min="1" max="90" value={verifyDays} onChange={e => setVerifyDays(e.target.value)}
                   data-testid="verify-delivery-days"
                   style={{ width: 50, padding: 4, border: "1px solid #ccc", fontSize: 12, marginLeft: 6, marginRight: 4 }} />
            giorni
          </label>
          <button onClick={verificaConsegna} disabled={verifying || !resendCfg}
                  data-testid="verify-delivery-btn"
                  title={resendCfg ? "Interroga Resend per ogni email e aggiorna lo stato di consegna" : "Resend non configurato"}
                  style={{ padding: "6px 14px", background: verifying ? "#bbb" : "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: (verifying || !resendCfg) ? "not-allowed" : "pointer", borderRadius: 3 }}>
            {verifying ? "Verifica in corso…" : "📬 Verifica consegna"}
          </button>
        </div>
      </div>
      {verifyResult && (
        <div data-testid="verify-delivery-result"
             style={{ marginBottom: 12, padding: 10, background: "#f3e5f5", border: "1px solid #ba68c8", borderRadius: 3, fontSize: 12 }}>
          <div style={{ fontWeight: 800, color: "#4a148c", marginBottom: 6 }}>
            📬 Verifica consegna completata: <b>{verifyResult.checked}</b> email controllate
          </div>
          {verifyResult.counts && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11 }}>
              <span>✅ Consegnate: <b style={{ color: "#1b5e20" }}>{verifyResult.counts.delivered || 0}</b></span>
              <span>⏳ In viaggio: <b style={{ color: "#ef6c00" }}>{verifyResult.counts.sent || 0}</b></span>
              <span>✗ Rimbalzate: <b style={{ color: "#c62828" }}>{verifyResult.counts.bounced || 0}</b></span>
              <span>⚠ Segnalate spam: <b style={{ color: "#bf360c" }}>{verifyResult.counts.complained || 0}</b></span>
              {(verifyResult.counts.not_found || 0) > 0 && <span>❓ Non trovate: <b>{verifyResult.counts.not_found}</b></span>}
              {(verifyResult.counts.api_error || 0) > 0 && <span style={{ color: "#c62828" }}>⚠ Errori API: <b>{verifyResult.counts.api_error}</b></span>}
            </div>
          )}
        </div>
      )}
      <div style={{ background: "white", border: "1px solid #ccc", overflowX: "auto", maxWidth: "100%" }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", minWidth: 1180, fontSize: 11 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ width: 38, textAlign: "center" }}>#</th>
              <th style={{ width: 110, textAlign: "center" }}>DATA</th>
              <th style={{ width: 130 }}>PROTOCOLLO</th>
              <th style={{ width: 80, textAlign: "center" }}>STATO</th>
              <th style={{ width: 110, textAlign: "center" }}>CONSEGNA</th>
              <th style={{ width: 50, textAlign: "center" }} title="Ciclo curriculum (I/P1-P5/M1-M3/U)">CICLO</th>
              <th>ALUNNO</th>
              <th>MODELLO</th>
              <th>MODULO PDF</th>
              <th>DESTINATARI</th>
              <th style={{ width: 60, textAlign: "center" }}>PDF</th>
              <th style={{ width: 90 }}>INVIATA DA</th>
              <th style={{ width: 70, textAlign: "center" }} title="Reinvia questa email agli stessi destinatari">REINVIA</th>
              {isAdmin && <th style={{ width: 36, textAlign: "center" }} title="Elimina riga">✕</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isAdmin ? 14 : 13} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && itemsFiltered.length === 0 && (
              <tr><td colSpan={isAdmin ? 14 : 13} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                {items.length === 0 ? "Nessun invio registrato." : `Nessun risultato per "${filtroAlunno}".`}
              </td></tr>
            )}
            {!loading && itemsFiltered.map((it, idx) => {
              const dests = (it.to_list && it.to_list.length > 0) ? it.to_list : (it.to ? [it.to] : []);
              return (
              <tr key={it._id} onClick={() => setPreview(it)} data-testid={`comlog-row-${it._id}`}
                style={{ cursor: "pointer", borderTop: "1px solid #eee" }}>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 10, color: "#888", fontWeight: 700 }}
                    data-testid={`comlog-num-${it._id}`}>
                  {itemsFiltered.length - idx}
                </td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 10 }}>
                  {it.data_invio || fmt(it.sent_at)}
                </td>
                <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#1f3864", fontSize: 10 }}>{it.protocollo || "—"}</td>
                <td style={{ textAlign: "center" }}>{badge(it.status)}</td>
                <td style={{ textAlign: "center" }}>{deliveryBadge(it.delivery_status)}</td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 800, fontSize: 11, color: "#4a148c" }}
                    data-testid={`comlog-ciclo-${it._id}`}
                    title={it.ciclo_curriculum ? `Ciclo: ${it.ciclo_curriculum}` : "Ciclo non disponibile"}>
                  {it.ciclo_curriculum || <span style={{ color: "#ccc" }}>—</span>}
                </td>
                <td style={{ fontSize: 11 }}>{it.cognome} {it.nome} <span style={{ color: "#888", fontFamily: "Consolas, monospace", marginLeft: 4 }}>#{it.alunno_cod}</span></td>
                <td style={{ color: "#00838f", fontWeight: 700 }}>{it.template_label || it.template_id}</td>
                <td style={{ fontSize: 10, color: "#666" }}>{it.modulo_nome || "—"}</td>
                <td style={{ fontFamily: "Consolas, monospace", fontSize: 10 }}
                    title={dests.join("\n")}
                    data-testid={`comlog-dests-${it._id}`}>
                  {dests.length === 0 ? <span style={{ color: "#bbb" }}>—</span> :
                   dests.length === 1 ? dests[0] :
                   <>
                     <span style={{ background: "#1565c0", color: "white", padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 800, marginRight: 6 }}>
                       ×{dests.length}
                     </span>
                     <span style={{ color: "#1565c0", fontWeight: 600 }}>{dests[0]}</span>
                     <span style={{ color: "#888" }}> + altre {dests.length - 1}</span>
                   </>}
                </td>
                <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                  {it.has_pdf ? (
                    <>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/comunicator/outbox/${it._id}/attachment/0`} target="_blank" rel="noreferrer"
                        data-testid={`comlog-pdf-${it._id}`}
                        title={(it.attachments_names || []).join(", ")}
                        style={{ color: "#c62828", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>📄</a>
                      {(it.attachments_count || 0) > 1 && (
                        <span style={{ marginLeft: 3, padding: "1px 5px", background: "#1565c0", color: "white", fontSize: 9, fontWeight: 700, borderRadius: 8 }}
                              title={(it.attachments_names || []).join(", ")}>×{it.attachments_count}</span>
                      )}
                    </>
                  ) : <span style={{ color: "#ccc" }}>—</span>}
                </td>
                <td style={{ fontSize: 10, color: "#666" }}>{it.sent_by}</td>
                <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                  {(it.provider === "manual" || it.template_id === "nota" || it.template_id === "telefonata" || !((it.to_list && it.to_list.length) || it.to)) ? (
                    <span style={{ color: "#ccc" }}>—</span>
                  ) : (
                    <button onClick={() => reinviaInvio(it)}
                            data-testid={`comlog-resend-${it._id}`}
                            disabled={resendingId === it._id}
                            title="Reinvia questa email agli stessi destinatari (con lo stesso oggetto, testo ed eventuali allegati)"
                            style={{ background: resendingId === it._id ? "#b0bec5" : "white", color: "#1b5e20", border: "1px solid #66bb6a", borderRadius: 3, padding: "1px 7px", fontSize: 11, fontWeight: 800, cursor: resendingId === it._id ? "wait" : "pointer", lineHeight: 1.4 }}>
                      {resendingId === it._id ? "…" : "↻ Reinvia"}
                    </button>
                  )}
                </td>
                {isAdmin && (
                  <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => eliminaInvio(it)}
                            data-testid={`comlog-del-${it._id}`}
                            title="Elimina questa riga di log (l'email reale resta su Resend)"
                            style={{ background: "white", color: "#c62828", border: "1px solid #ef5350", borderRadius: 3, padding: "1px 6px", fontSize: 11, fontWeight: 800, cursor: "pointer", lineHeight: 1 }}>
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#1f3864", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>📬 Dettaglio email</strong>
              <button onClick={() => setPreview(null)} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto", fontSize: 12 }}>
              <div style={{ marginBottom: 8 }}>Stato: {badge(preview.status)}</div>
              {preview.protocollo && <div style={{ marginBottom: 4 }}><b>Protocollo:</b> <code style={{ background: "#fff3cd", padding: "1px 6px", fontWeight: 700 }}>{preview.protocollo}</code> · <b>Data invio:</b> {preview.data_invio || fmt(preview.sent_at)}</div>}
              <div style={{ marginBottom: 4 }}><b>Alunno:</b> {preview.cognome} {preview.nome} (#{preview.alunno_cod}){preview.classe ? ` · classe ${preview.classe}` : ""}</div>
              <div style={{ marginBottom: 4 }}><b>Modello:</b> {preview.template_label} (<code>{preview.template_id}</code>)</div>
              {preview.modulo_nome && <div style={{ marginBottom: 4 }}><b>Modulo PDF:</b> {preview.modulo_nome}</div>}
              {preview.firma_nome && <div style={{ marginBottom: 4 }}><b>Firma:</b> {preview.firma_nome}</div>}
              <div style={{ marginBottom: 4 }}>
                <b>Destinatari ({(preview.to_list || [preview.to]).filter(Boolean).length}):</b>
                {(() => {
                  const dl = (preview.to_list && preview.to_list.length > 0) ? preview.to_list : (preview.to ? [preview.to] : []);
                  if (dl.length <= 1) return <> {dl[0] || "—"}</>;
                  return (
                    <ul data-testid="comlog-preview-destinatari"
                        style={{ margin: "4px 0 0 18px", padding: 0, fontFamily: "Consolas, monospace", fontSize: 11, color: "#1565c0" }}>
                      {dl.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  );
                })()}
              </div>
              <div style={{ marginBottom: 4 }}><b>Mittente:</b> {preview.from_name ? `${preview.from_name} <${preview.from_email}>` : preview.from_email}</div>
              <div style={{ marginBottom: 4 }}><b>Operatore:</b> {preview.sent_by} · <b>Sent at:</b> {fmt(preview.sent_at)}</div>
              {preview.has_pdf && (
                <div style={{ marginBottom: 8, padding: 8, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 3 }}>
                  <div style={{ fontWeight: 700, color: "#1b5e20", marginBottom: 4 }}>
                    📎 Allegati ({preview.attachments_count || 1})
                  </div>
                  {(preview.attachments_names && preview.attachments_names.length > 0
                    ? preview.attachments_names
                    : [preview.pdf_filename]
                  ).filter(Boolean).map((name, idx) => (
                    <div key={idx} style={{ marginLeft: 6, marginTop: 2 }}>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/comunicator/outbox/${preview._id}/attachment/${idx}`}
                         target="_blank" rel="noreferrer"
                         style={{ color: "#1b5e20", fontWeight: 600, textDecoration: "underline", fontSize: 12 }}>
                        Scarica {name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {preview.provider_message_id && <div style={{ marginBottom: 4, fontFamily: "Consolas, monospace", fontSize: 10, color: "#888" }}>Provider id: {preview.provider_message_id}</div>}
              {preview.error && <div style={{ marginBottom: 8, padding: 8, background: "#fff0f0", color: "#c62828", border: "1px solid #fcc", borderRadius: 3 }}>{preview.error}</div>}
              <div style={{ marginBottom: 6, fontSize: 11, color: "#666", fontWeight: 700, marginTop: 12 }}>OGGETTO</div>
              <div style={{ padding: 10, background: "#f5f5f5", fontWeight: 600, marginBottom: 12, fontSize: 13 }}>{preview.subject}</div>
              <div style={{ marginBottom: 6, fontSize: 11, color: "#666", fontWeight: 700 }}>CORPO</div>
              {(() => {
                const b = preview.body || "";
                const isHtml = typeof b === "string" && b.trim().startsWith("<") && b.includes(">");
                return isHtml
                  ? <div className="com-html-preview" style={{ padding: 12, background: "white", border: "1px solid #eee", maxHeight: 360, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b) }} />
                  : <div style={{ padding: 12, background: "#fafafa", border: "1px solid #eee", whiteSpace: "pre-wrap", fontFamily: "Consolas, monospace", fontSize: 11, lineHeight: 1.5 }}>{b}</div>;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== TAB 4: ARCHIVIO FIRME ==============
// eslint-disable-next-line no-unused-vars
function ComunicatorFirmeView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, nome, image_data }
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/comunicator/firme");
      setItems(r.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const onPaste = async (e) => {
    if (!editing) return;
    const item = Array.from(e.clipboardData?.items || []).find(it => it.type.startsWith("image/"));
    if (!item) return;
    const blob = item.getAsFile();
    const reader = new FileReader();
    reader.onload = () => setEditing({ ...editing, image_data: reader.result });
    reader.readAsDataURL(blob);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Seleziona un'immagine JPG/PNG"); return; }
    const reader = new FileReader();
    reader.onload = () => setEditing({ ...editing, image_data: reader.result });
    reader.readAsDataURL(file);
  };

  const salva = async () => {
    if (!editing) return;
    setSaving(true); setErr(null);
    try {
      if (editing.id) {
        await api.put(`/comunicator/firme/${editing.id}`, { nome: editing.nome, image_data: editing.image_data });
      } else {
        await api.post("/comunicator/firme", { nome: editing.nome, image_data: editing.image_data });
      }
      setEditing(null);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const elimina = async (fid) => {
    if (!window.confirm("Eliminare questa firma?")) return;
    try { await api.delete(`/comunicator/firme/${fid}`); await carica(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div style={{ padding: "12px 20px" }} data-testid="com-firme-view">
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: "#00838f", fontSize: 20 }}>✍ Archivio firme</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
            Carica le firme scansionate (JPG/PNG). Puoi incollarle dagli appunti o selezionare un file.
          </div>
        </div>
        <button onClick={() => setEditing({ nome: "", image_data: "" })} data-testid="com-firme-new"
          style={{ background: "#00838f", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          + Nuova firma
        </button>
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      <div style={{ background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 12 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ width: 280 }}>NOME</th>
              <th>FIRMA</th>
              <th style={{ width: 130, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 24, textAlign: "center", color: "#888" }}>Nessuna firma archiviata. Aggiungi la prima con "+ Nuova firma".</td></tr>
            )}
            {!loading && items.map(f => (
              <tr key={f.id} data-testid={`com-firma-row-${f.id}`} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ fontWeight: 700, color: "#1f3864" }}>{f.nome}</td>
                <td style={{ padding: 8 }}>
                  {f.image_data
                    ? <img src={f.image_data} alt={f.nome} style={{ maxHeight: 80, maxWidth: 360, border: "1px solid #ddd", background: "white" }} />
                    : <span style={{ color: "#888", fontStyle: "italic" }}>(nessuna immagine)</span>}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => setEditing({ id: f.id, nome: f.nome, image_data: f.image_data })} data-testid={`com-firma-edit-${f.id}`}
                    style={{ background: "#1565c0", color: "white", border: "none", padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏ Modifica</button>
                  <button onClick={() => elimina(f.id)} data-testid={`com-firma-del-${f.id}`}
                    style={{ background: "#c62828", color: "white", border: "none", padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onPaste={onPaste} data-testid="com-firma-modal"
            style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#00838f", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{editing.id ? `✏ Modifica firma: ${editing.nome}` : "+ Nuova firma"}</strong>
              <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto" }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOME *</label>
              <input value={editing.nome} onChange={e => setEditing({ ...editing, nome: e.target.value })}
                placeholder="es: Direttrice — sig.ra Rossi" data-testid="com-firma-nome"
                style={{ width: "100%", padding: 8, border: "2px solid #00838f", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 16 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>IMMAGINE FIRMA (JPG/PNG) *</label>
              <div data-testid="com-firma-cella"
                style={{
                  border: "2px dashed #00838f",
                  borderRadius: 4,
                  background: "#f7fafa",
                  minHeight: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 12,
                  marginBottom: 10,
                  textAlign: "center",
                }}>
                {editing.image_data
                  ? <img src={editing.image_data} alt="preview" style={{ maxHeight: 220, maxWidth: "100%", background: "white", border: "1px solid #ddd" }} />
                  : <div style={{ color: "#00838f", fontSize: 12 }}>
                      📋 Incolla un'immagine qui (Ctrl+V) oppure usa il pulsante sotto
                    </div>}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ background: "#1565c0", color: "white", padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  📁 Seleziona file…
                  <input type="file" accept="image/*" onChange={onFile} data-testid="com-firma-file" style={{ display: "none" }} />
                </label>
                {editing.image_data && (
                  <button onClick={() => setEditing({ ...editing, image_data: "" })} data-testid="com-firma-clear"
                    style={{ background: "#c62828", color: "white", padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3, border: "none" }}>
                    ✕ Rimuovi immagine
                  </button>
                )}
                <span style={{ fontSize: 10, color: "#888" }}>Max ~2MB · formato JPG/PNG</span>
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setEditing(null)}
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>Annulla</button>
              <button onClick={salva} disabled={saving || !editing.nome || !editing.image_data}
                data-testid="com-firma-save"
                style={{ background: (saving || !editing.nome || !editing.image_data) ? "#bbb" : "#00838f", color: "white", border: "none", padding: "8px 24px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                {saving ? "Salvataggio..." : "💾 Salva firma"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============== TAB: ALLEGATI DOCX (upload .docx con placeholder) ==============
export function ComunicatorAllegatiDocxView() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState({ document: [], row: [], note: "" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewName, setPreviewName] = useState("");
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/comunicator/allegati-docx"),
        api.get("/comunicator/allegati-docx/placeholders"),
      ]);
      setItems(r1.data.items || []);
      setPlaceholders(r2.data || { document: [], row: [], note: "" });
    } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/comunicator/allegati-docx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sostituisci = async (id, file) => {
    if (!file) return;
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put(`/comunicator/allegati-docx/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || e.message || "Errore"); }
    finally {
      setUploading(false); setReplacingId(null);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  };

  const elimina = async (id, nome) => {
    if (!confirm(`Eliminare l'allegato DOCX "${nome}"?`)) return;
    try { await api.delete(`/comunicator/allegati-docx/${id}`); await carica(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const download = async (id, filename) => {
    try {
      const r = await api.get(`/comunicator/allegati-docx/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "allegato.docx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr(e.response?.data?.detail || "Errore download"); }
  };

  const anteprima = async (it) => {
    setPreviewLoading(true); setErr(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      const r = await api.post(`/comunicator/allegati-docx/${it.id}/anteprima-pdf`, {}, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
      setPreviewName(it.filename || it.nome);
    } catch (e) {
      let detail = "";
      try { detail = await e.response?.data?.text?.(); } catch (e2) { /* ignore */ }
      setErr(detail || e.response?.data?.detail || e.message || "Errore anteprima");
    } finally { setPreviewLoading(false); }
  };

  const [copiedPh, setCopiedPh] = useState(null);
  const copyToClipboard = async (txt) => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(txt);
        ok = true;
      }
    } catch (e) { /* fallback sotto */ }
    if (!ok) {
      // Fallback: textarea temporanea + execCommand (funziona anche su HTTP/iframe)
      try {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) { /* ignore */ }
    }
    if (ok) {
      setCopiedPh(txt);
      setTimeout(() => setCopiedPh((v) => v === txt ? null : v), 1500);
    } else {
      // Ultima fallback: mostra un prompt con il testo da copiare manualmente
      window.prompt("Copia manualmente il placeholder (CTRL+C / CMD+C):", txt);
    }
  };

  return (
    <div style={{ padding: "12px 20px" }} data-testid="com-allegati-docx-view">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, color: "#00838f", fontSize: 20 }}>📎 Allegati DOCX</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            Carica i file Word <b>.docx</b> già pronti con i placeholder. Al momento dell'invio, il sistema sostituisce i placeholder, ripete le righe della tabella per ogni movimento e converte in PDF.
          </div>
        </div>
        <div>
          <input type="file" accept=".docx" ref={fileInputRef} style={{ display: "none" }}
            onChange={(e) => upload(e.target.files?.[0])} data-testid="com-docx-upload-input" />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            data-testid="com-docx-upload-btn"
            style={{ background: uploading ? "#bbb" : "#00838f", color: "white", border: "none", padding: "10px 20px", fontSize: 13, fontWeight: 800, cursor: uploading ? "wait" : "pointer", borderRadius: 3 }}>
            {uploading ? "⏳ Caricamento..." : "📤 Carica DOCX"}
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="com-docx-err" style={{ background: "#ffebee", color: "#c62828", padding: 10, marginBottom: 10, fontSize: 12, borderRadius: 3, border: "1px solid #c62828" }}>{err}</div>
      )}

      {/* Sezione placeholder disponibili */}
      <div style={{ background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 5, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#006064", marginBottom: 6 }}>
          📋 PLACEHOLDER DA INSERIRE NEL TUO DOCX (clicca per copiare)
        </div>
        <div style={{ fontSize: 11, color: "#006064", marginBottom: 8, lineHeight: 1.5 }}>
          {placeholders.note || "Inserisci i seguenti placeholder nel tuo file Word prima di caricarlo."}
          <br/>
          <b>💡 Due modi per inserire i placeholder in Word:</b>
          <br/>
          <b>1) Testo letterale</b> (più semplice): digita direttamente <code style={{ background: "white", padding: "0 4px", borderRadius: 2 }}>{"{nome}"}</code> nel documento.
          <br/>
          <b>2) MERGEFIELD nativo</b> (campo Stampa Unione di Word): vai su <i>Inserisci → Parti rapide → Campo… → MergeField</i> e inserisci come nome campo <code style={{ background: "white", padding: "0 4px", borderRadius: 2 }}>nome</code> (senza graffe). Funzionano entrambi.
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: "#006064", marginBottom: 4 }}>
          🔹 Placeholder di DOCUMENTO (clicca per copiare → incolla in Word con <b>CTRL+V</b>):
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
          {(placeholders.document || []).map(ph => (
            <button key={ph} onClick={() => copyToClipboard(ph)}
              title={copiedPh === ph ? "Copiato!" : "Clicca per copiare"}
              data-testid={`com-ph-doc-${ph}`}
              style={{
                padding: "3px 8px",
                background: copiedPh === ph ? "#43a047" : "white",
                color: copiedPh === ph ? "white" : "#006064",
                border: "1px solid #00838f",
                fontFamily: "Consolas, monospace",
                fontSize: 11, fontWeight: 700,
                cursor: "pointer", borderRadius: 2,
                transition: "background 0.2s, color 0.2s",
              }}>
              {copiedPh === ph ? "✓ Copiato" : ph}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: "#bf360c", marginBottom: 4 }}>
          📊 Placeholder di RIGA (clicca per copiare; mettili in UNA SOLA riga della tabella movimenti — verrà duplicata per ogni movimento):
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(placeholders.row || []).map(ph => (
            <button key={ph} onClick={() => copyToClipboard(ph)}
              title={copiedPh === ph ? "Copiato!" : "Clicca per copiare"}
              data-testid={`com-ph-row-${ph}`}
              style={{
                padding: "3px 8px",
                background: copiedPh === ph ? "#43a047" : "#fff3e0",
                color: copiedPh === ph ? "white" : "#bf360c",
                border: "1px solid #bf360c",
                fontFamily: "Consolas, monospace",
                fontSize: 11, fontWeight: 700,
                cursor: "pointer", borderRadius: 2,
                transition: "background 0.2s, color 0.2s",
              }}>
              {copiedPh === ph ? "✓ Copiato" : ph}
            </button>
          ))}
        </div>
      </div>

      {/* Lista allegati */}
      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888", border: "2px dashed #ccc", borderRadius: 5 }}>
          Nessun allegato DOCX caricato. Clicca su <b>📤 Carica DOCX</b> per iniziare.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="com-docx-table">
          <thead>
            <tr style={{ background: "#00838f", color: "white" }}>
              <th style={{ padding: 8, textAlign: "left", width: 80 }}>Etichetta</th>
              <th style={{ padding: 8, textAlign: "left" }}>Nome file</th>
              <th style={{ padding: 8, textAlign: "right", width: 90 }}>Dimensione</th>
              <th style={{ padding: 8, textAlign: "left", width: 160 }}>Aggiornato</th>
              <th style={{ padding: 8, textAlign: "center", width: 360 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} style={{ borderBottom: "1px solid #e0e0e0" }} data-testid={`com-docx-row-${it.id}`}>
                <td style={{ padding: 6, fontFamily: "Consolas, monospace", fontWeight: 700, color: "#1f3864" }}>{it.nome}</td>
                <td style={{ padding: 6 }}>{it.filename}</td>
                <td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{(it.size / 1024).toFixed(1)} KB</td>
                <td style={{ padding: 6, fontSize: 11, color: "#666" }}>{it.updated_at ? new Date(it.updated_at).toLocaleString("it-IT") : "-"}</td>
                <td style={{ padding: 6 }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "center", alignItems: "center", flexWrap: "nowrap" }}>
                    <button onClick={() => anteprima(it)} disabled={previewLoading}
                      data-testid={`com-docx-preview-${it.id}`}
                      style={{ background: "#1565c0", color: "white", border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap", minWidth: 90 }}>
                      {previewLoading ? "..." : "🔍 Anteprima"}
                    </button>
                    <button onClick={() => download(it.id, it.filename)}
                      data-testid={`com-docx-dl-${it.id}`}
                      style={{ background: "#f57c00", color: "white", border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap", minWidth: 80 }}>
                      📥 Scarica
                    </button>
                    <button onClick={() => { setReplacingId(it.id); replaceInputRef.current?.click(); }}
                      data-testid={`com-docx-replace-${it.id}`}
                      style={{ background: "#7b1fa2", color: "white", border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap", minWidth: 90 }}>
                      🔄 Sostituisci
                    </button>
                    <button onClick={() => elimina(it.id, it.nome)} data-testid={`com-docx-del-${it.id}`}
                      style={{ background: "#c62828", color: "white", border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap", minWidth: 70 }}>
                      🗑 Elimina
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Input file nascosto per "Sostituisci" */}
      <input type="file" accept=".docx" ref={replaceInputRef} style={{ display: "none" }}
        onChange={(e) => { if (replacingId) sostituisci(replacingId, e.target.files?.[0]); }} />

      {/* Modale anteprima PDF */}
      {previewUrl && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div style={{ background: "white", width: "100%", maxWidth: 1000, height: "92vh", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ background: "#1565c0", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>🔍 Anteprima PDF: {previewName}</strong>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20 }}>✕</button>
            </div>
            <iframe title="Anteprima PDF" src={previewUrl} style={{ flex: 1, border: "none" }} />
            <div style={{ padding: "8px 14px", background: "#f5f5f5", fontSize: 11, color: "#666", textAlign: "center", borderTop: "1px solid #ddd" }}>
              Anteprima generata con dati demo (primo alunno disponibile + 3 movimenti di esempio). Al momento dell'invio, ogni alunno avrà i suoi dati reali.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============== TAB: MODULI ALLEGATI (HTML rich-text) ==============
function ComunicatorModuliView() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // editing = { id?, nome, body_html, header_html, footer_html, page_size, margin }
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [section, setSection] = useState("body"); // body | header | footer | page
  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/comunicator/moduli");
      setItems(r.data.items || []);
      setPlaceholders(r.data.placeholders || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const apriEdit = async (mid) => {
    try {
      const r = await api.get(`/comunicator/moduli/${mid}`);
      setEditing({
        id: r.data.id,
        nome: r.data.nome || "",
        body_html: r.data.body_html || "",
        header_html: r.data.header_html || "",
        footer_html: r.data.footer_html || "",
        page_size: r.data.page_size || "A4",
        margin: r.data.margin || "2cm",
      });
      setSection("body");
    } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento modulo"); }
  };

  const salva = async () => {
    if (!editing) return;
    if (!editing.body_html) { setErr("Il corpo del modulo è obbligatorio"); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        body_html: editing.body_html,
        header_html: editing.header_html || "",
        footer_html: editing.footer_html || "",
        page_size: editing.page_size || "A4",
        margin: editing.margin || "2cm",
      };
      if (editing.id) await api.put(`/comunicator/moduli/${editing.id}`, payload);
      else await api.post("/comunicator/moduli", payload);
      setEditing(null);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const elimina = async (mid) => {
    if (!window.confirm("Eliminare questo modulo?")) return;
    try { await api.delete(`/comunicator/moduli/${mid}`); await carica(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const insertPh = (ph) => {
    if (!editing) return;
    const r = section === "header" ? headerRef.current : section === "footer" ? footerRef.current : bodyRef.current;
    if (r && r.insertText) { r.insertText(ph); return; }
    const k = section === "header" ? "header_html" : section === "footer" ? "footer_html" : "body_html";
    setEditing({ ...editing, [k]: (editing[k] || "") + ph });
  };

  const docxInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const importDocx = async (file) => {
    if (!file) return;
    setImporting(true); setImportMsg(null); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/comunicator/moduli/import-docx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const html = r.data?.html || "";
      // Sostituisce il corpo con quello importato; switch alla sezione body
      setEditing({ ...editing, body_html: html });
      setSection("body");
      const warns = (r.data?.messages || []).filter(m => /warning|unrecognised/i.test(m));
      setImportMsg(`✓ DOCX importato (${Math.round((r.data?.size || html.length) / 1024)} KB)${warns.length ? ` · ${warns.length} avvisi conversione` : ""}`);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore importazione DOCX");
    } finally { setImporting(false); if (docxInputRef.current) docxInputRef.current.value = ""; }
  };

  const anteprima = async () => {
    if (!editing) return;
    setPreviewLoading(true); setErr(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      // Salva prima così abbiamo un modulo_id valido
      let mid = editing.id;
      const payload = {
        body_html: editing.body_html,
        header_html: editing.header_html || "", footer_html: editing.footer_html || "",
        page_size: editing.page_size || "A4", margin: editing.margin || "2cm",
      };
      if (mid) { await api.put(`/comunicator/moduli/${mid}`, payload); }
      else {
        const cr = await api.post("/comunicator/moduli", payload);
        mid = cr.data.id;
        setEditing({ ...editing, id: mid, nome: cr.data.nome });
      }
      // Anteprima con dati alunno dummy/primo alunno disponibile dell'anno corrente
      const anniRes = await api.get("/anni-disponibili");
      const anno = anniRes.data?.anni?.[0] || new Date().getFullYear();
      const al = await api.get("/alunni", { params: { anno, limit: 1, page: 1, sort_by: "Cog", sort_dir: "asc" } });
      const first = (al.data?.data || al.data?.items || [])[0];
      if (!first) { setErr("Nessun alunno disponibile per anteprima"); setPreviewLoading(false); return; }
      const r = await api.post("/comunicator/anteprima-pdf", {
        modulo_id: mid, cod: first.Cod, anno,
        movimenti: [
          { id: "demo1", num: 1001, competenza: anno, data_doc: "01/09/" + anno, data_scadenza: "30/09/" + anno, data_pagamento: null, causale: "ISCR", descrizione: "Iscrizione (esempio)", importo: 500, modalita: "Bonifico", pagato: false },
          { id: "demo2", num: 1002, competenza: anno, data_doc: "01/10/" + anno, data_scadenza: "31/10/" + anno, data_pagamento: "05/11/" + anno, causale: "RETTA", descrizione: "Retta 1^ rata (esempio)", importo: 1120, modalita: "Bonifico", pagato: true },
        ],
      }, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore anteprima");
    } finally { setPreviewLoading(false); }
  };

  return (
    <div style={{ padding: "12px 20px" }} data-testid="com-moduli-view">
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: "#00838f", fontSize: 20 }}>📎 Allegati PDF</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
            Editor rich-text con placeholder. Il sistema genera un PDF per ogni alunno e lo allega all'email. Etichetta assegnata automaticamente: <code>A001</code>, <code>A002</code>, ...
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setEditing({ body_html: "", header_html: "", footer_html: "", page_size: "A4", margin: "2cm" }); setSection("body"); }} data-testid="com-moduli-new"
            style={{ background: "#00838f", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            + Nuovo allegato
          </button>
        </div>
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      <div style={{ background: "white", border: "1px solid #ccc", marginBottom: 14 }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 12 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ width: 140 }}>ETICHETTA</th>
              <th style={{ width: 130, textAlign: "center" }}>DIMENSIONE</th>
              <th style={{ width: 110, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 24, textAlign: "center", color: "#888" }}>Nessun allegato. Crea il primo con "+ Nuovo allegato".</td></tr>
            )}
            {!loading && items.map(m => (
              <tr key={m.id} data-testid={`com-modulo-row-${m.id}`} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ fontFamily: "Consolas, monospace", color: "#00838f", fontWeight: 700 }}>{m.nome}</td>
                <td style={{ fontSize: 11, color: "#666", textAlign: "center" }}>{Math.round((m.size || 0) / 1024)} KB</td>
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => apriEdit(m.id)} data-testid={`com-modulo-edit-${m.id}`}
                    style={{ background: "#1565c0", color: "white", border: "none", padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏ Modifica</button>
                  <button onClick={() => elimina(m.id)} data-testid={`com-modulo-del-${m.id}`}
                    style={{ background: "#c62828", color: "white", border: "none", padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div data-testid="com-modulo-modal"
            style={{ background: "white", width: "100%", maxWidth: 1200, borderRadius: 6, overflow: "hidden", maxHeight: "94vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#00838f", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{editing.id ? `✏ Modifica allegato: ${editing.nome || "(senza nome)"}` : "+ Nuovo allegato PDF"}</strong>
              <button onClick={() => { setEditing(null); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
                style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto", flex: 1, display: "grid", gridTemplateColumns: previewUrl ? "1fr 1fr" : "1fr", gap: 16 }}>
              <div>
                {editing.id ? (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ETICHETTA</label>
                    <input value={editing.nome || ""} readOnly data-testid="com-modulo-nome-ro"
                      style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 13, fontWeight: 700, fontFamily: "Consolas, monospace", background: "#f5f5f5", color: "#1f3864" }} />
                  </div>
                ) : (
                  <div style={{ marginBottom: 12, padding: 10, background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 12, color: "#006064" }}>
                    L'<b>etichetta</b> verrà assegnata automaticamente al salvataggio (es. <code>A001</code>, <code>A002</code>, ...)
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, padding: "8px 10px", background: "#fffde7", border: "1px solid #ffeb3b", borderRadius: 3 }}>
                  <input type="file" accept=".docx" ref={docxInputRef} style={{ display: "none" }}
                    onChange={(e) => importDocx(e.target.files?.[0])} data-testid="com-modulo-docx-input" />
                  <button type="button" onClick={() => docxInputRef.current?.click()} disabled={importing}
                    data-testid="com-modulo-docx-btn"
                    style={{ background: importing ? "#bbb" : "#f57f17", color: "white", border: "none", padding: "7px 14px", fontSize: 12, fontWeight: 800, cursor: importing ? "wait" : "pointer", borderRadius: 3, whiteSpace: "nowrap" }}>
                    {importing ? "⏳ Importazione..." : "📥 Importa da DOCX (Word)"}
                  </button>
                  <span style={{ fontSize: 11, color: "#5d4037", lineHeight: 1.35 }}>
                    Carica un file <b>.docx</b> per popolare automaticamente il <b>Corpo</b> dell'allegato.
                    {" "}Vengono preservate tabelle, grassetto/corsivo e immagini. <b>Sovrascrive</b> il corpo attuale.
                  </span>
                  {importMsg && <span style={{ fontSize: 11, color: "#2e7d32", fontWeight: 700, marginLeft: "auto" }} data-testid="com-modulo-docx-ok">{importMsg}</span>}
                </div>

                <div style={{ display: "flex", gap: 2, borderBottom: "2px solid #00838f", marginBottom: 8 }}>
                  {[
                    { k: "body", l: "📄 Corpo" },
                    { k: "header", l: "↑ Intestazione" },
                    { k: "footer", l: "↓ Piè di pagina" },
                    { k: "page", l: "⚙ Pagina" },
                  ].map(s => (
                    <button key={s.k} onClick={() => setSection(s.k)} data-testid={`com-modulo-sec-${s.k}`}
                      style={{
                        padding: "6px 14px",
                        background: section === s.k ? "#00838f" : "#f5fafa",
                        color: section === s.k ? "white" : "#00838f",
                        border: "1px solid #00838f", borderBottom: section === s.k ? "1px solid #00838f" : "1px solid transparent",
                        fontSize: 11, fontWeight: 700, cursor: "pointer", marginBottom: -2,
                      }}>{s.l}</button>
                  ))}
                </div>

                {section === "body" && (
                  <RichEditor ref={bodyRef} value={editing.body_html} onChange={(html) => setEditing({ ...editing, body_html: html })}
                    placeholder="Scrivi il contenuto del modulo. Usa la barra strumenti per formattare." height={360}
                    testid="com-modulo-body" />
                )}
                {section === "header" && (
                  <>
                    <div style={{ marginBottom: 6, padding: 8, background: "#fff8e1", border: "1px solid #ffb74d", fontSize: 11, color: "#bf360c", borderRadius: 3 }}>
                      ℹ Se questo campo è vuoto, viene usata l'<b>intestazione globale</b> condivisa fra tutti gli allegati. Compilalo solo per personalizzare questo singolo allegato.
                    </div>
                    <RichEditor ref={headerRef} value={editing.header_html} onChange={(html) => setEditing({ ...editing, header_html: html })}
                      placeholder="Intestazione specifica di questo allegato (sovrascrive quella globale). Lascia vuoto per ereditare." height={200}
                      testid="com-modulo-header" />
                  </>
                )}
                {section === "footer" && (
                  <>
                    <div style={{ marginBottom: 6, padding: 8, background: "#fff8e1", border: "1px solid #ffb74d", fontSize: 11, color: "#bf360c", borderRadius: 3 }}>
                      ℹ Se questo campo è vuoto, viene usato il <b>piè di pagina globale</b> condiviso fra tutti gli allegati. Compilalo solo per personalizzare questo singolo allegato.
                    </div>
                    <RichEditor ref={footerRef} value={editing.footer_html} onChange={(html) => setEditing({ ...editing, footer_html: html })}
                      placeholder="Piè di pagina specifico di questo allegato (sovrascrive quello globale). Lascia vuoto per ereditare." height={200}
                      testid="com-modulo-footer" />
                  </>
                )}
                {section === "page" && (
                  <div style={{ padding: 12, background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 3 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>FORMATO PAGINA</label>
                    <select value={editing.page_size} onChange={e => setEditing({ ...editing, page_size: e.target.value })}
                      style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, marginBottom: 12 }}>
                      <option value="A4">A4</option>
                      <option value="A4 landscape">A4 orizzontale</option>
                      <option value="Letter">Letter</option>
                      <option value="Legal">Legal</option>
                    </select>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>MARGINI</label>
                    <input value={editing.margin} onChange={e => setEditing({ ...editing, margin: e.target.value })}
                      placeholder='es: "2cm" oppure "2cm 1.5cm" (top/bot, left/right)'
                      style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace" }} />
                    <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
                      Sintassi CSS: <code>2cm</code> oppure <code>2cm 1.5cm 2cm 1.5cm</code> (top, right, bottom, left).
                    </div>
                  </div>
                )}
              </div>

              {previewUrl && (
                <div>
                  <div style={{ fontSize: 11, color: "#666", fontWeight: 700, marginBottom: 4 }}>ANTEPRIMA PDF</div>
                  <iframe title="Anteprima PDF" src={previewUrl}
                    style={{ width: "100%", height: 580, border: "1px solid #ccc", borderRadius: 3 }} />
                </div>
              )}
            </div>
            {/* PLACEHOLDER bar — sempre visibile, fuori dallo scroll */}
            <div style={{ padding: "10px 18px", background: "#e0f7fa", borderTop: "1px solid #4dd0e1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#006064", marginBottom: 6 }}>📋 PLACEHOLDER (clicca per inserire nella sezione attiva: <b>{section.toUpperCase()}</b>){section === "page" && " — disabilitati: la sezione Pagina non accetta placeholder"}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 90, overflowY: "auto" }}>
                {placeholders.map(ph => (
                  <button key={ph} onClick={() => insertPh(ph)} disabled={section === "page"}
                    style={{ padding: "3px 8px", background: section === "page" ? "#eee" : "white", color: "#006064", border: "1px solid #00838f", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, cursor: section === "page" ? "not-allowed" : "pointer", borderRadius: 2 }}>{ph}</button>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: "#006064", lineHeight: 1.4 }}>
                <b>{"{tabella_addebiti}"}</b> = tabella HTML completa · <b>{"{firma}"}</b> = firma immagine
                <br/>
                <b style={{ color:"#bf360c" }}>📊 PLACEHOLDER DI RIGA</b> (per tabelle personalizzate): disegna una <b>singola riga dati</b> con <b>{"{scadenza}"}</b>, <b>{"{descrizione}"}</b>, <b>{"{importo}"}</b>, <b>{"{data_pagamento}"}</b>, <b>{"{modalita}"}</b>, <b>{"{causale}"}</b> — il sistema la <i>duplicherà automaticamente per ogni movimento</i> mantenendo stili e bordi della tabella.
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <button onClick={anteprima} disabled={previewLoading || !editing.body_html} data-testid="com-modulo-preview"
                style={{ background: (previewLoading || !editing.body_html) ? "#bbb" : "#1565c0", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: previewLoading ? "wait" : "pointer", borderRadius: 3, marginRight: 8 }}>
                {previewLoading ? "Generazione..." : "🔍 Anteprima PDF"}
              </button>
              {previewUrl && (
                <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  data-testid="com-modulo-preview-close"
                  style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  ✕ Chiudi anteprima
                </button>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setEditing(null); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
                  style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>Annulla</button>
                <button onClick={salva} disabled={saving || !editing.body_html} data-testid="com-modulo-save"
                  style={{ background: (saving || !editing.body_html) ? "#bbb" : "#00838f", color: "white", border: "none", padding: "8px 24px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                  {saving ? "Salvataggio..." : "💾 Salva allegato"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// GlobalSettingsModal — Impostazioni globali Comunicator (header/footer/page + coordinate pagamento)
// ============================================================================
function GlobalSettingsModal({ onClose }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [sec, setSec] = useState("header"); // header | footer | page | pagamento
  const [placeholders, setPlaceholders] = useState([]);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const pagamentoRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get("/comunicator/layout-globale"),
          api.get("/comunicator/moduli"),
        ]);
        setData({
          header_html: r1.data.header_html || "",
          footer_html: r1.data.footer_html || "",
          page_size: r1.data.page_size || "A4",
          margin: r1.data.margin || "2cm",
          coordinate_pagamento: r1.data.coordinate_pagamento || "",
        });
        setPlaceholders(r2.data.placeholders || []);
      } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento"); }
    })();
  }, []);

  const salva = async () => {
    setSaving(true); setErr(null);
    try {
      await api.put("/comunicator/layout-globale", data);
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const insertPh = (ph) => {
    const ref = sec === "header" ? headerRef : sec === "footer" ? footerRef : sec === "pagamento" ? pagamentoRef : null;
    if (!ref?.current) return;
    ref.current.insertText(ph);
  };

  if (!data) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div data-testid="com-global-modal"
        style={{ background: "white", width: "100%", maxWidth: 1000, borderRadius: 6, overflow: "hidden", maxHeight: "94vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#006064", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>🌐 Impostazioni globali Comunicator</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>
        {err && <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err}</div>}
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ marginBottom: 12, padding: 10, background: "#e0f7fa", border: "1px solid #4dd0e1", borderRadius: 3, fontSize: 12, color: "#006064" }}>
            Impostazioni condivise da <b>tutti gli allegati PDF</b> e <b>tutti i template email</b>. Header/Piè di pagina si applicano se gli allegati non hanno valori specifici. <b>Coordinate di pagamento</b> sono riferibili nei testi col placeholder <code>{"{coordinate_pagamento}"}</code>.
          </div>
          <div style={{ display: "flex", gap: 2, borderBottom: "2px solid #006064", marginBottom: 8, flexWrap: "wrap" }}>
            {[
              { k: "header", l: "↑ Intestazione" },
              { k: "footer", l: "↓ Piè di pagina" },
              { k: "pagamento", l: "💳 Coordinate di pagamento" },
              { k: "page", l: "⚙ Pagina (default)" },
            ].map(s => (
              <button key={s.k} onClick={() => setSec(s.k)} data-testid={`com-glob-sec-${s.k}`}
                style={{
                  padding: "6px 14px",
                  background: sec === s.k ? "#006064" : "#f5fafa",
                  color: sec === s.k ? "white" : "#006064",
                  border: "1px solid #006064", borderBottom: sec === s.k ? "1px solid #006064" : "1px solid transparent",
                  fontSize: 11, fontWeight: 700, cursor: "pointer", marginBottom: -2,
                }}>{s.l}</button>
            ))}
          </div>
          {sec === "header" && (
            <RichEditor ref={headerRef} value={data.header_html} onChange={(html) => setData({ ...data, header_html: html })}
              placeholder="Intestazione comune a tutti gli allegati (es. logo, nome scuola)." height={300}
              testid="com-glob-header" />
          )}
          {sec === "footer" && (
            <RichEditor ref={footerRef} value={data.footer_html} onChange={(html) => setData({ ...data, footer_html: html })}
              placeholder="Piè di pagina comune a tutti gli allegati (es. recapiti, numerazione pagine)." height={300}
              testid="com-glob-footer" />
          )}
          {sec === "pagamento" && (
            <>
              <div style={{ marginBottom: 8, padding: 8, background: "#fff8e1", border: "1px solid #ffb74d", borderRadius: 3, fontSize: 11, color: "#6d4c41" }}>
                💡 Il testo qui sotto sarà disponibile nei template email e negli allegati PDF tramite il placeholder <b><code>{"{coordinate_pagamento}"}</code></b>. Puoi includere IBAN, intestatario, banca, BIC/SWIFT, causale standard, ecc. Formattazione HTML completa supportata.
              </div>
              <RichEditor ref={pagamentoRef} value={data.coordinate_pagamento} onChange={(html) => setData({ ...data, coordinate_pagamento: html })}
                placeholder={"Esempio:\nIBAN: IT60 X054 2811 1010 0000 0123 456\nIntestatario: Scuola Canossiana\nBanca: BPM\nCausale: Saldo addebiti {anno_scolastico} - {cognome} {nome}"}
                height={320}
                testid="com-glob-pagamento" />
            </>
          )}
          {sec === "page" && (
            <div style={{ padding: 12, background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 3 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>FORMATO PAGINA DI DEFAULT</label>
              <select value={data.page_size} onChange={e => setData({ ...data, page_size: e.target.value })}
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, marginBottom: 12 }}>
                <option value="A4">A4</option>
                <option value="A4 landscape">A4 orizzontale</option>
                <option value="Letter">Letter</option>
                <option value="Legal">Legal</option>
              </select>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>MARGINI DI DEFAULT</label>
              <input value={data.margin} onChange={e => setData({ ...data, margin: e.target.value })}
                placeholder='es: "2cm"'
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace" }} />
            </div>
          )}
        </div>
        {sec !== "page" && (
          <div style={{ padding: "10px 18px", background: "#e0f7fa", borderTop: "1px solid #4dd0e1" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#006064", marginBottom: 6 }}>📋 PLACEHOLDER (clicca per inserire in <b>{sec.toUpperCase()}</b>)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 90, overflowY: "auto" }}>
              {placeholders.map(ph => (
                <button key={ph} onClick={() => insertPh(ph)}
                  style={{ padding: "3px 8px", background: "white", color: "#006064", border: "1px solid #00838f", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>{ph}</button>
              ))}
            </div>
          </div>
        )}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose}
            style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="com-glob-save"
            style={{ background: saving ? "#bbb" : "#006064", color: "white", border: "none", padding: "8px 24px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
            {saving ? "Salvataggio..." : "💾 Salva impostazioni"}
          </button>
        </div>
      </div>
    </div>
  );
}
// ============================================================================
function MultiSelectChips({ label, options, selected, onChange, testid, width = 160 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (v) => {
    const set = new Set(selected || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange(Array.from(set));
  };
  const allOn = options.length > 0 && options.every(o => (selected || []).includes(o));
  const toggleAll = () => onChange(allOn ? [] : [...options]);
  const summary = !selected || selected.length === 0
    ? "Tutte"
    : selected.length === options.length
      ? "Tutte"
      : `${selected.length} sel.`;
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }} data-testid={testid}>
      <label style={{ fontSize: 11, color: "#666", fontWeight: 700, marginRight: 4 }}>{label}:</label>
      <button type="button" onClick={() => setOpen(o => !o)}
        data-testid={`${testid}-btn`}
        style={{ padding: "5px 8px", border: "1px solid #ccc", background: "white", fontSize: 11, cursor: "pointer", minWidth: width, textAlign: "left", display: "inline-flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: (selected || []).length > 0 && (selected || []).length < options.length ? 700 : 400, color: (selected || []).length > 0 && (selected || []).length < options.length ? "#0d47a1" : "#444" }}>
          {summary}
        </span>
        <span style={{ color: "#888", fontSize: 9 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, zIndex: 50, background: "white", border: "1px solid #888", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: 3, minWidth: width + 40, maxHeight: 280, overflow: "auto", padding: 4 }}>
          {options.length === 0 ? (
            <div style={{ padding: 8, fontSize: 11, color: "#888" }}>Nessuna opzione disponibile</div>
          ) : (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", cursor: "pointer", borderBottom: "1px solid #eee", fontSize: 11, fontWeight: 700, color: "#0d47a1" }}>
                <input type="checkbox" checked={allOn} onChange={toggleAll} data-testid={`${testid}-all`} />
                {allOn ? "Deseleziona tutto" : "Seleziona tutto"}
              </label>
              {options.map(o => (
                <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 6px", cursor: "pointer", fontSize: 11 }}>
                  <input type="checkbox" checked={(selected || []).includes(o)} onChange={() => toggle(o)}
                    data-testid={`${testid}-opt-${o}`} />
                  <span>{o}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* Card di anteprima per un genitore (idx 1|2) */
function GenitoreCard({ idx, data, highlight }) {
  const prefix = `genitore_${idx}_`;
  const cog = data?.[`${prefix}cognome`] || "";
  const nom = data?.[`${prefix}nome`] || "";
  const ana = data?.[`${prefix}anagrafica_completa`] || "";
  const ind = data?.[`${prefix}indirizzo`] || "";
  const cap_citta = data?.[`${prefix}cap_citta`] || "";
  const nato_a = data?.[`${prefix}nato_a`] || "";
  const nato_il = data?.[`${prefix}nato_il`] || "";
  const cf = data?.[`${prefix}cf`] || "";
  const empty = !cog && !nom && !cf;
  return (
    <div data-testid={`com-genitore-card-${idx}`}
      style={{
        background: empty ? "#fafafa" : (highlight ? "#f3e5f5" : "white"),
        border: highlight ? "2px solid #7e57c2" : "1px solid #e1bee7",
        borderRadius: 4, padding: 10, fontSize: 11,
        opacity: empty ? 0.6 : 1,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ background: "#7e57c2", color: "white", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
          GENITORE {idx}
        </span>
        {highlight && (
          <span style={{ background: "#2e7d32", color: "white", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
            ✓ SELEZIONATO
          </span>
        )}
        {empty && <span style={{ color: "#888", fontStyle: "italic", fontSize: 11 }}>(non presente)</span>}
      </div>
      {!empty && (
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 8px", color: "#37474f" }}>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>Cognome:</span><span style={{ fontWeight: 600 }}>{cog || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>Nome:</span><span style={{ fontWeight: 600 }}>{nom || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>Indirizzo:</span><span>{ind || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>CAP Città:</span><span>{cap_citta || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>Nato a:</span><span>{nato_a || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>Nato il:</span><span>{nato_il || "—"}</span>
          <span style={{ color: "#7e57c2", fontWeight: 700 }}>CF:</span><span style={{ fontFamily: "Consolas, monospace" }}>{cf || "—"}</span>
        </div>
      )}
      {!empty && ana && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 10, color: "#7e57c2", cursor: "pointer" }}>Anagrafica completa (blocco multi-riga)</summary>
          <pre style={{ margin: "4px 0 0", fontSize: 10, color: "#555", fontFamily: "Consolas, monospace", whiteSpace: "pre-wrap" }}>
{ana}
          </pre>
        </details>
      )}
    </div>
  );
}

// ============================================================
// 🆕 Feb 2026: AlunnoInviiModal — Storico invii per un alunno
// - Elenca i record `comunicator_outbox` per quell'alunno
// - Checkbox per selezionare uno o più invii
// - Editor placeholder personalizzato: nome (es. `rif_prec`) + testo custom
//   Il testo di default è formato 5a: "In riferimento a: [oggetto] del [data]"
//   L'utente può modificarlo liberamente prima di salvarlo.
// - Al salvataggio persiste in localStorage e viene iniettato come
//   `custom_placeholders` nel payload di invia-bulk-v2 dal wizard.
// ============================================================
function AlunnoInviiModal({ cod, alunnoLabel, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const existing = readCustomPlaceholder(cod);
  const [phName, setPhName] = useState(existing?.name || "rif_prec");
  const [phValue, setPhValue] = useState(existing?.value || "");
  const [phEditedManually, setPhEditedManually] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getComunicatorOutboxByAlunno(cod, { limit: 200 })
      .then(r => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.detail || e.message || "Errore caricamento invii"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cod]);

  // Format data compatto "gg/mm/aaaa" da ISO
  const _fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso).slice(0, 10);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      return `${dd}/${mm}/${yy}`;
    } catch { return ""; }
  };

  // Genera testo custom a partire dagli invii spuntati (formato 5a)
  const _buildDefaultText = useCallback((checked) => {
    if (checked.size === 0) return "";
    const rows = items.filter(it => checked.has(it._id));
    if (rows.length === 0) return "";
    if (rows.length === 1) {
      const r = rows[0];
      return `In riferimento a: ${r.subject || "(senza oggetto)"} del ${_fmtDate(r.sent_at)}`;
    }
    return "In riferimento a:\n" + rows.map(r => `- ${r.subject || "(senza oggetto)"} del ${_fmtDate(r.sent_at)}`).join("\n");
  }, [items]);

  // Auto-aggiorna phValue quando cambiano le spunte, a meno che l'utente non l'abbia già modificato a mano
  useEffect(() => {
    if (phEditedManually) return;
    setPhValue(_buildDefaultText(checkedIds));
  }, [checkedIds, _buildDefaultText, phEditedManually]);

  const toggleCheck = (id) => setCheckedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const handleSave = () => {
    const name = String(phName || "").trim().replace(/[^a-zA-Z0-9_]/g, "_");
    if (!name) { alert("Il nome del placeholder è obbligatorio (solo lettere, cifre, underscore)."); return; }
    if (!phValue.trim()) { alert("Il testo del placeholder è vuoto. Spunta almeno un invio o scrivi un testo custom."); return; }
    writeCustomPlaceholder(cod, name, phValue);
    onSaved && onSaved();
    onClose();
  };
  const handleClear = () => {
    if (!window.confirm("Rimuovi il placeholder salvato per questo alunno?")) return;
    clearCustomPlaceholder(cod);
    setPhValue("");
    setPhEditedManually(false);
    setCheckedIds(new Set());
    onSaved && onSaved();
  };

  return (
    <div data-testid="alunno-invii-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9998 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", width: 780, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto", padding: 18, border: "2px solid #6a1b9a", borderRadius: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div>
            <h3 style={{ margin: 0, color: "#6a1b9a", fontSize: 16 }}>📜 Storico invii — {alunnoLabel}</h3>
            <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>
              Spunta gli invii da citare, personalizza il testo del placeholder e salva. Il placeholder sarà iniettato nella mail al prossimo invio a questo alunno.
            </div>
          </div>
          <button onClick={onClose} data-testid="invii-close"
            style={{ padding: "4px 10px", background: "#eceff1", border: "1px solid #b0bec5", cursor: "pointer" }}>✕</button>
        </div>

        {loading && <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</div>}
        {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", fontSize: 12 }}>{err}</div>}

        {!loading && !err && items.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Nessun invio registrato per questo alunno.</div>
        )}

        {!loading && !err && items.length > 0 && (
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e0e0e0", marginBottom: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f3e5f5" }}>
                <tr>
                  <th style={{ width: 28, padding: 4 }}></th>
                  <th style={{ width: 90, padding: 4, textAlign: "left" }}>Data</th>
                  <th style={{ padding: 4, textAlign: "left" }}>Oggetto</th>
                  <th style={{ width: 140, padding: 4, textAlign: "left" }}>Template</th>
                  <th style={{ width: 80, padding: 4, textAlign: "center" }}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it._id} data-testid={`invii-row-${it._id}`}
                    style={{ borderTop: "1px solid #f0f0f0", background: checkedIds.has(it._id) ? "#f3e5f5" : "white", cursor: "pointer" }}
                    onClick={() => toggleCheck(it._id)}>
                    <td style={{ padding: 4, textAlign: "center" }}>
                      <input type="checkbox" checked={checkedIds.has(it._id)} onChange={() => toggleCheck(it._id)}
                        data-testid={`invii-check-${it._id}`} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: 4, fontFamily: "Consolas,monospace", color: "#37474f" }}>{_fmtDate(it.sent_at)}</td>
                    <td style={{ padding: 4 }}>{it.subject || <i style={{ color: "#aaa" }}>(senza oggetto)</i>}</td>
                    <td style={{ padding: 4, color: "#6a1b9a", fontWeight: 600 }}>{it.template_label || it.template_id || "—"}</td>
                    <td style={{ padding: 4, textAlign: "center", fontSize: 10,
                                 color: it.status === "sent" || it.status === "delivered" ? "#1b5e20"
                                      : it.status === "error" ? "#c62828" : "#666" }}>
                      {it.status || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Editor placeholder */}
        <div style={{ background: "#fafafa", border: "1px solid #ddd", padding: 12, borderRadius: 3 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6a1b9a", marginBottom: 6, textTransform: "uppercase" }}>
            🏷 Placeholder personalizzato per la mail di questo alunno
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#444", minWidth: 100 }}>Nome placeholder:</label>
            <span style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>{"{"}</span>
            <input type="text" value={phName}
              onChange={e => setPhName(e.target.value.replace(/[^a-zA-Z0-9_]/g, "_"))}
              data-testid="invii-ph-name"
              style={{ padding: "3px 6px", border: "1px solid #b39ddb", fontFamily: "monospace", fontSize: 12, width: 180 }} />
            <span style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>{"}"}</span>
            <span style={{ fontSize: 10, color: "#888" }}>← usa questa stringa nel corpo della mail</span>
          </div>
          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#444", display: "block", marginBottom: 3 }}>
              Testo che sostituirà il placeholder:
              {!phEditedManually && checkedIds.size > 0 && <span style={{ fontSize: 9, color: "#888", fontWeight: 400, marginLeft: 8 }}>(auto-generato dagli invii spuntati — modificalo se vuoi)</span>}
            </label>
            <textarea value={phValue}
              onChange={e => { setPhValue(e.target.value); setPhEditedManually(true); }}
              data-testid="invii-ph-value"
              rows={4}
              placeholder="Es: In riferimento a: Sollecito Retta del 15/03/2026"
              style={{ width: "100%", padding: 6, border: "1px solid #b39ddb", fontSize: 12, fontFamily: "monospace" }} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {existing && (
              <button onClick={handleClear} data-testid="invii-ph-clear"
                style={{ padding: "6px 12px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                🗑 Rimuovi placeholder
              </button>
            )}
            <button onClick={onClose} data-testid="invii-ph-cancel"
              style={{ padding: "6px 12px", background: "#eceff1", color: "#37474f", border: "1px solid #b0bec5", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              Annulla
            </button>
            <button onClick={handleSave} data-testid="invii-ph-save"
              style={{ padding: "6px 16px", background: "#6a1b9a", color: "white", border: "none", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
              💾 Salva placeholder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

